import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { selectUserData, selectUserInfo, selectUserToken } from "../../store/selectors";
import { API_ENDPOINTS, baseHost } from "../../constants/common";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CircleHelp, List as ListIcon, Map as MapIcon, Search, SlidersHorizontal } from "lucide-react";
import imageCompression from "browser-image-compression";
import { Button } from "../../components/common/Button";
import { Error } from "../../components/common/Error";
import { PageNavigation } from "../../components/common/PageNavigation";
import { HHTabs, TabsMap } from "../../components/common/Tabs";
import { MarketNavigation } from "../../components/common/MarketNavigation";
import { apiClient, publicApiClient } from "../../utils";
import { RootState } from "../../store";
import { connect } from "react-redux";
import {
  loadYandexMaps,
  parseCoordinate,
  YandexCoordinates,
  YandexGeoObject,
  YandexMapEvent,
  YandexMapInstance,
  YandexPlacemarkInstance,
} from "../../utils/yandexMaps";
import {
  getClassifiedsScrollPosition,
  restoreClassifiedsScrollPosition,
  saveClassifiedsScrollPosition,
} from "../../utils/classifiedsScrollRestoration";
import {
  ANALYTICS_EVENTS,
  getMarketListingAnalyticsProperties,
  trackAnalyticsEvent,
} from "../../analytics";
import {
  finalizeAreaInput,
  formatDecimalInput,
  formatConstraintNumber,
  getAreaInputError,
  getNumericConstraintHint,
  getNumericInputError,
  normalizeAreaInput,
  NumericInputConstraint,
} from "../../utils/areaInput";
import { getFilterRangeError } from "../../utils/filterRange";
import {
  FALLBACK_LISTING_CONSTRAINTS,
  getStringConstraintHint,
  getStringInputError,
  getUrlInputError,
  ListingMoneyField,
  normalizeListingConstraints,
  roundToDecimalPlaces,
} from "../../utils/listingConstraints";
import {
  formatPriceInput,
  getPriceInputCaretPosition,
  normalizePriceInput,
} from "../../utils/priceInput";
import { buildAddressAddons, buildAgentCommissionAddons } from "../../utils/classifiedsPayload";
import { extractAddressFromGeoObject } from "../../utils/classifiedsAddress";
import {
  ClassifiedsFilterCapabilities,
  getClassifiedsFilterCapabilities,
} from "../../utils/classifiedsFilterCapabilities";
import { resolveListingTotalPrice } from "../../utils/listingPrice";

type ListingType = "house" | "land_plot" | "apartment" | "commercial";
type MarketType = "sale" | "rent";
type ModerationStatus = "pending" | "approved" | "rejected";
type UtilitiesPayer = "tenant" | "landlord";
type RentTerm = "short_term" | "long_term";
type AssignmentPaymentType = "installment" | "full_payment";
type FilterCategoryOption = {
  value: string;
  label: string;
  market?: MarketType;
};

const isMarketType = (value: unknown): value is MarketType => value === "sale" || value === "rent";
type TaxonomySubcategory = {
  value: string;
  label: string;
  listing_type: ListingType;
};
type TaxonomyCategory = {
  value: string;
  label: string;
  listing_type: ListingType | null;
  subcategories: TaxonomySubcategory[];
};
type TaxonomyMarket = {
  market_type: MarketType;
  label: string;
  categories: TaxonomyCategory[];
};

type Listing = {
  id: number | string;
  owner_id?: number | string;
  status_id?: number | string;
  moderation_status?: ModerationStatus;
  moderation_reason?: string | null;
  moderation_submitted_at?: string | null;
  moderated_at?: string | null;
  title: string;
  type: ListingType;
  market_type: MarketType;
  board_category: string;
  board_subcategory: string | null;
  longitude?: number | string;
  latitude?: number | string;
  image_url: string | null;
  area_sqm: number | string | null;
  area_site: number | string | null;
  price_sqm: number | string | null;
  price_total?: number | string | null;
  price_monthly: number | string | null;
  utilities_payer?: UtilitiesPayer | null;
  rent_term?: RentTerm | null;
  agent_commission_percent?: number | string | null;
  agent_commission_amount?: number | string | null;
  addons?: Record<string, unknown>;
};

type ClassifiedsMapListing = Listing & {
  kind?: "listing";
};
type ClassifiedsMapCluster = {
  kind: "cluster";
  id: number | string;
  latitude: number | string;
  longitude: number | string;
  count: number;
  bounds?:
    | MapBoundsState
    | {
        lat_min?: number | string;
        lat_max?: number | string;
        lng_min?: number | string;
        lng_max?: number | string;
      };
};
type ClassifiedsMapItem = ClassifiedsMapListing | ClassifiedsMapCluster;

type ListingPagination = {
  limit: number;
  has_more: boolean;
  next_cursor: string | null;
  total: number | null;
};

type MyListingsCounts = Record<MyListingsTab, number>;

type ListingCreationPermission = {
  can_create_listings: boolean;
  is_listing_creation_whitelisted: boolean;
  create_listings_allowed_email: string | null;
};

type ListingImage = {
  id?: number | string;
  url: string;
  is_primary?: boolean;
  sort_order: number;
};

type PaymentTerm = {
  period: string;
  price: number | string;
  currency_id?: number | string;
};

type ListingDetail = Listing & {
  owner_id: number | string;
  status_id: number | string;
  longitude: number | string;
  latitude: number | string;
  description?: string | null;
  images?: ListingImage[];
  addons?: Record<string, unknown>;
  payment_terms?: PaymentTerm[];
  assignment?: Record<string, unknown> | null;
  room_count?: number | string;
  floor_count?: number | string | null;
  floor?: number | string;
  type_id?: number | string;
  purpose_id?: number | string;
  window_view?: string;
  construction_year?: number | string | null;
  completion_year?: number | string | null;
};

type UserNode = {
  id?: number | string;
  user_id?: number | string;
  userId?: number | string;
  abilities?: string[];
  token?: string | null;
  data?: UserNode;
  profile?: UserNode;
  user?: UserNode;
} | null;
type UserInfo = UserNode;
type UserDataShape = UserNode;
type Props = {
  user: UserInfo;
  userData: UserDataShape;
  userToken?: string | null;
  createMode?: boolean;
  editMode?: boolean;
  myMode?: boolean;
  favoriteMode?: boolean;
};
type UploadStatus = "queued" | "uploading" | "done" | "error";
type UploadItem = {
  id: string;
  file?: File;
  name: string;
  size: number;
  originalSize: number;
  previewUrl: string;
  progress: number;
  status: UploadStatus;
  uploadedUrl?: string;
  uploadedPath?: string;
  uploadedFileId?: number;
  error?: string;
};

type UploadResolvedMeta = {
  url: string | null;
  path: string | null;
  fileId: number | null;
};
type ListingImagePayload = {
  url: string;
  is_primary: boolean;
  sort_order: number;
};
type CreateStep = 1 | 2 | 3 | 4 | 5;
type MyListingsTab = "published" | "moderation" | "unpublished";

const getMyListingsTabFromQuery = (searchParams: URLSearchParams): MyListingsTab => {
  const tab = searchParams.get("tab");

  return tab === "moderation" || tab === "unpublished" ? tab : "published";
};

const LISTINGS_PAGE_LIMIT = 30;
const PUBLISHED_STATUS_ID = 1;
const UNPUBLISHED_STATUS_ID = 2;
const HEIF_EXTENSIONS = new Set(["heic", "heif"]);
const HEIF_MIME_TYPES = new Set([
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);
const HEIF_BRANDS = new Set(["heic", "heix", "hevc", "hevx", "mif1", "msf1"]);
const MARKET_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp", ...HEIF_EXTENSIONS]);
const MARKET_IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  ...HEIF_MIME_TYPES,
]);
const getFileExtension = (fileName: string) => fileName.split(".").pop()?.toLowerCase() || "";
const isMarketImageFile = (file: File) =>
  MARKET_IMAGE_MIME_TYPES.has(file.type.toLowerCase()) || MARKET_IMAGE_EXTENSIONS.has(getFileExtension(file.name));
const isHeifFile = async (file: File) => {
  if (HEIF_MIME_TYPES.has(file.type.toLowerCase()) || HEIF_EXTENSIONS.has(getFileExtension(file.name))) {
    return true;
  }
  if (file.size < 12) return false;

  try {
    const brandBytes = new Uint8Array(await file.slice(8, 12).arrayBuffer());
    const brand = String.fromCharCode(...brandBytes).replace(/\0/g, "").trim().toLowerCase();
    return HEIF_BRANDS.has(brand);
  } catch {
    return false;
  }
};
const getConvertedJpegName = (fileName: string) => {
  const extensionIndex = fileName.lastIndexOf(".");
  const baseName = extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;
  return `${baseName || "photo"}.jpg`;
};
const isListingPublished = (item: Pick<Listing, "status_id" | "moderation_status">) =>
  Number(item.status_id ?? PUBLISHED_STATUS_ID) === PUBLISHED_STATUS_ID &&
  item.moderation_status !== "pending" &&
  item.moderation_status !== "rejected";
const isListingInModeration = (item: Pick<Listing, "moderation_status">) =>
  item.moderation_status === "pending" || item.moderation_status === "rejected";
const getMyListingsTab = (item: Pick<Listing, "status_id" | "moderation_status">): MyListingsTab => {
  if (isListingInModeration(item)) return "moderation";
  return isListingPublished(item) ? "published" : "unpublished";
};
const parseMyListingsCounts = (value: unknown): MyListingsCounts | null => {
  if (!value || typeof value !== "object") return null;

  const counts = value as Partial<Record<MyListingsTab, unknown>>;
  const published = Number(counts.published);
  const moderation = Number(counts.moderation);
  const unpublished = Number(counts.unpublished);

  if (![published, moderation, unpublished].every((count) => Number.isInteger(count) && count >= 0)) {
    return null;
  }

  return { published, moderation, unpublished };
};
const MODERATION_LABELS: Record<ModerationStatus, string> = {
  pending: "На модерации",
  approved: "Модерация пройдена",
  rejected: "Отклонено",
};
type SerializedDraftUpload = {
  id: string;
  name: string;
  size: number;
  originalSize: number;
  progress: number;
  status: UploadStatus;
  uploadedUrl?: string;
  uploadedPath?: string;
  uploadedFileId?: number;
};
type DraftPayload = {
  createStep: CreateStep;
  createMarketType: MarketType;
  createCategory: string;
  createSubcategory: string;
  assignmentHouseCompleted?: boolean;
  baseForm: Record<string, string>;
  features: {
    is_renovated: boolean;
    is_penthouse: boolean;
    is_apartment_unit: boolean;
    is_view: boolean;
    house_kind: string;
    is_completed: boolean;
    has_pool: boolean;
    has_bathhouse: boolean;
  };
  communications: {
    has_sewerage: boolean;
    has_water: boolean;
    has_electricity: boolean;
    has_gas: boolean;
  };
  address: {
    address_region: string;
    address_locality: string;
    address_street: string;
    address_house: string;
  };
  finance: {
    price_total: string;
    price_monthly: string;
    utility_included: boolean;
    utilities_payer: UtilitiesPayer;
    rent_term: RentTerm;
    agent_commission_percent: string;
    agent_commission_amount: string;
  };
  assignmentEnabled: boolean;
  assignmentPaymentType?: AssignmentPaymentType;
  assignment: {
    currency_id: string;
    initial_payment: string;
    payment: string;
    full_payment_purchase: string;
    full_payment_price: string;
    agent_markup: string;
    recalculation_percentage: string;
    recalculation_period: string;
    initial_payment_at: string;
    assignment_months_left: string;
    assignment_paid_amount: string;
    assignment_paid_months: string;
  };
  imageUploads: SerializedDraftUpload[];
};
type LocalDraftEnvelope = {
  version: 1;
  updatedAt: number;
  expiresAt: number;
  payload: DraftPayload;
};
type AddressState = {
  address_region: string;
  address_locality: string;
  address_street: string;
  address_house: string;
};

const DRAFT_LOCAL_TTL_MS = 2 * 60 * 1000;
type MarketFilter = MarketType | "my";
type ClassifiedsViewMode = "list" | "map";

const CLASSIFIEDS_VIEW_TABS: TabsMap = {
  list: {
    id: "list",
    name: "Список",
    icon: <ListIcon size={15} strokeWidth={1.8} />,
  },
  map: {
    id: "map",
    name: "Карта",
    icon: <MapIcon size={15} strokeWidth={1.8} />,
  },
};
type MapBoundsState = {
  latMin: number;
  latMax: number;
  lngMin: number;
  lngMax: number;
};
type MapViewportState = MapBoundsState & {
  zoom: number;
};
type AdvancedFiltersState = {
  priceSqmMin: string;
  priceSqmMax: string;
  priceMonthlyMin: string;
  priceMonthlyMax: string;
  areaMin: string;
  areaMax: string;
  utilitiesPayer: UtilitiesPayer | "all";
  rentTerm: RentTerm | "all";
  agentCommissionUnit: "percent" | "amount";
  agentCommissionMin: string;
  agentCommissionMax: string;
  commissionSort: "default" | "commission" | "rent_term";
  sortDirection: "asc" | "desc";
  hasImage: boolean;
};

type FilterRangeKey = "priceSqm" | "priceMonthly" | "area" | "agentCommission";
type FilterRangeErrors = Partial<Record<FilterRangeKey, string>>;

const DEFAULT_ADVANCED_FILTERS: AdvancedFiltersState = {
  priceSqmMin: "",
  priceSqmMax: "",
  priceMonthlyMin: "",
  priceMonthlyMax: "",
  areaMin: "",
  areaMax: "",
  utilitiesPayer: "all",
  rentTerm: "all",
  agentCommissionUnit: "percent",
  agentCommissionMin: "",
  agentCommissionMax: "",
  commissionSort: "default",
  sortDirection: "desc",
  hasImage: false,
};
const getFilterRangeErrors = (filters: AdvancedFiltersState): FilterRangeErrors => {
  const errors: FilterRangeErrors = {};
  const ranges: Array<[FilterRangeKey, string, string]> = [
    ["priceSqm", filters.priceSqmMin, filters.priceSqmMax],
    ["priceMonthly", filters.priceMonthlyMin, filters.priceMonthlyMax],
    ["area", filters.areaMin, filters.areaMax],
    ["agentCommission", filters.agentCommissionMin, filters.agentCommissionMax],
  ];

  ranges.forEach(([key, min, max]) => {
    const error = getFilterRangeError(min, max);
    if (error) errors[key] = error;
  });

  return errors;
};
const TRUTHY_QUERY_VALUES = new Set(["1", "true", "yes"]);
const getQueryValue = (params: URLSearchParams, ...keys: string[]) => {
  for (const key of keys) {
    const value = params.get(key);
    if (value !== null && value.trim()) return value;
  }
  return "";
};
const getHeaderScrollBlockHeight = () => {
  const header = document.querySelector<HTMLElement>(".header_scroll-container.scroll-aware-block.top");
  return header ? Math.ceil(header.getBoundingClientRect().height) : 0;
};
const readAdvancedFiltersFromQuery = (params: URLSearchParams): AdvancedFiltersState => {
  const amountMin = getQueryValue(params, "agent_commission_amount_min", "agent_commission_amount");
  const amountMax = getQueryValue(params, "agent_commission_amount_max", "agent_commission_amount");
  const percentMin = getQueryValue(params, "agent_commission_percent_min", "agent_commission_percent");
  const percentMax = getQueryValue(params, "agent_commission_percent_max", "agent_commission_percent");
  const sort = params.get("sort");
  const useAmount = Boolean(
    params.get("agent_commission_unit") === "amount" || amountMin || amountMax || sort === "agent_commission_amount",
  );

  return {
    priceSqmMin: getQueryValue(params, "price_sqm_min"),
    priceSqmMax: getQueryValue(params, "price_sqm_max"),
    priceMonthlyMin: getQueryValue(params, "price_monthly_min"),
    priceMonthlyMax: getQueryValue(params, "price_monthly_max"),
    areaMin: getQueryValue(params, "area_min"),
    areaMax: getQueryValue(params, "area_max"),
    utilitiesPayer:
      params.get("utilities_payer") === "tenant" || params.get("utilities_payer") === "landlord"
        ? (params.get("utilities_payer") as UtilitiesPayer)
        : "all",
    rentTerm:
      params.get("rent_term") === "short_term" || params.get("rent_term") === "long_term"
        ? (params.get("rent_term") as RentTerm)
        : "all",
    agentCommissionUnit: useAmount ? "amount" : "percent",
    agentCommissionMin: useAmount ? amountMin : percentMin,
    agentCommissionMax: useAmount ? amountMax : percentMax,
    commissionSort:
      sort === "rent_term"
        ? "rent_term"
        : sort === "agent_commission_percent" || sort === "agent_commission_amount"
          ? "commission"
          : "default",
    sortDirection: params.get("sort_direction") === "asc" ? "asc" : "desc",
    hasImage: TRUTHY_QUERY_VALUES.has(String(params.get("has_image") || "").toLowerCase()),
  };
};
const hasActiveAdvancedFilters = (filters: AdvancedFiltersState) =>
  Boolean(
    filters.priceSqmMin ||
      filters.priceSqmMax ||
      filters.priceMonthlyMin ||
      filters.priceMonthlyMax ||
      filters.areaMin ||
      filters.areaMax ||
      filters.utilitiesPayer !== "all" ||
      filters.rentTerm !== "all" ||
      filters.agentCommissionMin ||
      filters.agentCommissionMax ||
      filters.commissionSort !== "default" ||
      filters.sortDirection !== "desc" ||
      filters.hasImage,
  );
const sanitizeAdvancedFilters = (
  filters: AdvancedFiltersState,
  capabilities: ClassifiedsFilterCapabilities,
): AdvancedFiltersState => ({
  ...filters,
  priceSqmMin: capabilities.priceSqm ? filters.priceSqmMin : "",
  priceSqmMax: capabilities.priceSqm ? filters.priceSqmMax : "",
  priceMonthlyMin: capabilities.priceMonthly ? filters.priceMonthlyMin : "",
  priceMonthlyMax: capabilities.priceMonthly ? filters.priceMonthlyMax : "",
  utilitiesPayer: capabilities.utilitiesPayer ? filters.utilitiesPayer : "all",
  rentTerm: capabilities.rentTerm ? filters.rentTerm : "all",
  agentCommissionUnit: capabilities.agentCommission ? filters.agentCommissionUnit : "percent",
  agentCommissionMin: capabilities.agentCommission ? filters.agentCommissionMin : "",
  agentCommissionMax: capabilities.agentCommission ? filters.agentCommissionMax : "",
  commissionSort: capabilities.commissionSort ? filters.commissionSort : "default",
  sortDirection: capabilities.commissionSort ? filters.sortDirection : "desc",
  hasImage: capabilities.hasImage ? filters.hasImage : false,
});
const countActiveFilters = (
  categoryFilter: string,
  subcategoryFilter: string,
  filters: AdvancedFiltersState,
  capabilities: ClassifiedsFilterCapabilities,
) =>
  [
    categoryFilter !== "all",
    subcategoryFilter !== "all",
    capabilities.priceSqm && Boolean(filters.priceSqmMin || filters.priceSqmMax),
    capabilities.priceMonthly && Boolean(filters.priceMonthlyMin || filters.priceMonthlyMax),
    capabilities.area && Boolean(filters.areaMin || filters.areaMax),
    capabilities.utilitiesPayer && filters.utilitiesPayer !== "all",
    capabilities.rentTerm && filters.rentTerm !== "all",
    capabilities.agentCommission && Boolean(filters.agentCommissionMin || filters.agentCommissionMax),
    capabilities.commissionSort && (filters.commissionSort !== "default" || filters.sortDirection !== "desc"),
    capabilities.hasImage && filters.hasImage,
  ].filter(Boolean).length;
const areAdvancedFiltersEqual = (left: AdvancedFiltersState, right: AdvancedFiltersState) =>
  left.priceSqmMin === right.priceSqmMin &&
  left.priceSqmMax === right.priceSqmMax &&
  left.priceMonthlyMin === right.priceMonthlyMin &&
  left.priceMonthlyMax === right.priceMonthlyMax &&
  left.areaMin === right.areaMin &&
  left.areaMax === right.areaMax &&
  left.utilitiesPayer === right.utilitiesPayer &&
  left.rentTerm === right.rentTerm &&
  left.agentCommissionUnit === right.agentCommissionUnit &&
  left.agentCommissionMin === right.agentCommissionMin &&
  left.agentCommissionMax === right.agentCommissionMax &&
  left.commissionSort === right.commissionSort &&
  left.sortDirection === right.sortDirection &&
  left.hasImage === right.hasImage;
const formatRangeLabel = (from: string, to: string, unit = "") => {
  const normalizedFrom = formatDecimalInput(from);
  const normalizedTo = formatDecimalInput(to);
  if (normalizedFrom && normalizedTo) return `${normalizedFrom}-${normalizedTo}${unit}`;
  if (normalizedFrom) return `от ${normalizedFrom}${unit}`;
  if (normalizedTo) return `до ${normalizedTo}${unit}`;
  return "";
};
const CREATE_STEPS: Array<{ id: CreateStep; label: string }> = [
  { id: 1, label: "Основное" },
  { id: 2, label: "Фото" },
  { id: 3, label: "Детали" },
  { id: 4, label: "Адрес" },
  { id: 5, label: "Переуступка" },
];

const COMMON_FORM = {
  title: "",
  longitude: "45.6987",
  latitude: "43.3178",
  status_id: "1",
  image_url: "",
};

const BASE_BY_TYPE: Record<ListingType, Record<string, string>> = {
  house: {
    ...COMMON_FORM,
    description: "",
    room_count: "",
    floor_count: "",
    area_sqm: "",
    area_site: "",
    type_id: "1",
    construction_year: "",
    completion_year: "",
  },
  land_plot: {
    ...COMMON_FORM,
    description: "",
    area_site: "",
    purpose_id: "1",
  },
  apartment: {
    ...COMMON_FORM,
    description: "",
    floor: "",
    room_count: "",
    area_sqm: "",
    window_view: "fancy",
    construction_year: "",
    completion_year: "",
  },
  commercial: {
    ...COMMON_FORM,
    description: "",
    floor: "",
    floor_count: "",
    area_sqm: "",
    window_view: "common",
    construction_year: "",
    completion_year: "",
  },
};

const numericField = (key: string) => !["title", "window_view", "image_url", "description"].includes(key);

const LISTING_TYPES: ListingType[] = ["house", "land_plot", "apartment", "commercial"];

const normalizeListingType = (value: unknown): ListingType | null => {
  const normalized = String(value || "");
  return LISTING_TYPES.includes(normalized as ListingType) ? (normalized as ListingType) : null;
};

const getListingDetailPath = (type: ListingType, id: number | string) =>
  `/classifieds/${encodeURIComponent(type)}/${encodeURIComponent(String(id))}`;

const normalizeTaxonomy = (payload: unknown): TaxonomyMarket[] => {
  const rows = Array.isArray(payload) ? payload : [];

  return rows
    .map((market): TaxonomyMarket | null => {
      const marketType = String((market as { market_type?: unknown })?.market_type || "");
      if (marketType !== "sale" && marketType !== "rent") return null;

      const categoriesRaw = (market as { categories?: unknown })?.categories;
      const categories = Array.isArray(categoriesRaw) ? categoriesRaw : [];

      return {
        market_type: marketType,
        label: String((market as { label?: unknown })?.label || marketType),
        categories: categories
          .map((category): TaxonomyCategory | null => {
            const value = String((category as { value?: unknown })?.value || "");
            if (!value) return null;

            const subcategoriesRaw = (category as { subcategories?: unknown })?.subcategories;
            const subcategories = Array.isArray(subcategoriesRaw) ? subcategoriesRaw : [];

            return {
              value,
              label: String((category as { label?: unknown })?.label || value),
              listing_type: normalizeListingType((category as { listing_type?: unknown })?.listing_type),
              subcategories: subcategories
                .map((subcategory): TaxonomySubcategory | null => {
                  const subValue = String((subcategory as { value?: unknown })?.value || "");
                  const listingType = normalizeListingType((subcategory as { listing_type?: unknown })?.listing_type);
                  if (!subValue || !listingType) return null;

                  return {
                    value: subValue,
                    label: String((subcategory as { label?: unknown })?.label || subValue),
                    listing_type: listingType,
                  };
                })
                .filter((item): item is TaxonomySubcategory => Boolean(item)),
            };
          })
          .filter((item): item is TaxonomyCategory => Boolean(item)),
      };
    })
    .filter((item): item is TaxonomyMarket => Boolean(item));
};

const FormSectionTitle = ({ text }: { text: string }) => <div className={ui.sectionTitle}>{text}</div>;

type GroupedPriceInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "inputMode" | "onChange" | "type" | "value"
> & {
  value: string;
  onValueChange: (value: string) => void;
};

type DecimalInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "inputMode" | "onChange" | "type" | "value"
> & {
  value: string;
  onValueChange: (value: string) => void;
};

const DecimalInput = ({ value, onValueChange, ...props }: DecimalInputProps) => (
  <input
    {...props}
    type="text"
    inputMode="decimal"
    value={formatDecimalInput(value)}
    onChange={(event) => onValueChange(normalizeAreaInput(event.currentTarget.value))}
  />
);

const GroupedPriceInput = ({ value, onValueChange, ...props }: GroupedPriceInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const caretPositionRef = useRef<number | null>(null);
  const formattedValue = formatPriceInput(value);

  React.useLayoutEffect(() => {
    const input = inputRef.current;
    const caretPosition = caretPositionRef.current;

    if (!input || caretPosition === null || document.activeElement !== input) return;

    caretPositionRef.current = null;
    input.setSelectionRange(caretPosition, caretPosition);
  }, [formattedValue]);

  return (
    <input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={formattedValue}
      onChange={(event) => {
        const rawValue = event.currentTarget.value;
        const rawCaretPosition = event.currentTarget.selectionStart ?? rawValue.length;

        caretPositionRef.current = getPriceInputCaretPosition(rawValue, rawCaretPosition);
        onValueChange(normalizePriceInput(rawValue));
      }}
    />
  );
};

type FieldHintProps = {
  text: string;
  positionClassName?: string;
};

const FieldHint = ({ text, positionClassName = "right-3 top-1/2 -translate-y-1/2" }: FieldHintProps) => (
  <span
    className={`group absolute z-10 grid h-5 w-5 place-items-center rounded-full text-[#8a8a8a] outline-none transition hover:text-[#8b2a5e] focus:text-[#8b2a5e] ${positionClassName}`}
    tabIndex={0}
    aria-label={`Подсказка: ${text}`}
  >
    <CircleHelp size={16} strokeWidth={1.8} aria-hidden="true" />
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-[calc(100%+7px)] right-0 z-40 hidden w-max max-w-[260px] rounded-lg bg-[#252525] px-2.5 py-2 text-left text-[11px] font-normal leading-[1.4] text-white shadow-lg group-hover:block group-focus:block"
    >
      {text}
    </span>
  </span>
);

type AreaInputProps = {
  constraint: NumericInputConstraint;
  error: string | null;
  hint: string;
  id: string;
  onValueChange: (value: string) => void;
  value: string;
};

const AreaInput = ({ constraint, error, hint, id, onValueChange, value }: AreaInputProps) => {
  const errorId = `${id}-error`;

  return (
    <>
      <div className="relative">
        <input
          id={id}
          className={`${ui.input} pr-10 ${
            error ? "!border-[#b42318] !bg-[#fff7f6] focus:!border-[#b42318]" : ""
          }`}
          type="text"
          inputMode="decimal"
          min={constraint.min}
          max={constraint.max}
          step={10 ** -constraint.decimal_places}
          autoComplete="off"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          placeholder="Например: 52,75"
          value={formatDecimalInput(value)}
          onChange={(event) => onValueChange(normalizeAreaInput(event.target.value))}
          onBlur={() => onValueChange(finalizeAreaInput(value))}
        />
        <FieldHint text={hint} />
      </div>
      {error ? (
        <span id={errorId} className={ui.errorText} role="alert">
          {error}
        </span>
      ) : null}
    </>
  );
};

const DESCRIPTION_MIN_HEIGHT = 120;
const DESCRIPTION_MAX_HEIGHT = 320;

const AutoGrowingTextarea = ({
  onChange,
  value,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const resizeTextarea = useCallback((textarea: HTMLTextAreaElement) => {
    textarea.style.height = "0px";
    const contentHeight = textarea.scrollHeight;
    const nextHeight = Math.min(
      Math.max(contentHeight, DESCRIPTION_MIN_HEIGHT),
      DESCRIPTION_MAX_HEIGHT,
    );
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = contentHeight > DESCRIPTION_MAX_HEIGHT ? "auto" : "hidden";
  }, []);

  useEffect(() => {
    if (textareaRef.current) resizeTextarea(textareaRef.current);
  }, [resizeTextarea, value]);

  return (
    <textarea
      {...props}
      ref={textareaRef}
      rows={4}
      value={value}
      onChange={(event) => {
        resizeTextarea(event.currentTarget);
        onChange?.(event);
      }}
    />
  );
};

const formatMoney = (value: unknown) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return `${amount.toLocaleString("ru-RU")} ₽`;
};

const UTILITIES_PAYER_LABELS: Record<UtilitiesPayer, string> = {
  tenant: "Арендатор",
  landlord: "Собственник",
};

const RENT_TERM_LABELS: Record<RentTerm, string> = {
  short_term: "1-3 месяца",
  long_term: "Долгосрок, более 3 месяцев",
};

const formatAgentCommission = (percentValue: unknown, amountValue: unknown): string => {
  const commissionPercent = parseNumber(unwrapListingValue(percentValue));
  const commissionAmount = formatMoney(unwrapListingValue(amountValue));
  const commissionParts = [
    commissionPercent && commissionPercent > 0 ? `${formatConstraintNumber(commissionPercent, 2)}%` : null,
    commissionAmount,
  ].filter(Boolean);
  return commissionParts.length ? commissionParts.join(" / ") : "Договорная";
};

const parseNumber = (value: unknown): number | null => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatAreaValue = (value: unknown, unit: "sqm" | "sotka"): string | null => {
  const parsed = parseNumber(value);
  if (parsed === null || parsed <= 0) return null;

  const amount = unit === "sotka" ? parsed / 100 : parsed;
  const formatted = new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: unit === "sotka" ? 2 : 1,
  }).format(amount);

  return unit === "sotka" ? `${formatted} сот.` : `${formatted} м²`;
};

const unwrapListingValue = (value: unknown): unknown => {
  if (value && typeof value === "object" && "value" in value) {
    return (value as { value: unknown }).value;
  }
  return value;
};

const toFormString = (value: unknown): string => {
  const unwrapped = unwrapListingValue(value);
  if (unwrapped === null || unwrapped === undefined) return "";
  return String(unwrapped);
};

const toFormBoolean = (value: unknown): boolean => {
  const unwrapped = unwrapListingValue(value);
  return unwrapped === true || unwrapped === "true" || unwrapped === 1 || unwrapped === "1";
};

const toUtilitiesPayer = (value: unknown, fallbackUtilityIncluded?: unknown): UtilitiesPayer => {
  const unwrapped = unwrapListingValue(value);
  if (unwrapped === "tenant" || unwrapped === "landlord") return unwrapped;
  return toFormBoolean(fallbackUtilityIncluded) ? "landlord" : "tenant";
};

const toRentTerm = (value: unknown): RentTerm => {
  const unwrapped = unwrapListingValue(value);
  return unwrapped === "long_term" ? "long_term" : "short_term";
};

const getListingUtilitiesPayer = (item: Listing): unknown => item.utilities_payer ?? item.addons?.utilities_payer;
const getListingRentTerm = (item: Listing): unknown => item.rent_term ?? item.addons?.rent_term;
const getListingAgentCommissionPercent = (item: Listing): unknown =>
  item.agent_commission_percent ?? item.addons?.agent_commission_percent;
const getListingAgentCommissionAmount = (item: Listing): unknown =>
  item.agent_commission_amount ?? item.addons?.agent_commission_amount;

const isApartmentFloorCountContext = (
  listingType: unknown,
  marketType: unknown,
  boardCategory: unknown,
  boardSubcategory: unknown,
) =>
  listingType === "apartment" &&
  marketType === "sale" &&
  (boardCategory === "secondary" || boardCategory === "assignments") &&
  boardSubcategory === "apartments";

const ASSIGNMENT_LABELS: Record<string, string> = {
  currency_id: "Валюта (id)",
  initial_payment: "Взнос",
  payment: "Остаток платежа",
  full_payment_purchase: "Стоимость при полной оплате (покупка)",
  full_payment_price: "Стоимость при полной оплате (продажа)",
  agent_markup: "Наценка мастер-агента, ₽",
  recalculation_percentage: "Процент перерасчета",
  recalculation_period: "Период перерасчета, дней",
  initial_payment_at: "Дата первоначального платежа",
  assignment_months_left: "Остаток месяцев",
  assignment_paid_amount: "Оплаченная сумма",
  assignment_paid_months: "Оплачено месяцев",
};

const ASSIGNMENT_PRICE_FIELDS = new Set([
  "initial_payment",
  "payment",
  "full_payment_purchase",
  "full_payment_price",
  "assignment_paid_amount",
]);

const MAP_DEFAULT_COORDINATES: YandexCoordinates = [43.3178, 45.6987];
const MAP_DEFAULT_ZOOM = 12;
const MAP_DEFAULT_LOCALITY = "Грозный";

const formatCoordinate = (value: number, decimalPlaces = 7) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: decimalPlaces,
    useGrouping: false,
  }).format(value);

const isMapCluster = (item: ClassifiedsMapItem): item is ClassifiedsMapCluster => item.kind === "cluster";

const normalizeMapClusterBounds = (bounds: ClassifiedsMapCluster["bounds"]): MapBoundsState | null => {
  if (!bounds) return null;
  const latMin = parseCoordinate("latMin" in bounds ? bounds.latMin : bounds.lat_min);
  const latMax = parseCoordinate("latMax" in bounds ? bounds.latMax : bounds.lat_max);
  const lngMin = parseCoordinate("lngMin" in bounds ? bounds.lngMin : bounds.lng_min);
  const lngMax = parseCoordinate("lngMax" in bounds ? bounds.lngMax : bounds.lng_max);
  if (latMin === null || latMax === null || lngMin === null || lngMax === null) return null;

  return {
    latMin: Math.min(latMin, latMax),
    latMax: Math.max(latMin, latMax),
    lngMin: Math.min(lngMin, lngMax),
    lngMax: Math.max(lngMin, lngMax),
  };
};

const buildMapStreetSearchQuery = (query: string) => {
  const normalized = query.trim();
  if (!normalized) return "";
  return /грозн|чечен/i.test(normalized) ? normalized : `${MAP_DEFAULT_LOCALITY}, ${normalized}`;
};

const normalizeGeocodeResponse = (payload: unknown): { coordinates: YandexCoordinates; bounds: MapBoundsState | null } | null => {
  const data = payload && typeof payload === "object" && "data" in payload ? (payload as { data?: unknown }).data : payload;
  if (!data || typeof data !== "object") return null;

  const source = data as Record<string, unknown>;
  const latitude = parseCoordinate(source.latitude ?? source.lat);
  const longitude = parseCoordinate(source.longitude ?? source.lng ?? source.lon);
  if (latitude === null || longitude === null) return null;

  return {
    coordinates: [latitude, longitude],
    bounds: normalizeMapClusterBounds((source.bounds ?? source) as ClassifiedsMapCluster["bounds"]),
  };
};

const buildAddressQuery = (value: AddressState): string =>
  [
    value.address_region,
    value.address_locality,
    value.address_street,
    value.address_house,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");

const getGeoObjectCoordinates = (geoObject: YandexGeoObject | null): YandexCoordinates | null => {
  const coordinates = geoObject?.geometry?.getCoordinates();
  return Array.isArray(coordinates) && coordinates.length >= 2 ? [coordinates[0], coordinates[1]] : null;
};

type AddressSuggestion = {
  id: string;
  title: string;
  subtitle: string;
  fullAddress: string;
  coordinates: YandexCoordinates;
  address: AddressState;
};

const getGeoObjectAddressLine = (geoObject: YandexGeoObject): string =>
  geoObject.getAddressLine?.() || String(geoObject.properties?.get("text", "") || "");

const buildAddressSuggestion = (geoObject: YandexGeoObject, index: number): AddressSuggestion | null => {
  const coordinates = getGeoObjectCoordinates(geoObject);
  const address = extractAddressFromGeoObject(geoObject);
  const fullAddress = getGeoObjectAddressLine(geoObject) || buildAddressQuery(address);
  if (!coordinates || !fullAddress) return null;

  const title = [address.address_street, address.address_house].filter(Boolean).join(", ") ||
    address.address_locality ||
    fullAddress;
  const subtitle = fullAddress === title ? "" : fullAddress;

  return {
    id: `${coordinates.join(":")}:${index}`,
    title,
    subtitle,
    fullAddress,
    coordinates,
    address,
  };
};

const ui = {
  container:
    "mx-auto grid max-w-[1344px] gap-3.5 px-3.5 pb-8 pt-5 md:px-6 md:pb-12 lg:px-10 lg:pb-16 xl:px-12",
  header: "flex flex-wrap items-start justify-between gap-3",
  title: "m-0 text-[30px] font-bold leading-[1.08] tracking-[-0.03em] text-[#1a1a1a]",
  subtitle: "mt-2 text-sm leading-[1.45] text-[#6b6b6b]",
  headerActions: "flex w-full items-center gap-2 sm:w-auto",
  btnBase:
    "h-[38px] cursor-pointer rounded-md border-[1.5px] px-3.5 text-[13px] font-medium leading-none transition-all duration-150",
  btnPrimary: "border-[#c8508a] bg-[#c8508a] text-white hover:border-[#b3407a] hover:bg-[#b3407a]",
  btnGhost: "border-[#c8508a] bg-transparent text-[#c8508a] hover:bg-[#fbeaf0]",
  advancedFiltersToggle: "whitespace-nowrap lg:w-[190px]",
  skeletonPulse: "animate-pulse [animation-duration:2.5s]",
  marketTabs: "flex flex-wrap gap-1.5",
  marketTab:
    "h-[38px] cursor-pointer rounded-[10px] border-[1.5px] border-[#e8e8e8] bg-white px-[22px] text-sm font-medium text-[#6b6b6b]",
  marketTabActive: "!border-[#c8508a] !bg-[#c8508a] !text-white",
  filtersStickyShell: "sticky z-40 grid gap-2.5",
  filters:
    "grid grid-cols-1 items-center gap-2.5 rounded-[14px] border border-[#e8e8e8] bg-white px-[14px] py-[14px] shadow-[0_8px_22px_rgba(0,0,0,0.08)] md:grid-cols-2 md:px-4 lg:px-[18px]",
  filtersGridWithSubcategory:
    "lg:grid-cols-[minmax(120px,0.7fr)_minmax(130px,0.9fr)_minmax(130px,0.9fr)_minmax(260px,1.7fr)_auto]",
  filtersGridWithoutSubcategory:
    "lg:grid-cols-[minmax(120px,0.75fr)_minmax(130px,0.95fr)_minmax(260px,1.8fr)_auto]",
  filtersMobileEdge: "max-md:-mx-3.5 max-md:rounded-none max-md:border-x-0 max-md:px-3.5",
  mobileFilters:
    "-mx-3.5 overflow-hidden border-y border-[#e8e8e8] bg-white shadow-[0_8px_22px_rgba(0,0,0,0.08)] md:hidden",
  mobileFiltersScroller:
    "flex snap-x snap-proximity items-center gap-2 overflow-x-auto px-3.5 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
  mobileFilterPill:
    "inline-flex h-10 shrink-0 snap-start cursor-pointer items-center gap-2 whitespace-nowrap rounded-[12px] border border-transparent bg-[#f5f3f4] px-3.5 text-[13px] font-semibold text-[#262326] transition active:scale-[0.98]",
  mobileFilterIconButton: "relative !w-10 !justify-center !px-0",
  mobileFilterPillActive: "border-[#c8508a] bg-[#fff7fb] text-[#8b2a5e]",
  mobileFilterCount:
    "absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-[#c8508a] px-1.5 text-[11px] font-bold leading-none text-white",
  mobileFilterSelect:
    "h-10 shrink-0 snap-start cursor-pointer appearance-none whitespace-nowrap rounded-[12px] border border-transparent bg-[#f5f3f4] bg-[linear-gradient(45deg,transparent_50%,#4a4648_50%),linear-gradient(135deg,#4a4648_50%,transparent_50%)] bg-[position:calc(100%-15px)_17px,calc(100%-10px)_17px] bg-[size:5px_5px,5px_5px] bg-no-repeat py-0 pl-3.5 pr-8 text-[13px] font-semibold text-[#262326] outline-none focus:border-[#c8508a]",
  mobileSearchRow: "grid grid-cols-[minmax(0,1fr)_auto] gap-2 px-3.5",
  mobileSearchInput:
    "h-11 min-w-0 rounded-[12px] border border-[#e8e8e8] bg-[#f7f7f7] px-3.5 text-[14px] text-[#1a1a1a] outline-none placeholder:text-[#9a9698] focus:border-[#c8508a] focus:bg-white",
  mobileSearchSubmit:
    "grid h-11 w-11 cursor-pointer place-items-center rounded-[12px] border-0 bg-[#c8508a] text-white transition active:scale-[0.96]",
  advancedFiltersDropdown:
    "absolute left-0 right-0 top-[calc(100%+8px)] z-50 max-h-[min(68vh,620px)] overflow-y-auto overscroll-contain rounded-[18px] border border-[#e8e8e8] bg-white p-3.5 shadow-[0_18px_45px_rgba(0,0,0,0.16)] max-md:left-[-14px] max-md:right-[-14px] max-md:max-h-[62vh] max-md:rounded-none max-md:border-x-0 max-md:px-3.5",
  advancedFiltersActions:
    "flex flex-col gap-2 border-t border-[#f0d6e3] pt-3 md:col-span-2 md:flex-row md:justify-end xl:col-span-4",
  input:
    "h-[38px] w-full rounded-md border border-[#e8e8e8] bg-[#f7f7f7] px-3 text-[13px] text-[#1a1a1a] outline-none placeholder:text-[#a0a0a0] focus:border-[#c8508a] focus:bg-white",
  textarea:
    "min-h-[120px] max-h-[320px] w-full resize-none rounded-md border border-[#e8e8e8] bg-[#f7f7f7] px-3 py-2 text-[13px] leading-[1.5] text-[#1a1a1a] outline-none placeholder:text-[#a0a0a0] focus:border-[#c8508a] focus:bg-white",
  inputClearButton:
    "absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 cursor-pointer place-items-center rounded-full border border-transparent bg-transparent text-[18px] leading-none text-[#9b9b9b] transition hover:border-[#efc5d8] hover:bg-white hover:text-[#8b2a5e]",
  searchClearButton:
    "absolute right-10 top-1/2 grid h-6 w-6 -translate-y-1/2 cursor-pointer place-items-center rounded-full border border-transparent bg-transparent text-[18px] leading-none text-[#9b9b9b] transition hover:border-[#efc5d8] hover:bg-white hover:text-[#8b2a5e]",
  searchSubmitButton:
    "absolute right-1 top-1/2 grid h-[30px] w-[30px] -translate-y-1/2 cursor-pointer place-items-center rounded-md border border-transparent bg-[#c8508a] text-white transition hover:bg-[#b3407a] focus:outline-none focus:ring-2 focus:ring-[#efc5d8]",
  select:
    "h-[38px] w-full cursor-pointer appearance-none rounded-md border border-[#e8e8e8] bg-[#f7f7f7] bg-[linear-gradient(45deg,transparent_50%,#7a7a7a_50%),linear-gradient(135deg,#7a7a7a_50%,transparent_50%)] bg-[position:calc(100%-14px)_16px,calc(100%-9px)_16px] bg-[size:5px_5px,5px_5px] bg-no-repeat px-3 pr-[30px] text-[13px] text-[#1a1a1a] outline-none focus:border-[#c8508a] focus:bg-white",
  checkbox: "inline-flex cursor-pointer items-center gap-[7px] whitespace-nowrap text-[13px] text-[#6b6b6b]",
  checkboxInput: "h-[15px] w-[15px] cursor-pointer accent-[#c8508a]",
  toolbar: "mb-1 flex flex-wrap items-center justify-between gap-3",
  toolbarControls: "flex w-full flex-wrap items-center justify-end gap-3 sm:w-auto sm:flex-1",
  viewTabs: "w-full sm:w-auto sm:shrink-0",
  foundCount: "min-w-0 text-sm text-[#6b6b6b]",
  chips: "flex flex-wrap justify-end gap-1.5",
  chip: "cursor-pointer rounded-full border border-[#e8e8e8] bg-white px-[13px] py-[5px] text-xs text-[#6b6b6b]",
  chipActive: "border-[#c8508a] bg-[#fbeaf0] text-[#8b2a5e]",
  appliedFilters:
    "flex flex-wrap items-center gap-1.5 rounded-[12px] border border-[#f0d6e3] bg-[#fff7fb] px-3 py-2 text-xs text-[#6b6b6b]",
  appliedFilterBadge: "rounded-full border border-[#e8c5d8] bg-white px-2.5 py-1 font-medium text-[#8b2a5e]",
  block: "grid gap-2.5 rounded-[18px] border border-[#e8e8e8] bg-white p-3.5",
  blockMobileEdge: "max-md:-mx-3.5 max-md:rounded-none max-md:border-x-0 max-md:px-3.5",
  formMarket: "grid grid-cols-1 gap-2 md:grid-cols-3",
  formGrid: "grid grid-cols-1 gap-2.5 md:grid-cols-2",
  formField: "grid gap-1.5",
  formLabel: "text-xs text-[#6b6b6b]",
  sectionTitle: "mt-0.5 text-xs font-bold uppercase tracking-[0.04em] text-[#6b6b6b]",
  errorText: "text-[13px] text-[#b42318]",
  formActions: "flex justify-end",
  stepperDock:
    "sticky z-20 grid gap-2 rounded-[16px] border border-[#e8e8e8] bg-white/95 p-2.5 backdrop-blur md:static md:rounded-[18px] md:bg-white md:p-3",
  stepper: "grid grid-cols-2 gap-2",
  previewToggleBtn:
    "h-[36px] w-full cursor-pointer rounded-md border border-[#c8508a] bg-[#fff2f8] px-3 text-[13px] font-medium text-[#8b2a5e] transition hover:bg-[#fbeaf0] md:hidden",
  stepItem: "flex items-center gap-2 rounded-xl border border-[#ececec] bg-[#fafafa] px-2.5 py-2 text-left",
  stepItemActive: "border-[#c8508a] bg-[#fff2f8]",
  stepItemDone: "border-[#e8c5d8] bg-[#fff7fb]",
  stepDot:
    "grid h-6 w-6 shrink-0 place-items-center rounded-full border border-[#d3d3d3] bg-white text-[11px] font-semibold text-[#6b6b6b]",
  stepDotActive: "!border-[#c8508a] !bg-[#c8508a] !text-white",
  stepDotDone: "border-[#c8508a] bg-[#fbeaf0] text-[#8b2a5e]",
  stepText: "text-[12px] font-medium text-[#6b6b6b]",
  stepTextActive: "text-[#8b2a5e]",
  createLayout: "grid gap-3.5 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start",
  createMain: "grid gap-3.5",
  previewWrap: "xl:sticky",
  previewWrapDesktop: "hidden xl:block",
  previewWrapMobile: "xl:hidden",
  previewCard: "grid gap-3 rounded-[18px] border border-[#e8e8e8] bg-white p-3.5",
  previewImage: "relative h-[180px] w-full overflow-hidden rounded-xl bg-[#f3f3f3]",
  previewImageTag: "h-full w-full object-cover",
  previewPlaceholder: "grid h-full place-items-center text-[13px] text-[#9b9b9b]",
  previewTitle: "text-[16px] font-semibold leading-[1.35] text-[#1a1a1a]",
  previewPrice: "text-[20px] font-bold leading-none text-[#8b2a5e]",
  previewMeta: "text-[13px] leading-[1.45] text-[#6b6b6b]",
  previewBadges: "flex flex-wrap gap-1.5",
  helperText: "text-[11px] leading-[1.45] text-[#8a8a8a]",
  requiredMark: "ml-0.5 text-[#c8508a]",
  uploadGrid: "grid gap-2",
  uploadItemCard: "grid gap-2 rounded-lg border border-[#eed8e4] bg-white p-2.5",
  subtleBlock: "grid gap-2 rounded-xl border border-[#efefef] bg-[#fafafa] p-3",
  checksGrid: "grid grid-cols-1 gap-2 sm:grid-cols-2",
  uploadPanel: "grid gap-3 rounded-xl border border-[#edd5e1] bg-[#fff7fb] p-3",
  uploadDropzone:
    "grid min-h-[110px] cursor-pointer place-items-center rounded-lg border-2 border-dashed border-[#d9adc3] bg-white p-4 text-center transition hover:border-[#c8508a] hover:bg-[#fff2f8]",
  uploadHint: "text-xs text-[#8b2a5e]",
  uploadList: "grid gap-2",
  uploadItem: "grid gap-2 rounded-lg border border-[#eed8e4] bg-white p-2.5",
  uploadRow: "flex items-start justify-between gap-2",
  uploadInfo: "flex min-w-0 flex-1 items-center gap-2",
  uploadText: "min-w-0 flex-1",
  uploadName: "truncate text-[13px] text-[#1a1a1a]",
  uploadMeta: "truncate text-[11px] text-[#7a7a7a]",
  uploadActions: "flex shrink-0 items-center gap-1.5",
  uploadDrag:
    "grid h-7 w-5 cursor-grab place-items-center rounded-sm text-[#cdcdcd] transition hover:bg-[#f6f6f6] hover:text-[#9f9f9f] active:cursor-grabbing",
  uploadGrip: "grid grid-cols-2 gap-[3px]",
  uploadGripDot: "h-[5px] w-[5px] rounded-full bg-current",
  uploadItemDragging: "opacity-70",
  uploadDropPlaceholder: "h-3 rounded-md border-2 border-dashed border-[#c8508a] bg-[#fff2f8]",
  uploadPrimaryBadge: "rounded-full bg-[#fbeaf0] px-2 py-[3px] text-[10px] font-semibold text-[#8b2a5e]",
  uploadRemove:
    "cursor-pointer rounded-md border border-[#efc5d8] px-2 py-1 text-[11px] text-[#8b2a5e] hover:bg-[#fdeef5]",
  uploadTrack: "h-2 overflow-hidden rounded-full bg-[#f3e6ed]",
  uploadFill: "h-full rounded-full bg-[#c8508a] transition-all duration-200",
  uploadPreview: "h-16 w-16 rounded-md object-cover",
  empty: "rounded-[14px] border border-dashed border-[#e8e8e8] bg-white px-3.5 py-[30px] text-center text-[#6b6b6b]",
  list: "m-0 grid auto-rows-fr list-none grid-cols-1 gap-3 p-0 sm:gap-4 md:grid-cols-2 md:gap-4 lg:gap-[18px] xl:grid-cols-3 max-md:-mx-3.5",
  card: "flex h-full cursor-pointer flex-col overflow-hidden rounded-none border-y border-[#e8e8e8] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_8px_rgba(0,0,0,0.04)] transition duration-200 hover:-translate-y-[1px] hover:shadow-[0_4px_16px_rgba(200,80,138,0.12),0_1px_4px_rgba(0,0,0,0.08)] md:rounded-[18px] md:border",
  cardImage: "relative h-[200px] w-full overflow-hidden bg-[#ddd]",
  cardImageTag: "block h-full w-full object-cover",
  cardPlaceholder: "flex h-full w-full items-center justify-center text-[13px] text-[#6b6b6b]",
  cardBody: "grid flex-1 content-start gap-2 px-4 pb-4 pt-3.5",
  cardTop: "mb-px flex items-start justify-between gap-2.5",
  cardTypeWrap: "flex flex-wrap items-center gap-1.5",
  cardType: "rounded-full bg-[#fbeaf0] px-2.5 py-1 text-[11px] font-semibold text-[#8b2a5e]",
  cardStatus: "text-[11px] text-[#a0a0a0]",
  cardTitle: "m-0 min-h-[40px] text-[15px] leading-[1.35] tracking-[-0.2px] text-[#1a1a1a] line-clamp-2",
  cardMeta: "text-xs leading-[1.45] text-[#6b6b6b]",
  cardMainPrice: "text-[22px] font-bold leading-[1.15] text-[#8b2a5e]",
  cardPriceDescriptor: "min-h-[18px] text-xs leading-[1.45] text-[#6b6b6b]",
  cardPrices: "flex flex-wrap gap-2",
  priceChip: "rounded-full bg-[#f2f2f2] px-2 py-[3px] text-[11px] font-medium text-[#1a1a1a]",
  cardPublicationRow: "mt-1 flex flex-wrap items-center justify-between gap-2 border-t border-[#efefef] pt-3",
  cardPublicationStatus: "inline-flex items-center gap-1.5 text-[12px] font-medium",
  cardPublicationDot: "h-2 w-2 rounded-full",
  cardPublicationButton:
    "h-[34px] cursor-pointer rounded-md border px-3 text-[12px] font-semibold leading-none transition disabled:cursor-wait disabled:opacity-60",
  cardAdminActions: "flex w-full flex-wrap gap-2",
  cardAdminButton:
    "h-[34px] cursor-pointer rounded-md border px-3 text-[12px] font-semibold leading-none transition disabled:cursor-wait disabled:opacity-60",
  cardPublicationError: "w-full text-[12px] leading-[1.4] text-[#b42318]",
  mapLayout: "grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)] lg:items-start",
  mapPanel: "relative min-h-[420px] overflow-hidden rounded-none border-y border-[#e8e8e8] bg-[#f2f2f2] md:rounded-[18px] md:border",
  mapCanvas: "h-[62svh] min-h-[420px] w-full lg:h-[calc(100svh-270px)] lg:min-h-[560px]",
  mapSearch: "absolute left-3 right-3 top-3 z-10 grid gap-2 rounded-xl border border-[#e8e8e8] bg-white/95 p-2 shadow-[0_8px_22px_rgba(0,0,0,0.12)] backdrop-blur md:left-4 md:right-auto md:w-[360px]",
  mapSide: "grid gap-3 lg:sticky",
} as const;

type ListingLocationPickerProps = {
  latitudeValue: string;
  longitudeValue: string;
  address: AddressState;
  coordinateDecimalPlaces: number;
  error?: string | null;
  onCoordinatesChange: (coordinates: { latitude: string; longitude: string }) => void;
  onAddressChange: (address: AddressState) => void;
};

const ListingLocationPicker: React.FC<ListingLocationPickerProps> = ({
  latitudeValue,
  longitudeValue,
  address,
  coordinateDecimalPlaces,
  error,
  onCoordinatesChange,
  onAddressChange,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<YandexMapInstance | null>(null);
  const placemarkRef = useRef<YandexPlacemarkInstance | null>(null);
  const onCoordinatesChangeRef = useRef(onCoordinatesChange);
  const onAddressChangeRef = useRef(onAddressChange);
  const searchRequestRef = useRef(0);
  const [mapStatus, setMapStatus] = useState<"loading" | "ready" | "error">("loading");
  const [searchStatus, setSearchStatus] = useState<"idle" | "searching" | "found" | "not_found" | "error">("idle");
  const [addressInput, setAddressInput] = useState(buildAddressQuery(address));
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const latitude = parseCoordinate(latitudeValue);
  const longitude = parseCoordinate(longitudeValue);
  const coordinates = useMemo<YandexCoordinates>(
    () => (latitude !== null && longitude !== null ? [latitude, longitude] : MAP_DEFAULT_COORDINATES),
    [latitude, longitude],
  );
  const coordinatesRef = useRef<YandexCoordinates>(coordinates);
  const externalAddressQuery = useMemo(() => buildAddressQuery(address), [address]);
  const lastResolvedAddressRef = useRef(externalAddressQuery);

  useEffect(() => {
    onCoordinatesChangeRef.current = onCoordinatesChange;
  }, [onCoordinatesChange]);

  useEffect(() => {
    onAddressChangeRef.current = onAddressChange;
  }, [onAddressChange]);

  useEffect(() => {
    coordinatesRef.current = coordinates;
    placemarkRef.current?.geometry.setCoordinates(coordinates);
    mapRef.current?.setCenter(coordinates, 15, { duration: 180 });
  }, [coordinates]);

  useEffect(() => {
    if (externalAddressQuery && externalAddressQuery !== lastResolvedAddressRef.current) {
      lastResolvedAddressRef.current = externalAddressQuery;
      setAddressInput(externalAddressQuery);
    }
  }, [externalAddressQuery]);

  const applyCoordinates = useCallback((nextCoordinates: YandexCoordinates) => {
    onCoordinatesChangeRef.current({
      latitude: formatCoordinate(nextCoordinates[0], coordinateDecimalPlaces),
      longitude: formatCoordinate(nextCoordinates[1], coordinateDecimalPlaces),
    });
  }, [coordinateDecimalPlaces]);

  const applySuggestion = useCallback(
    (suggestion: AddressSuggestion) => {
      lastResolvedAddressRef.current = suggestion.fullAddress;
      setAddressInput(suggestion.fullAddress);
      setSuggestions([]);
      setDropdownOpen(false);
      setSearchStatus("found");
      onAddressChangeRef.current(suggestion.address);
      applyCoordinates(suggestion.coordinates);
      placemarkRef.current?.geometry.setCoordinates(suggestion.coordinates);
      mapRef.current?.setCenter(suggestion.coordinates, 16, { duration: 180 });
    },
    [applyCoordinates],
  );

  const fillAddressByCoordinates = useCallback(async (nextCoordinates: YandexCoordinates) => {
    try {
      setSearchStatus("searching");
      const ymaps = await loadYandexMaps();
      const result = await ymaps.geocode(nextCoordinates, { results: 1, kind: "house" });
      const geoObject = result.geoObjects.get(0);
      if (!geoObject) {
        onAddressChangeRef.current({
          address_region: "",
          address_locality: "",
          address_street: "",
          address_house: "",
        });
        setAddressInput("");
        setSuggestions([]);
        setDropdownOpen(false);
        setSearchStatus("not_found");
        return;
      }

      const suggestion = buildAddressSuggestion(geoObject, 0);
      if (suggestion) {
        lastResolvedAddressRef.current = suggestion.fullAddress;
        setAddressInput(suggestion.fullAddress);
        setSuggestions([]);
        setDropdownOpen(false);
        onAddressChangeRef.current(suggestion.address);
        setSearchStatus("found");
      } else {
        onAddressChangeRef.current({
          address_region: "",
          address_locality: "",
          address_street: "",
          address_house: "",
        });
        setAddressInput("");
        setSuggestions([]);
        setDropdownOpen(false);
        setSearchStatus("not_found");
      }
    } catch {
      setSearchStatus("error");
    }
  }, []);

  const selectCoordinates = useCallback(
    (nextCoordinates: YandexCoordinates, shouldFillAddress: boolean) => {
      applyCoordinates(nextCoordinates);
      placemarkRef.current?.geometry.setCoordinates(nextCoordinates);
      mapRef.current?.setCenter(nextCoordinates, 15, { duration: 180 });
      if (shouldFillAddress) {
        void fillAddressByCoordinates(nextCoordinates);
      }
    },
    [applyCoordinates, fillAddressByCoordinates],
  );

  useEffect(() => {
    let cancelled = false;
    setMapStatus("loading");

    loadYandexMaps()
      .then((ymaps) => {
        if (cancelled || !containerRef.current) return;

        const map = new ymaps.Map(
          containerRef.current,
          {
            center: coordinatesRef.current,
            zoom: 15,
            controls: ["zoomControl", "fullscreenControl", "geolocationControl"],
          },
          {
            suppressMapOpenBlock: true,
            yandexMapDisablePoiInteractivity: true,
          },
        );
        const placemark = new ymaps.Placemark(
          coordinatesRef.current,
          {},
          {
            draggable: true,
            preset: "islands#redHomeIcon",
          },
        );

        map.geoObjects.add(placemark);
        map.events.add("click", (event: YandexMapEvent) => {
          const eventCoordinates = event.get("coords");
          if (Array.isArray(eventCoordinates) && eventCoordinates.length >= 2) {
            selectCoordinates([Number(eventCoordinates[0]), Number(eventCoordinates[1])], true);
          }
        });
        placemark.events.add("dragend", () => {
          selectCoordinates(placemark.geometry.getCoordinates(), true);
        });

        mapRef.current = map;
        placemarkRef.current = placemark;
        setMapStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setMapStatus("error");
      });

    return () => {
      cancelled = true;
      mapRef.current?.destroy();
      mapRef.current = null;
      placemarkRef.current = null;
    };
  }, [selectCoordinates]);

  useEffect(() => {
    const query = addressInput.trim();
    if (query.length < 3) {
      setSuggestions([]);
      setSearchStatus("idle");
      return;
    }
    if (query === lastResolvedAddressRef.current) return;

    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    const timeout = window.setTimeout(async () => {
      try {
        setSearchStatus("searching");
        const ymaps = await loadYandexMaps();
        const result = await ymaps.geocode(query, { results: 5 });
        if (searchRequestRef.current !== requestId) return;

        const foundSuggestions = Array.from({
          length: Math.min(result.geoObjects.getLength(), 5),
        })
          .map((_, index) => {
            const geoObject = result.geoObjects.get(index);
            return geoObject ? buildAddressSuggestion(geoObject, index) : null;
          })
          .filter((item): item is AddressSuggestion => Boolean(item));

        setSuggestions(foundSuggestions);
        setDropdownOpen(true);
        if (!foundSuggestions.length) {
          setSearchStatus("not_found");
          return;
        }

        setSearchStatus("found");
      } catch {
        setSearchStatus("error");
      }
    }, 800);

    return () => window.clearTimeout(timeout);
  }, [addressInput]);

  const handleAddressInputChange = (value: string) => {
    setAddressInput(value);
    setDropdownOpen(true);
    if (!value.trim()) {
      setSuggestions([]);
      setSearchStatus("idle");
      lastResolvedAddressRef.current = "";
      onAddressChangeRef.current({
        address_region: "",
        address_locality: "",
        address_street: "",
        address_house: "",
      });
    }
  };

  const statusText =
    mapStatus === "error"
      ? "Карту не удалось загрузить."
      : searchStatus === "searching"
        ? "Ищем адрес..."
        : searchStatus === "not_found"
          ? "Адрес не найден. Поставьте точку на карте."
          : searchStatus === "error"
            ? "Не удалось определить адрес."
            : "";

  return (
    <div className="grid gap-2">
      <label className={ui.formField}>
        <span className={ui.formLabel}>Адрес</span>
        <div className="relative">
          <input
            id="listing-address"
            className={`${ui.input} pr-9 ${error ? "!border-[#b42318] !bg-[#fff7f6]" : ""}`}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "listing-address-error" : undefined}
            placeholder="Начните вводить адрес"
            value={addressInput}
            onBlur={() => window.setTimeout(() => setDropdownOpen(false), 180)}
            onChange={(event) => handleAddressInputChange(event.target.value)}
            onFocus={() => setDropdownOpen(true)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && suggestions[0]) {
                event.preventDefault();
                applySuggestion(suggestions[0]);
              }
            }}
          />
          {addressInput ? (
            <button
              type="button"
              aria-label="Очистить адрес"
              className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full border-0 bg-transparent text-[20px] leading-none text-[#6b6b6b] transition hover:bg-white hover:text-[#1a1a1a]"
              onClick={() => handleAddressInputChange("")}
            >
              ×
            </button>
          ) : null}

          {dropdownOpen && (suggestions.length > 0 || searchStatus === "searching" || searchStatus === "not_found") ? (
            <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-30 overflow-hidden rounded-2xl border border-[#ececec] bg-white py-2 shadow-[0_18px_36px_rgba(0,0,0,0.16)]">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  type="button"
                  className="grid w-full cursor-pointer gap-1 border-0 bg-white px-4 py-3 text-left transition hover:bg-[#f7f7f7]"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applySuggestion(suggestion)}
                >
                  <span className="text-[15px] font-semibold leading-5 text-[#1a1a1a]">{suggestion.title}</span>
                  {suggestion.subtitle ? (
                    <span className="text-[13px] leading-5 text-[#777]">{suggestion.subtitle}</span>
                  ) : null}
                </button>
              ))}

              {searchStatus === "searching" ? (
                <div className="px-4 py-3 text-[13px] text-[#777]">Ищем адрес...</div>
              ) : null}

              {searchStatus === "not_found" && !suggestions.length ? (
                <div className="px-4 py-3 text-[13px] leading-5 text-[#777]">
                  Подходящий адрес не найден. Поставьте точку на карте ниже.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </label>

      <div className="relative h-[320px] overflow-hidden rounded-xl border border-[#efefef] bg-[#f2f2f2]">
        <div ref={containerRef} className="h-full w-full" />
        {mapStatus === "loading" ? (
          <div className="absolute inset-0 grid place-items-center bg-[#f7f7f7] text-sm text-[#777]">
            Загружаем карту...
          </div>
        ) : null}
        {mapStatus === "error" ? (
          <div className="absolute inset-0 grid place-items-center bg-[#f7f7f7] px-5 text-center text-sm leading-6 text-[#777]">
            Карту не удалось загрузить.
          </div>
        ) : null}
      </div>
      {error ? (
        <div id="listing-address-error" className={ui.errorText} role="alert">
          {error}
        </div>
      ) : null}
      {statusText ? <div className={ui.helperText}>{statusText}</div> : null}
    </div>
  );
};

type FilterControlsProps = {
  marketFilter: MarketFilter;
  search: string;
  categoryFilter: string;
  subcategoryFilter: string;
  stickyTopOffset: number;
  showAdvancedFilters: boolean;
  advancedFilters: AdvancedFiltersState;
  filterCapabilities: ClassifiedsFilterCapabilities;
  filterCategoryOptions: FilterCategoryOption[];
  filterSubcategoryOptions: Array<{ value: string; label: string; listing_type: ListingType }>;
  syncRevision: number;
  onApply: (payload: { search: string; advancedFilters: AdvancedFiltersState }) => void;
  onSearchClear: () => void;
  onReset: () => void;
  onMarketChange: (value: MarketType) => void;
  onCategoryChange: (value: string) => void;
  onSubcategoryChange: (value: string) => void;
  onToggleAdvanced: () => void;
  onAdvancedFiltersChange: React.Dispatch<React.SetStateAction<AdvancedFiltersState>>;
};

const FilterControls = React.memo(
  ({
    marketFilter,
    search,
    categoryFilter,
    subcategoryFilter,
    stickyTopOffset,
    showAdvancedFilters,
    advancedFilters,
    filterCapabilities,
    filterCategoryOptions,
    filterSubcategoryOptions,
    syncRevision,
    onApply,
    onSearchClear,
    onReset,
    onMarketChange,
    onCategoryChange,
    onSubcategoryChange,
    onToggleAdvanced,
    onAdvancedFiltersChange,
  }: FilterControlsProps) => {
    const searchRef = useRef<HTMLInputElement | null>(null);
    const mobileSearchRef = useRef<HTMLInputElement | null>(null);
    const categoryRef = useRef<HTMLSelectElement | null>(null);
    const subcategoryRef = useRef<HTMLSelectElement | null>(null);
    const priceSqmMinRef = useRef<HTMLInputElement | null>(null);
    const priceSqmMaxRef = useRef<HTMLInputElement | null>(null);
    const priceMonthlyMinRef = useRef<HTMLInputElement | null>(null);
    const priceMonthlyMaxRef = useRef<HTMLInputElement | null>(null);
    const areaMinRef = useRef<HTMLInputElement | null>(null);
    const areaMaxRef = useRef<HTMLInputElement | null>(null);
    const utilitiesPayerRef = useRef<HTMLSelectElement | null>(null);
    const rentTermRef = useRef<HTMLSelectElement | null>(null);
    const agentCommissionUnitRef = useRef<HTMLSelectElement | null>(null);
    const agentCommissionMinRef = useRef<HTMLInputElement | null>(null);
    const agentCommissionMaxRef = useRef<HTMLInputElement | null>(null);
    const commissionSortRef = useRef<HTMLSelectElement | null>(null);
    const sortDirectionRef = useRef<HTMLSelectElement | null>(null);
    const hasImageRef = useRef<HTMLInputElement | null>(null);
    const [agentCommissionUnit, setAgentCommissionUnit] = useState(advancedFilters.agentCommissionUnit);
    const [commissionSort, setCommissionSort] = useState(advancedFilters.commissionSort);
    const [mobileSearchVisible, setMobileSearchVisible] = useState(Boolean(search));
    const [rangeErrors, setRangeErrors] = useState<FilterRangeErrors>(() =>
      getFilterRangeErrors(advancedFilters),
    );
    const activeFilterCount = countActiveFilters(
      categoryFilter,
      subcategoryFilter,
      advancedFilters,
      filterCapabilities,
    );
    const selectedCategoryLabel =
      filterCategoryOptions.find((item) => item.value === categoryFilter)?.label || "Категория";
    const selectedSubcategoryLabel =
      filterSubcategoryOptions.find((item) => item.value === subcategoryFilter)?.label || "Подкатегория";

    useEffect(() => {
      if (searchRef.current) searchRef.current.value = search;
      if (mobileSearchRef.current) mobileSearchRef.current.value = search;
      if (categoryRef.current) categoryRef.current.value = categoryFilter;
      if (subcategoryRef.current) subcategoryRef.current.value = subcategoryFilter;
      if (priceSqmMinRef.current) priceSqmMinRef.current.value = formatPriceInput(advancedFilters.priceSqmMin);
      if (priceSqmMaxRef.current) priceSqmMaxRef.current.value = formatPriceInput(advancedFilters.priceSqmMax);
      if (priceMonthlyMinRef.current) {
        priceMonthlyMinRef.current.value = formatPriceInput(advancedFilters.priceMonthlyMin);
      }
      if (priceMonthlyMaxRef.current) {
        priceMonthlyMaxRef.current.value = formatPriceInput(advancedFilters.priceMonthlyMax);
      }
      if (areaMinRef.current) areaMinRef.current.value = formatDecimalInput(advancedFilters.areaMin);
      if (areaMaxRef.current) areaMaxRef.current.value = formatDecimalInput(advancedFilters.areaMax);
      if (utilitiesPayerRef.current) utilitiesPayerRef.current.value = advancedFilters.utilitiesPayer;
      if (rentTermRef.current) rentTermRef.current.value = advancedFilters.rentTerm;
      if (agentCommissionUnitRef.current) agentCommissionUnitRef.current.value = advancedFilters.agentCommissionUnit;
      if (agentCommissionMinRef.current) {
        agentCommissionMinRef.current.value =
          advancedFilters.agentCommissionUnit === "amount"
            ? formatPriceInput(advancedFilters.agentCommissionMin)
            : formatDecimalInput(advancedFilters.agentCommissionMin);
      }
      if (agentCommissionMaxRef.current) {
        agentCommissionMaxRef.current.value =
          advancedFilters.agentCommissionUnit === "amount"
            ? formatPriceInput(advancedFilters.agentCommissionMax)
            : formatDecimalInput(advancedFilters.agentCommissionMax);
      }
      if (commissionSortRef.current) commissionSortRef.current.value = advancedFilters.commissionSort;
      if (sortDirectionRef.current) sortDirectionRef.current.value = advancedFilters.sortDirection;
      if (hasImageRef.current) hasImageRef.current.checked = advancedFilters.hasImage;
      setAgentCommissionUnit(advancedFilters.agentCommissionUnit);
      setCommissionSort(advancedFilters.commissionSort);
      setMobileSearchVisible(Boolean(search));
      setRangeErrors(getFilterRangeErrors(advancedFilters));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [syncRevision]);

    useEffect(() => {
      if (categoryRef.current) categoryRef.current.value = categoryFilter;
      if (subcategoryRef.current) subcategoryRef.current.value = subcategoryFilter;
    }, [categoryFilter, filterSubcategoryOptions.length, subcategoryFilter]);

    useEffect(() => {
      if (utilitiesPayerRef.current) utilitiesPayerRef.current.value = advancedFilters.utilitiesPayer;
      if (rentTermRef.current) rentTermRef.current.value = advancedFilters.rentTerm;
      if (agentCommissionUnitRef.current) agentCommissionUnitRef.current.value = advancedFilters.agentCommissionUnit;
      if (commissionSortRef.current) commissionSortRef.current.value = advancedFilters.commissionSort;
      if (sortDirectionRef.current) sortDirectionRef.current.value = advancedFilters.sortDirection;
      if (hasImageRef.current) hasImageRef.current.checked = advancedFilters.hasImage;
      setAgentCommissionUnit(advancedFilters.agentCommissionUnit);
      setCommissionSort(advancedFilters.commissionSort);
    }, [
      advancedFilters.agentCommissionUnit,
      advancedFilters.commissionSort,
      advancedFilters.hasImage,
      advancedFilters.rentTerm,
      advancedFilters.sortDirection,
      advancedFilters.utilitiesPayer,
      filterCapabilities,
    ]);

    const inputValue = (ref: React.RefObject<HTMLInputElement | null>, fallback: string) =>
      ref.current ? ref.current.value : fallback;
    const selectValue = <T extends string>(ref: React.RefObject<HTMLSelectElement | null>, fallback: T) =>
      (ref.current ? ref.current.value : fallback) as T;

    const collectAdvancedFilters = (): AdvancedFiltersState => {
      const nextAgentCommissionUnit = filterCapabilities.agentCommission
        ? selectValue(agentCommissionUnitRef, advancedFilters.agentCommissionUnit)
        : "percent";
      const agentCommissionMin = filterCapabilities.agentCommission
        ? inputValue(agentCommissionMinRef, advancedFilters.agentCommissionMin)
        : "";
      const agentCommissionMax = filterCapabilities.agentCommission
        ? inputValue(agentCommissionMaxRef, advancedFilters.agentCommissionMax)
        : "";

      return {
        priceSqmMin: filterCapabilities.priceSqm
          ? normalizePriceInput(inputValue(priceSqmMinRef, advancedFilters.priceSqmMin))
          : "",
        priceSqmMax: filterCapabilities.priceSqm
          ? normalizePriceInput(inputValue(priceSqmMaxRef, advancedFilters.priceSqmMax))
          : "",
        priceMonthlyMin: filterCapabilities.priceMonthly
          ? normalizePriceInput(inputValue(priceMonthlyMinRef, advancedFilters.priceMonthlyMin))
          : "",
        priceMonthlyMax: filterCapabilities.priceMonthly
          ? normalizePriceInput(inputValue(priceMonthlyMaxRef, advancedFilters.priceMonthlyMax))
          : "",
        areaMin: filterCapabilities.area ? normalizeAreaInput(inputValue(areaMinRef, advancedFilters.areaMin)) : "",
        areaMax: filterCapabilities.area ? normalizeAreaInput(inputValue(areaMaxRef, advancedFilters.areaMax)) : "",
        utilitiesPayer: filterCapabilities.utilitiesPayer
          ? selectValue(utilitiesPayerRef, advancedFilters.utilitiesPayer)
          : "all",
        rentTerm: filterCapabilities.rentTerm ? selectValue(rentTermRef, advancedFilters.rentTerm) : "all",
        agentCommissionUnit: nextAgentCommissionUnit,
        agentCommissionMin:
          !filterCapabilities.agentCommission
            ? ""
            : nextAgentCommissionUnit === "amount"
            ? normalizePriceInput(agentCommissionMin)
            : normalizeAreaInput(agentCommissionMin),
        agentCommissionMax:
          !filterCapabilities.agentCommission
            ? ""
            : nextAgentCommissionUnit === "amount"
            ? normalizePriceInput(agentCommissionMax)
            : normalizeAreaInput(agentCommissionMax),
        commissionSort: filterCapabilities.commissionSort
          ? selectValue(commissionSortRef, advancedFilters.commissionSort)
          : "default",
        sortDirection: filterCapabilities.commissionSort
          ? selectValue(sortDirectionRef, advancedFilters.sortDirection)
          : "desc",
        hasImage: filterCapabilities.hasImage
          ? hasImageRef.current
            ? hasImageRef.current.checked
            : advancedFilters.hasImage
          : false,
      };
    };

    const handleApply = () => {
      const nextAdvancedFilters = collectAdvancedFilters();
      const nextRangeErrors = getFilterRangeErrors(nextAdvancedFilters);
      setRangeErrors(nextRangeErrors);
      const firstInvalidRange = (Object.keys(nextRangeErrors) as FilterRangeKey[])[0];

      if (firstInvalidRange) {
        const invalidRangeRefs: Record<FilterRangeKey, React.RefObject<HTMLInputElement | null>> = {
          priceSqm: priceSqmMaxRef,
          priceMonthly: priceMonthlyMaxRef,
          area: areaMaxRef,
          agentCommission: agentCommissionMaxRef,
        };
        invalidRangeRefs[firstInvalidRange].current?.focus();
        return;
      }

      onApply({
        search: mobileSearchRef.current?.value || searchRef.current?.value || "",
        advancedFilters: nextAdvancedFilters,
      });
      if (showAdvancedFilters) onToggleAdvanced();
    };
    const clearRangeError = (key: FilterRangeKey) => {
      setRangeErrors((current) => {
        if (!current[key]) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });
    };
    const getRangeInputClassName = (key: FilterRangeKey) =>
      `${ui.input} pr-9 ${
        rangeErrors[key] ? "!border-[#b42318] !bg-[#fff7f6] focus:!border-[#b42318]" : ""
      }`;
    const getRangeErrorId = (key: FilterRangeKey) => `classifieds-filter-${key}-error`;
    const syncSearchInputs = (value: string, source: HTMLInputElement) => {
      if (searchRef.current && searchRef.current !== source) searchRef.current.value = value;
      if (mobileSearchRef.current && mobileSearchRef.current !== source) mobileSearchRef.current.value = value;
      if (value === "") onSearchClear();
    };
    const clearInput = (ref: React.RefObject<HTMLInputElement | null>) => {
      if (!ref.current) return;
      ref.current.value = "";
      ref.current.focus();
    };
    const formatUncontrolledPriceInput = (event: React.FormEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const rawValue = input.value;
      const rawCaretPosition = input.selectionStart ?? rawValue.length;
      const formattedValue = formatPriceInput(rawValue);
      const caretPosition = getPriceInputCaretPosition(
        rawValue,
        rawCaretPosition,
        formattedValue,
      );

      input.value = formattedValue;
      input.setSelectionRange(caretPosition, caretPosition);
    };
    const formatUncontrolledDecimalInput = (event: React.FormEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const rawCaretPosition = input.selectionStart ?? input.value.length;
      const caretPosition = formatDecimalInput(input.value.slice(0, rawCaretPosition)).length;

      input.value = formatDecimalInput(input.value);
      input.setSelectionRange(caretPosition, caretPosition);
    };
    const renderClearableInput = (
      ref: React.RefObject<HTMLInputElement | null>,
      props: Omit<React.InputHTMLAttributes<HTMLInputElement>, "ref">,
      options: { clearButtonClassName?: string; inputClassName?: string; onClear?: () => void } = {},
    ) => {
      const { className, ...restProps } = props;
      const inputClassName = options.inputClassName || `${className || ui.input} pr-9`;
      return (
        <div className="relative">
          <input {...restProps} ref={ref} className={inputClassName} />
          <button
            type="button"
            className={options.clearButtonClassName || ui.inputClearButton}
            aria-label="Очистить поле"
            onClick={() => {
              clearInput(ref);
              options.onClear?.();
            }}
          >
            ×
          </button>
        </div>
      );
    };
    const filtersGridClassName = filterSubcategoryOptions.length
      ? ui.filtersGridWithSubcategory
      : ui.filtersGridWithoutSubcategory;

    return (
      <div
        className={ui.filtersStickyShell}
        style={{ top: `${stickyTopOffset}px` }}
        data-classifieds-filter-shell="true"
      >
        <div className={ui.mobileFilters}>
          <div className={ui.mobileFiltersScroller} aria-label="Быстрые фильтры">
            <select
              className={ui.mobileFilterSelect}
              value={marketFilter === "rent" ? "rent" : "sale"}
              aria-label="Тип сделки"
              onChange={(event) => onMarketChange(event.target.value as MarketType)}
            >
              <option value="sale">Купить</option>
              <option value="rent">Снять</option>
            </select>
            <select
              className={ui.mobileFilterSelect}
              value={categoryFilter}
              aria-label={`Категория: ${selectedCategoryLabel}`}
              disabled={!filterCategoryOptions.length}
              onChange={(event) => onCategoryChange(event.target.value)}
            >
              <option value="all">Все категории</option>
              {filterCategoryOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {filterSubcategoryOptions.length ? (
              <select
                className={ui.mobileFilterSelect}
                value={subcategoryFilter}
                aria-label={`Подкатегория: ${selectedSubcategoryLabel}`}
                onChange={(event) => onSubcategoryChange(event.target.value)}
              >
                <option value="all">Все подкатегории</option>
                {filterSubcategoryOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            ) : null}
            <button
              type="button"
              className={`${ui.mobileFilterPill} ${ui.mobileFilterIconButton} ${
                mobileSearchVisible || search ? ui.mobileFilterPillActive : ""
              }`}
              aria-label="Поиск"
              aria-expanded={mobileSearchVisible}
              onClick={() => setMobileSearchVisible((visible) => !visible)}
            >
              <Search size={18} strokeWidth={2.3} />
            </button>
            <button
              type="button"
              className={`${ui.mobileFilterPill} ${ui.mobileFilterIconButton} ${
                showAdvancedFilters ? ui.mobileFilterPillActive : ""
              }`}
              aria-label="Фильтры"
              aria-expanded={showAdvancedFilters}
              onClick={onToggleAdvanced}
            >
              <SlidersHorizontal size={18} strokeWidth={2.2} />
              {activeFilterCount ? <span className={ui.mobileFilterCount}>{activeFilterCount}</span> : null}
            </button>
          </div>
          <div
            className={`${ui.mobileSearchRow} overflow-hidden transition-all duration-200 ease-out ${
              mobileSearchVisible ? "max-h-20 pb-3 opacity-100" : "max-h-0 pb-0 opacity-0"
            }`}
          >
            <input
              ref={mobileSearchRef}
              className={ui.mobileSearchInput}
              placeholder="Название, адрес, ключевые слова"
              defaultValue={search}
              aria-label="Поиск объявлений"
              onChange={(event) => syncSearchInputs(event.currentTarget.value, event.currentTarget)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                handleApply();
              }}
            />
            <button type="button" className={ui.mobileSearchSubmit} aria-label="Найти" onClick={handleApply}>
              <Search size={18} strokeWidth={2.4} />
            </button>
          </div>
        </div>

        <div className="hidden md:block">
          <div className={`${ui.filters} ${filtersGridClassName} ${ui.filtersMobileEdge}`}>
            <select
              className={ui.select}
              value={marketFilter === "rent" ? "rent" : "sale"}
              onChange={(e) => onMarketChange(e.target.value as MarketType)}
            >
              <option value="sale">купить</option>
              <option value="rent">снять</option>
            </select>
            <select
              ref={categoryRef}
              className={ui.select}
              defaultValue={categoryFilter}
              disabled={!filterCategoryOptions.length}
              onChange={(e) => onCategoryChange(e.target.value)}
            >
              <option value="all">Все категории</option>
              {filterCategoryOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {filterSubcategoryOptions.length ? (
              <select
                ref={subcategoryRef}
                className={ui.select}
                defaultValue={subcategoryFilter}
                onChange={(e) => onSubcategoryChange(e.target.value)}
              >
                <option value="all">Все подкатегории</option>
                {filterSubcategoryOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            ) : null}
            <div className="relative md:col-span-2 lg:col-span-1">
              {renderClearableInput(
                searchRef,
                {
                  className: ui.input,
                  placeholder: "Название, адрес, ключевые слова",
                  defaultValue: search,
                  onChange: (e) => syncSearchInputs(e.currentTarget.value, e.currentTarget),
                  onKeyDown: (e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    handleApply();
                  },
                },
                {
                  clearButtonClassName: ui.searchClearButton,
                  inputClassName: `${ui.input} pr-[76px]`,
                  onClear: () => {
                    if (mobileSearchRef.current) mobileSearchRef.current.value = "";
                    onSearchClear();
                  },
                },
              )}
              <button type="button" className={ui.searchSubmitButton} aria-label="Найти" onClick={handleApply}>
                <Search size={16} strokeWidth={2.4} />
              </button>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              text={showAdvancedFilters ? "Скрыть расширенные" : "Расширенные фильтры"}
              className={ui.advancedFiltersToggle}
              onClick={onToggleAdvanced}
            />
          </div>
        </div>

        {showAdvancedFilters ? (
          <div className={`${ui.advancedFiltersDropdown} grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-4`}>
            {filterCapabilities.priceSqm ? <>
            <label className={ui.formField}>
              <span className={ui.formLabel}>Цена за м² от</span>
              {renderClearableInput(priceSqmMinRef, {
                className: ui.input,
                type: "text",
                inputMode: "decimal",
                placeholder: "От",
                defaultValue: formatPriceInput(advancedFilters.priceSqmMin),
                "aria-invalid": Boolean(rangeErrors.priceSqm),
                "aria-describedby": rangeErrors.priceSqm ? getRangeErrorId("priceSqm") : undefined,
                onInput: (event) => {
                  formatUncontrolledPriceInput(event);
                  clearRangeError("priceSqm");
                },
              }, {
                inputClassName: getRangeInputClassName("priceSqm"),
                onClear: () => clearRangeError("priceSqm"),
              })}
            </label>
            <label className={ui.formField}>
              <span className={ui.formLabel}>Цена за м² до</span>
              {renderClearableInput(priceSqmMaxRef, {
                className: ui.input,
                type: "text",
                inputMode: "decimal",
                placeholder: "До",
                defaultValue: formatPriceInput(advancedFilters.priceSqmMax),
                "aria-invalid": Boolean(rangeErrors.priceSqm),
                "aria-describedby": rangeErrors.priceSqm ? getRangeErrorId("priceSqm") : undefined,
                onInput: (event) => {
                  formatUncontrolledPriceInput(event);
                  clearRangeError("priceSqm");
                },
              }, {
                inputClassName: getRangeInputClassName("priceSqm"),
                onClear: () => clearRangeError("priceSqm"),
              })}
              {rangeErrors.priceSqm ? (
                <span id={getRangeErrorId("priceSqm")} className={ui.errorText} role="alert">
                  {rangeErrors.priceSqm}
                </span>
              ) : null}
            </label>
            </> : null}
            {filterCapabilities.priceMonthly ? <>
            <label className={ui.formField}>
              <span className={ui.formLabel}>Цена/мес от</span>
              {renderClearableInput(priceMonthlyMinRef, {
                className: ui.input,
                type: "text",
                inputMode: "decimal",
                placeholder: "От",
                defaultValue: formatPriceInput(advancedFilters.priceMonthlyMin),
                "aria-invalid": Boolean(rangeErrors.priceMonthly),
                "aria-describedby": rangeErrors.priceMonthly ? getRangeErrorId("priceMonthly") : undefined,
                onInput: (event) => {
                  formatUncontrolledPriceInput(event);
                  clearRangeError("priceMonthly");
                },
              }, {
                inputClassName: getRangeInputClassName("priceMonthly"),
                onClear: () => clearRangeError("priceMonthly"),
              })}
            </label>
            <label className={ui.formField}>
              <span className={ui.formLabel}>Цена/мес до</span>
              {renderClearableInput(priceMonthlyMaxRef, {
                className: ui.input,
                type: "text",
                inputMode: "decimal",
                placeholder: "До",
                defaultValue: formatPriceInput(advancedFilters.priceMonthlyMax),
                "aria-invalid": Boolean(rangeErrors.priceMonthly),
                "aria-describedby": rangeErrors.priceMonthly ? getRangeErrorId("priceMonthly") : undefined,
                onInput: (event) => {
                  formatUncontrolledPriceInput(event);
                  clearRangeError("priceMonthly");
                },
              }, {
                inputClassName: getRangeInputClassName("priceMonthly"),
                onClear: () => clearRangeError("priceMonthly"),
              })}
              {rangeErrors.priceMonthly ? (
                <span id={getRangeErrorId("priceMonthly")} className={ui.errorText} role="alert">
                  {rangeErrors.priceMonthly}
                </span>
              ) : null}
            </label>
            </> : null}
            {filterCapabilities.area ? <>
            <label className={ui.formField}>
              <span className={ui.formLabel}>Площадь от, м²</span>
              {renderClearableInput(areaMinRef, {
                className: ui.input,
                type: "text",
                inputMode: "decimal",
                placeholder: "От",
                defaultValue: formatDecimalInput(advancedFilters.areaMin),
                "aria-invalid": Boolean(rangeErrors.area),
                "aria-describedby": rangeErrors.area ? getRangeErrorId("area") : undefined,
                onInput: (event) => {
                  formatUncontrolledDecimalInput(event);
                  clearRangeError("area");
                },
              }, {
                inputClassName: getRangeInputClassName("area"),
                onClear: () => clearRangeError("area"),
              })}
            </label>
            <label className={ui.formField}>
              <span className={ui.formLabel}>Площадь до, м²</span>
              {renderClearableInput(areaMaxRef, {
                className: ui.input,
                type: "text",
                inputMode: "decimal",
                placeholder: "До",
                defaultValue: formatDecimalInput(advancedFilters.areaMax),
                "aria-invalid": Boolean(rangeErrors.area),
                "aria-describedby": rangeErrors.area ? getRangeErrorId("area") : undefined,
                onInput: (event) => {
                  formatUncontrolledDecimalInput(event);
                  clearRangeError("area");
                },
              }, {
                inputClassName: getRangeInputClassName("area"),
                onClear: () => clearRangeError("area"),
              })}
              {rangeErrors.area ? (
                <span id={getRangeErrorId("area")} className={ui.errorText} role="alert">
                  {rangeErrors.area}
                </span>
              ) : null}
            </label>
            </> : null}
            {filterCapabilities.utilitiesPayer ? <label className={ui.formField}>
              <span className={ui.formLabel}>Коммунальные платит</span>
              <select
                ref={utilitiesPayerRef}
                className={ui.select}
                defaultValue={advancedFilters.utilitiesPayer}
                onChange={(e) =>
                  onAdvancedFiltersChange((p) => ({
                    ...p,
                    utilitiesPayer: e.target.value as UtilitiesPayer | "all",
                  }))
                }
              >
                <option value="all">Любой вариант</option>
                <option value="tenant">{UTILITIES_PAYER_LABELS.tenant}</option>
                <option value="landlord">{UTILITIES_PAYER_LABELS.landlord}</option>
              </select>
            </label> : null}
            {filterCapabilities.rentTerm ? (
              <label className={ui.formField}>
                <span className={ui.formLabel}>Срок аренды</span>
                <select
                  ref={rentTermRef}
                  className={ui.select}
                  defaultValue={advancedFilters.rentTerm}
                  onChange={(e) =>
                    onAdvancedFiltersChange((p) => ({
                      ...p,
                      rentTerm: e.target.value as RentTerm | "all",
                    }))
                  }
                >
                  <option value="all">Любой срок</option>
                  <option value="short_term">{RENT_TERM_LABELS.short_term}</option>
                  <option value="long_term">{RENT_TERM_LABELS.long_term}</option>
                </select>
              </label>
            ) : null}
            {filterCapabilities.agentCommission ? <>
            <label className={ui.formField}>
              <span className={ui.formLabel}>Комиссия агента</span>
              <select
                ref={agentCommissionUnitRef}
                className={ui.select}
                defaultValue={advancedFilters.agentCommissionUnit}
                onChange={(e) => {
                  const nextUnit = e.target.value as AdvancedFiltersState["agentCommissionUnit"];
                  [agentCommissionMinRef, agentCommissionMaxRef].forEach((ref) => {
                    if (!ref.current) return;
                    ref.current.value =
                      nextUnit === "amount"
                        ? formatPriceInput(ref.current.value)
                        : formatDecimalInput(normalizePriceInput(ref.current.value));
                  });
                  setAgentCommissionUnit(nextUnit);
                  clearRangeError("agentCommission");
                  onAdvancedFiltersChange((p) => ({
                    ...p,
                    agentCommissionUnit: nextUnit,
                  }));
                }}
              >
                <option value="percent">Проценты</option>
                <option value="amount">Рубли</option>
              </select>
            </label>
            <label className={ui.formField}>
              <span className={ui.formLabel}>Комиссия от, {agentCommissionUnit === "amount" ? "₽" : "%"}</span>
              {renderClearableInput(agentCommissionMinRef, {
                className: ui.input,
                type: "text",
                inputMode: "decimal",
                placeholder: "От",
                defaultValue:
                  agentCommissionUnit === "amount"
                    ? formatPriceInput(advancedFilters.agentCommissionMin)
                    : formatDecimalInput(advancedFilters.agentCommissionMin),
                "aria-invalid": Boolean(rangeErrors.agentCommission),
                "aria-describedby": rangeErrors.agentCommission
                  ? getRangeErrorId("agentCommission")
                  : undefined,
                onInput: (event) => {
                  if (agentCommissionUnit === "amount") formatUncontrolledPriceInput(event);
                  else formatUncontrolledDecimalInput(event);
                  clearRangeError("agentCommission");
                },
              }, {
                inputClassName: getRangeInputClassName("agentCommission"),
                onClear: () => clearRangeError("agentCommission"),
              })}
            </label>
            <label className={ui.formField}>
              <span className={ui.formLabel}>Комиссия до, {agentCommissionUnit === "amount" ? "₽" : "%"}</span>
              {renderClearableInput(agentCommissionMaxRef, {
                className: ui.input,
                type: "text",
                inputMode: "decimal",
                placeholder: "До",
                defaultValue:
                  agentCommissionUnit === "amount"
                    ? formatPriceInput(advancedFilters.agentCommissionMax)
                    : formatDecimalInput(advancedFilters.agentCommissionMax),
                "aria-invalid": Boolean(rangeErrors.agentCommission),
                "aria-describedby": rangeErrors.agentCommission
                  ? getRangeErrorId("agentCommission")
                  : undefined,
                onInput: (event) => {
                  if (agentCommissionUnit === "amount") formatUncontrolledPriceInput(event);
                  else formatUncontrolledDecimalInput(event);
                  clearRangeError("agentCommission");
                },
              }, {
                inputClassName: getRangeInputClassName("agentCommission"),
                onClear: () => clearRangeError("agentCommission"),
              })}
              {rangeErrors.agentCommission ? (
                <span id={getRangeErrorId("agentCommission")} className={ui.errorText} role="alert">
                  {rangeErrors.agentCommission}
                </span>
              ) : null}
            </label>
            </> : null}
            {filterCapabilities.commissionSort ? <>
            <label className={ui.formField}>
              <span className={ui.formLabel}>Сортировка</span>
              <select
                ref={commissionSortRef}
                className={ui.select}
                defaultValue={advancedFilters.commissionSort}
                onChange={(e) => {
                  const nextSort = e.target.value as AdvancedFiltersState["commissionSort"];
                  setCommissionSort(nextSort);
                  onAdvancedFiltersChange((p) => ({
                    ...p,
                    commissionSort: nextSort,
                    sortDirection: nextSort === "rent_term" ? "asc" : p.sortDirection,
                  }));
                  if (nextSort === "rent_term" && sortDirectionRef.current) {
                    sortDirectionRef.current.value = "asc";
                  }
                }}
              >
                <option value="default">По дате публикации</option>
                {filterCapabilities.rentTerm ? <option value="rent_term">По сроку аренды</option> : null}
                {filterCapabilities.agentCommission ? (
                  <option value="commission">По комиссии, {agentCommissionUnit === "amount" ? "₽" : "%"}</option>
                ) : null}
              </select>
            </label>
            <label className={ui.formField}>
              <span className={ui.formLabel}>Порядок сортировки</span>
              <select
                ref={sortDirectionRef}
                className={ui.select}
                defaultValue={advancedFilters.sortDirection}
                disabled={commissionSort === "default"}
                onChange={(e) =>
                  onAdvancedFiltersChange((p) => ({
                    ...p,
                    sortDirection: e.target.value as AdvancedFiltersState["sortDirection"],
                  }))
                }
              >
                <option value="desc">По убыванию</option>
                <option value="asc">По возрастанию</option>
              </select>
            </label>
            </> : null}
            {filterCapabilities.hasImage ? <label className={ui.checkbox}>
              <input
                ref={hasImageRef}
                className={ui.checkboxInput}
                type="checkbox"
                defaultChecked={advancedFilters.hasImage}
                onChange={(e) =>
                  onAdvancedFiltersChange((p) => ({
                    ...p,
                    hasImage: e.target.checked,
                  }))
                }
              />
              Только с фото
            </label> : null}
            <div className={ui.advancedFiltersActions}>
              <button type="button" className={`${ui.btnBase} ${ui.btnPrimary}`} onClick={handleApply}>
                Применить
              </button>
              <button type="button" className={`${ui.btnBase} ${ui.btnGhost}`} onClick={onReset}>
                Сбросить фильтры
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  },
  (prev, next) =>
    prev.marketFilter === next.marketFilter &&
    prev.search === next.search &&
    prev.categoryFilter === next.categoryFilter &&
    prev.subcategoryFilter === next.subcategoryFilter &&
    prev.stickyTopOffset === next.stickyTopOffset &&
    prev.showAdvancedFilters === next.showAdvancedFilters &&
    areAdvancedFiltersEqual(prev.advancedFilters, next.advancedFilters) &&
    prev.filterCapabilities === next.filterCapabilities &&
    prev.filterCategoryOptions === next.filterCategoryOptions &&
    prev.filterSubcategoryOptions === next.filterSubcategoryOptions &&
    prev.syncRevision === next.syncRevision &&
    prev.onApply === next.onApply &&
    prev.onSearchClear === next.onSearchClear &&
    prev.onReset === next.onReset &&
    prev.onMarketChange === next.onMarketChange &&
    prev.onCategoryChange === next.onCategoryChange &&
    prev.onSubcategoryChange === next.onSubcategoryChange &&
    prev.onToggleAdvanced === next.onToggleAdvanced &&
    prev.onAdvancedFiltersChange === next.onAdvancedFiltersChange,
);

type ClassifiedsMapViewProps = {
  items: ClassifiedsMapItem[];
  selectedItemKey: string | null;
  pending: boolean;
  error: string | null;
  onViewportChange: (viewport: MapViewportState) => void;
  onSelect: (item: Listing) => void;
  onRetry: () => void;
  getItemKey: (item: Listing) => string;
};

const ClassifiedsMapView: React.FC<ClassifiedsMapViewProps> = ({
  items,
  selectedItemKey,
  pending,
  error,
  onViewportChange,
  onSelect,
  onRetry,
  getItemKey,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<YandexMapInstance | null>(null);
  const ymapsRef = useRef<Awaited<ReturnType<typeof loadYandexMaps>> | null>(null);
  const boundsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [mapStatus, setMapStatus] = useState<"loading" | "ready" | "error">("loading");
  const [streetQuery, setStreetQuery] = useState("");
  const [streetSearchStatus, setStreetSearchStatus] = useState<
    "idle" | "searching" | "not_found" | "geocoder_unavailable"
  >("idle");

  const emitViewport = useCallback(() => {
    const bounds = mapRef.current?.getBounds?.();
    if (!bounds) return;
    const latitudes = [Number(bounds[0][0]), Number(bounds[1][0])].filter(Number.isFinite);
    const longitudes = [Number(bounds[0][1]), Number(bounds[1][1])].filter(Number.isFinite);
    if (latitudes.length !== 2 || longitudes.length !== 2) return;

    onViewportChange({
      latMin: Math.min(...latitudes),
      latMax: Math.max(...latitudes),
      lngMin: Math.min(...longitudes),
      lngMax: Math.max(...longitudes),
      zoom: Number(mapRef.current?.getZoom?.()) || MAP_DEFAULT_ZOOM,
    });
  }, [onViewportChange]);

  const scheduleViewportEmit = useCallback(() => {
    if (boundsTimerRef.current) window.clearTimeout(boundsTimerRef.current);
    boundsTimerRef.current = window.setTimeout(emitViewport, 250);
  }, [emitViewport]);

  useEffect(() => {
    let cancelled = false;
    setMapStatus("loading");

    loadYandexMaps()
      .then((ymaps) => {
        if (cancelled || !containerRef.current) return;
        ymapsRef.current = ymaps;
        const map = new ymaps.Map(
          containerRef.current,
          {
            center: MAP_DEFAULT_COORDINATES,
            zoom: MAP_DEFAULT_ZOOM,
            controls: ["zoomControl", "fullscreenControl", "geolocationControl"],
          },
          {
            suppressMapOpenBlock: true,
            yandexMapDisablePoiInteractivity: true,
          },
        );
        map.events.add("boundschange", scheduleViewportEmit);
        mapRef.current = map;
        setMapStatus("ready");
        window.setTimeout(emitViewport, 300);
      })
      .catch(() => {
        if (!cancelled) setMapStatus("error");
      });

    return () => {
      cancelled = true;
      if (boundsTimerRef.current) window.clearTimeout(boundsTimerRef.current);
      mapRef.current?.destroy();
      mapRef.current = null;
      ymapsRef.current = null;
    };
  }, [emitViewport, scheduleViewportEmit]);

  useEffect(() => {
    if (mapStatus !== "ready" || !mapRef.current || !ymapsRef.current) return;
    mapRef.current.geoObjects.removeAll?.();
    const ymaps = ymapsRef.current;

    items.forEach((item) => {
      const latitude = parseCoordinate(item.latitude);
      const longitude = parseCoordinate(item.longitude);
      if (latitude === null || longitude === null) return;

      const isCluster = isMapCluster(item);
      const isSelected = !isCluster && selectedItemKey === getItemKey(item);
      const placemark = new ymaps.Placemark(
        [latitude, longitude],
        isCluster
          ? { hintContent: `${item.count} объявлений`, iconCaption: String(item.count) }
          : { hintContent: item.title },
        {
          preset: isCluster
            ? "islands#violetCircleIcon"
            : isSelected
              ? "islands#redHomeIcon"
              : "islands#violetHomeIcon",
        },
      );
      placemark.events.add("click", () => {
        if (isCluster) {
          const clusterBounds = normalizeMapClusterBounds(item.bounds);
          if (clusterBounds) {
            mapRef.current?.setBounds?.(
              [
                [clusterBounds.latMin, clusterBounds.lngMin],
                [clusterBounds.latMax, clusterBounds.lngMax],
              ],
              { checkZoomRange: true, duration: 250 },
            );
          } else {
            const nextZoom = Math.min((mapRef.current?.getZoom?.() || MAP_DEFAULT_ZOOM) + 2, 18);
            mapRef.current?.setCenter([latitude, longitude], nextZoom, { duration: 250 });
          }
          window.setTimeout(emitViewport, 450);
          return;
        }
        onSelect(item);
      });
      mapRef.current?.geoObjects.add(placemark);
    });
  }, [emitViewport, getItemKey, items, mapStatus, onSelect, selectedItemKey]);

  const handleStreetSearch = async () => {
    const query = streetQuery.trim();
    if (!query) return;

    try {
      setStreetSearchStatus("searching");
      const params = new URLSearchParams({ q: buildMapStreetSearchQuery(query) });
      const response = await apiClient.get(`${API_ENDPOINTS.classifiedsGeocode}?${params.toString()}`);
      const geocodeResult = normalizeGeocodeResponse(response?.data);
      if (!geocodeResult) {
        setStreetSearchStatus("not_found");
        return;
      }
      if (geocodeResult.bounds) {
        mapRef.current?.setBounds?.(
          [
            [geocodeResult.bounds.latMin, geocodeResult.bounds.lngMin],
            [geocodeResult.bounds.latMax, geocodeResult.bounds.lngMax],
          ],
          { checkZoomRange: true, duration: 250 },
        );
      } else {
        mapRef.current?.setCenter(geocodeResult.coordinates, 15, { duration: 250 });
      }
      window.setTimeout(emitViewport, 450);
      setStreetSearchStatus("idle");
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setStreetSearchStatus("not_found");
        return;
      }
      setStreetSearchStatus("geocoder_unavailable");
    }
  };

  return (
    <div className={ui.mapPanel}>
      <div ref={containerRef} className={ui.mapCanvas} />
      <div className={ui.mapSearch}>
        <div className="relative">
          <input
            className={`${ui.input} pr-[76px]`}
            value={streetQuery}
            placeholder="Найти улицу на карте"
            onChange={(event) => setStreetQuery(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              void handleStreetSearch();
            }}
          />
          {streetQuery ? (
            <button
              type="button"
              className={ui.searchClearButton}
              aria-label="Очистить поиск улицы"
              onClick={() => setStreetQuery("")}
            >
              ×
            </button>
          ) : null}
          <button type="button" className={ui.searchSubmitButton} aria-label="Найти улицу" onClick={handleStreetSearch}>
            <Search size={16} strokeWidth={2.4} />
          </button>
        </div>
        {streetSearchStatus === "searching" ? <div className={ui.helperText}>Ищем улицу...</div> : null}
        {streetSearchStatus === "not_found" ? <div className={ui.helperText}>Улица не найдена.</div> : null}
        {streetSearchStatus === "geocoder_unavailable" ? (
          <div className={ui.helperText}>Не удалось выполнить поиск улицы.</div>
        ) : null}
      </div>
      {mapStatus === "loading" ? (
        <div className="absolute inset-0 grid place-items-center bg-[#f7f7f7] text-sm text-[#777]">
          Загружаем карту...
        </div>
      ) : null}
      {mapStatus === "error" ? (
        <div className="absolute inset-0 grid place-items-center bg-[#f7f7f7] px-5 text-center text-sm leading-6 text-[#777]">
          Карту не удалось загрузить.
        </div>
      ) : null}
      {pending ? (
        <div className="absolute bottom-3 left-3 rounded-full bg-white px-3 py-2 text-[12px] font-medium text-[#6b6b6b] shadow-[0_8px_22px_rgba(0,0,0,0.12)]">
          Обновляем объявления...
        </div>
      ) : null}
      {error ? (
        <button
          type="button"
          className="absolute bottom-3 left-3 cursor-pointer rounded-full border border-[#efc5d8] bg-white px-3 py-2 text-[12px] font-semibold text-[#8b2a5e] shadow-[0_8px_22px_rgba(0,0,0,0.12)]"
          onClick={onRetry}
        >
          Повторить загрузку
        </button>
      ) : null}
    </div>
  );
};

const resolveOwnerId = (user: UserInfo, userData: UserDataShape): number => {
  const candidates: Array<unknown> = [
    user?.id,
    user?.user_id,
    user?.userId,
    user?.data?.id,
    user?.data?.user_id,
    user?.data?.userId,
    user?.profile?.id,
    user?.user?.id,
    userData?.id,
    userData?.user_id,
    userData?.userId,
    userData?.data?.id,
    userData?.data?.user_id,
    userData?.data?.userId,
    userData?.profile?.id,
    userData?.user?.id,
    userData?.user?.user_id,
    userData?.user?.userId,
    userData?.user?.data?.id,
  ];
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return NaN;
};

const getCookieValue = (name: string): string => {
  const rawValue = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

  return rawValue ? decodeURIComponent(rawValue) : "";
};

const normalizeAuthToken = (value: unknown): string => {
  const token = String(value || "").trim();
  return token && token !== "undefined" && token !== "null" ? token : "";
};

const hasUserAbility = (ability: string, ...nodes: UserNode[]): boolean => {
  const stack = nodes.filter(Boolean) as NonNullable<UserNode>[];
  const visited = new Set<NonNullable<UserNode>>();

  while (stack.length) {
    const node = stack.pop();
    if (!node || visited.has(node)) continue;
    visited.add(node);

    if (Array.isArray(node.abilities) && node.abilities.includes(ability)) {
      return true;
    }

    if (node.data) stack.push(node.data);
    if (node.profile) stack.push(node.profile);
    if (node.user) stack.push(node.user);
  }

  return false;
};

const _ClassifiedsBoard: React.FC<Props> = ({
  user,
  userData,
  userToken,
  createMode = false,
  editMode = false,
  myMode = false,
  favoriteMode = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type: editTypeParam, id: editId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const normalizedEditType = editMode ? normalizeListingType(editTypeParam) : null;
  const formRouteMode = createMode || editMode;
  const requestedMarket = searchParams.get("market");
  const initialMarket: MarketFilter =
    requestedMarket === "rent" || requestedMarket === "sale" ? requestedMarket : myMode ? "my" : "sale";
  const queryAdvancedFilters = readAdvancedFiltersFromQuery(searchParams);
  const queryHasAdvancedFilters = hasActiveAdvancedFilters(queryAdvancedFilters);
  const ownerId = resolveOwnerId(user, userData);
  const authToken = normalizeAuthToken(userToken || userData?.token || getCookieValue("userToken"));
  const isAuthorized = Number.isFinite(ownerId) && ownerId > 0 && Boolean(authToken);
  const canManageListings = hasUserAbility("manager", userData, user);
  const [creationPermission, setCreationPermission] = useState<ListingCreationPermission | null>(null);
  const [creationPermissionPending, setCreationPermissionPending] = useState(false);
  const [creationPermissionError, setCreationPermissionError] = useState<string | null>(null);
  const [items, setItems] = useState<Listing[]>([]);
  const [pending, setPending] = useState(false);
  const [editPending, setEditPending] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [loadMorePending, setLoadMorePending] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<ListingPagination>({
    limit: LISTINGS_PAGE_LIMIT,
    has_more: false,
    next_cursor: null,
    total: null,
  });
  const [myListingsCounts, setMyListingsCounts] = useState<MyListingsCounts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(formRouteMode);
  const [createStep, setCreateStep] = useState<CreateStep>(1);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [draftApiAvailable, setDraftApiAvailable] = useState(true);
  const [saveDraftPending, setSaveDraftPending] = useState(false);
  const [draggedUploadId, setDraggedUploadId] = useState<string | null>(null);
  const [dragOverUploadId, setDragOverUploadId] = useState<string | null>(null);
  const [dragOverPosition, setDragOverPosition] = useState<"before" | "after">("before");
  const [stickyTopOffset, setStickyTopOffset] = useState(8);
  const [mobileDockHeight, setMobileDockHeight] = useState(0);
  const stepperDockRef = useRef<HTMLDivElement | null>(null);
  const listingsTopRef = useRef<HTMLDivElement | null>(null);
  const pendingValidationFocusIdRef = useRef<string | null>(null);
  const trackedMarketSurfaceRef = useRef("");
  const stickyTopOffsetRef = useRef(8);
  const previewUrlsRef = useRef<string[]>([]);
  const draftSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftHydratedRef = useRef(false);
  const draftApplyingRef = useRef(false);
  const skipLocalDraftSyncRef = useRef(false);
  const lastLocalDraftSnapshotRef = useRef<string>("");
  const [createPending, setCreatePending] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [taxonomy, setTaxonomy] = useState<TaxonomyMarket[]>([]);
  const [taxonomyPending, setTaxonomyPending] = useState(false);
  const [taxonomyError, setTaxonomyError] = useState<string | null>(null);
  const [listingConstraints, setListingConstraints] = useState(FALLBACK_LISTING_CONSTRAINTS);
  const [favoriteKeys, setFavoriteKeys] = useState<Set<string>>(new Set());
  const [favoriteKeysHydrated, setFavoriteKeysHydrated] = useState(false);
  const [favoriteViewKeys, setFavoriteViewKeys] = useState<Set<string> | null>(null);
  const [guestFavoriteHintKey, setGuestFavoriteHintKey] = useState<string | null>(null);
  const [listingStatusPendingKeys, setListingStatusPendingKeys] = useState<Set<string>>(new Set());
  const [listingStatusErrors, setListingStatusErrors] = useState<Record<string, string>>({});
  const [listingDeletePendingKeys, setListingDeletePendingKeys] = useState<Set<string>>(new Set());
  const [listingDeleteErrors, setListingDeleteErrors] = useState<Record<string, string>>({});
  const [selectedListingKeys, setSelectedListingKeys] = useState<Set<string>>(new Set());
  const [bulkDeletePending, setBulkDeletePending] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null);
  const guestFavoriteHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listingsRequestIdRef = useRef(0);
  const scrollRestorationReturnToRef = useRef(`${location.pathname}${location.search}`);
  const scrollRestorationTargetCountRef = useRef(
    getClassifiedsScrollPosition(scrollRestorationReturnToRef.current)?.loadedCount || 0,
  );
  const scrollRestorationCursorRef = useRef<string | null>(null);
  const scrollRestorationCompletedRef = useRef(false);
  const myListingsAutoLoadCursorRef = useRef<string | null>(null);
  const initialListingsLoadedRef = useRef(false);
  const previousInitialMarketRef = useRef(initialMarket);
  const firstFilterUrlSyncRef = useRef(true);

  const [search, setSearch] = useState(getQueryValue(searchParams, "q"));
  const [viewMode, setViewMode] = useState<ClassifiedsViewMode>(searchParams.get("view") === "map" ? "map" : "list");
  const [mapItems, setMapItems] = useState<ClassifiedsMapItem[]>([]);
  const [mapPending, setMapPending] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapViewport, setMapViewport] = useState<MapViewportState | null>(null);
  const [selectedMapItemKey, setSelectedMapItemKey] = useState<string | null>(null);
  const [marketFilter, setMarketFilter] = useState<MarketFilter>(initialMarket);
  const initialMyListingsTab = getMyListingsTabFromQuery(searchParams);
  const [myListingsTab, setMyListingsTab] = useState<MyListingsTab>(initialMyListingsTab);
  const [myListingsAutoLoadTab, setMyListingsAutoLoadTab] = useState<MyListingsTab | null>(
    myMode &&
      initialMyListingsTab !== "published" &&
      scrollRestorationTargetCountRef.current === 0
      ? initialMyListingsTab
      : null,
  );
  const [categoryFilter, setCategoryFilter] = useState(getQueryValue(searchParams, "category", "board_category") || "all");
  const [subcategoryFilter, setSubcategoryFilter] = useState(
    getQueryValue(searchParams, "subcategory", "board_subcategory") || "all",
  );
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(
    TRUTHY_QUERY_VALUES.has(String(searchParams.get("advanced") || "").toLowerCase()) || queryHasAdvancedFilters,
  );
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFiltersState>(queryAdvancedFilters);
  const [filterControlsSyncRevision, setFilterControlsSyncRevision] = useState(0);
  const selectedCommercialRentCategory = marketFilter === "rent" && categoryFilter === "commercial";
  const selectedFilterListingType = useMemo<ListingType | null>(() => {
    if (marketFilter === "my" || categoryFilter === "all") return null;

    const category = taxonomy
      .find((market) => market.market_type === marketFilter)
      ?.categories.find((item) => item.value === categoryFilter);
    const subcategory = category?.subcategories.find((item) => item.value === subcategoryFilter);

    return subcategory?.listing_type || category?.listing_type || null;
  }, [categoryFilter, marketFilter, subcategoryFilter, taxonomy]);
  const filterCapabilities = useMemo(
    () => getClassifiedsFilterCapabilities(marketFilter === "rent" ? "rent" : "sale", selectedFilterListingType),
    [marketFilter, selectedFilterListingType],
  );
  const scopedAdvancedFilters = useMemo(
    () => sanitizeAdvancedFilters(advancedFilters, filterCapabilities),
    [advancedFilters, filterCapabilities],
  );

  useEffect(() => {
    if (areAdvancedFiltersEqual(advancedFilters, scopedAdvancedFilters)) return;
    setAdvancedFilters(scopedAdvancedFilters);
  }, [advancedFilters, scopedAdvancedFilters]);

  // `my` is an internal UI mode, never a valid value for the public listings API.
  // During a route transition prefer the personal endpoint over leaking it as
  // `market_type=my` to `/v1/listings`.
  const isMyListingsRequest = myMode || marketFilter === "my";
  const listFiltersEnabled = !isMyListingsRequest && !favoriteMode;
  const canCreateListings = Boolean(creationPermission?.can_create_listings || canManageListings);
  const createAccessDenied = createMode && !creationPermissionPending && !canCreateListings;
  const handleViewModeChange = useCallback((tabId: string) => {
    if (tabId === "list" || tabId === "map") {
      setViewMode(tabId);
    }
  }, []);

  const loadCreationPermission = useCallback(async () => {
    if (!authToken) {
      setCreationPermission({
        can_create_listings: false,
        is_listing_creation_whitelisted: false,
        create_listings_allowed_email: null,
      });
      setCreationPermissionPending(false);
      setCreationPermissionError(null);
      return;
    }

    setCreationPermissionPending(true);
    setCreationPermissionError(null);
    try {
      const response = await apiClient.get(API_ENDPOINTS.classifiedsCreatePermission);
      const permission = response?.data?.data;
      setCreationPermission({
        can_create_listings: Boolean(permission?.can_create_listings),
        is_listing_creation_whitelisted: Boolean(permission?.is_listing_creation_whitelisted),
        create_listings_allowed_email: permission?.create_listings_allowed_email || null,
      });
    } catch (requestError: any) {
      setCreationPermission({
        can_create_listings: canManageListings,
        is_listing_creation_whitelisted: false,
        create_listings_allowed_email: null,
      });
      setCreationPermissionError(requestError?.response?.data?.error || "Не удалось проверить доступ к созданию");
    } finally {
      setCreationPermissionPending(false);
    }
  }, [authToken, canManageListings]);

  useEffect(() => {
    void loadCreationPermission();
  }, [loadCreationPermission]);

  useEffect(() => {
    if (!formRouteMode) return;

    setListingConstraints(FALLBACK_LISTING_CONSTRAINTS);
    const controller = new AbortController();
    let active = true;

    publicApiClient
      .get(API_ENDPOINTS.classifiedsListingConstraints, { signal: controller.signal })
      .then((response) => {
        if (active) {
          setListingConstraints(normalizeListingConstraints(response?.data?.data));
        }
      })
      .catch(() => {
        // The form is usable immediately with the OpenAPI fallback.
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [formRouteMode]);

  const [createMarketType, setCreateMarketType] = useState<MarketType>("sale");
  const [createCategory, setCreateCategory] = useState("houses");
  const [createSubcategory, setCreateSubcategory] = useState("");
  const [assignmentHouseCompleted, setAssignmentHouseCompleted] = useState(false);

  const [baseForm, setBaseForm] = useState<Record<string, string>>(BASE_BY_TYPE.house);
  const [formValidationAttempted, setFormValidationAttempted] = useState(false);
  const [validationFocusRevision, setValidationFocusRevision] = useState(0);
  const [features, setFeatures] = useState({
    is_renovated: false,
    is_penthouse: false,
    is_apartment_unit: false,
    is_view: false,
    house_kind: "house",
    is_completed: false,
    has_pool: false,
    has_bathhouse: false,
  });
  const [communications, setCommunications] = useState({
    has_sewerage: false,
    has_water: false,
    has_electricity: false,
    has_gas: false,
  });
  const [address, setAddress] = useState<AddressState>({
    address_region: "",
    address_locality: "",
    address_street: "",
    address_house: "",
  });
  const [finance, setFinance] = useState({
    price_total: "",
    price_monthly: "",
    utility_included: false,
    utilities_payer: "tenant" as UtilitiesPayer,
    rent_term: "short_term" as RentTerm,
    agent_commission_percent: "",
    agent_commission_amount: "",
  });
  const [imageUploads, setImageUploads] = useState<UploadItem[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [assignment, setAssignment] = useState({
    currency_id: "1",
    initial_payment: "",
    payment: "",
    full_payment_purchase: "",
    full_payment_price: "",
    agent_markup: "",
    recalculation_percentage: "",
    recalculation_period: "",
    initial_payment_at: "",
    assignment_months_left: "",
    assignment_paid_amount: "",
    assignment_paid_months: "",
  });
  const [assignmentPaymentType, setAssignmentPaymentType] = useState<AssignmentPaymentType>("installment");

  // This effect intentionally reacts to URL changes only: filter state writes update
  // the URL in the next effect, and then this reader hydrates state from that URL.
  useEffect(() => {
    if (formRouteMode) return;

    const nextMarket: MarketFilter =
      requestedMarket === "rent" || requestedMarket === "sale" ? requestedMarket : myMode ? "my" : "sale";
    const nextMyListingsTab = getMyListingsTabFromQuery(searchParams);
    const nextSearch = getQueryValue(searchParams, "q");
    const nextViewMode: ClassifiedsViewMode = searchParams.get("view") === "map" ? "map" : "list";
    const nextCategory = getQueryValue(searchParams, "category", "board_category") || "all";
    const nextSubcategory = getQueryValue(searchParams, "subcategory", "board_subcategory") || "all";
    const nextAdvancedFilters = readAdvancedFiltersFromQuery(searchParams);
    const nextShowAdvancedFilters =
      TRUTHY_QUERY_VALUES.has(String(searchParams.get("advanced") || "").toLowerCase()) ||
      (firstFilterUrlSyncRef.current && hasActiveAdvancedFilters(nextAdvancedFilters));

    let shouldSyncFilterControls = false;
    if (nextMarket !== marketFilter) setMarketFilter(nextMarket);
    if (myMode && nextMyListingsTab !== myListingsTab) {
      setMyListingsTab(nextMyListingsTab);
      setMyListingsAutoLoadTab(nextMyListingsTab);
      setLoadMoreError(null);
      myListingsAutoLoadCursorRef.current = null;
    }
    if (nextSearch !== search) {
      setSearch(nextSearch);
      shouldSyncFilterControls = true;
    }
    if (nextViewMode !== viewMode) setViewMode(nextViewMode);
    if (nextCategory !== categoryFilter) {
      setCategoryFilter(nextCategory);
      shouldSyncFilterControls = true;
    }
    if (nextSubcategory !== subcategoryFilter) {
      setSubcategoryFilter(nextSubcategory);
      shouldSyncFilterControls = true;
    }
    if (nextShowAdvancedFilters !== showAdvancedFilters) setShowAdvancedFilters(nextShowAdvancedFilters);
    if (!areAdvancedFiltersEqual(nextAdvancedFilters, advancedFilters)) {
      setAdvancedFilters(nextAdvancedFilters);
      shouldSyncFilterControls = true;
    }
    if (shouldSyncFilterControls) {
      setFilterControlsSyncRevision((current) => current + 1);
    }
    firstFilterUrlSyncRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRouteMode, location.search, myMode]);

  useEffect(() => {
    if (!selectedCommercialRentCategory) return;
    if (advancedFilters.rentTerm === "all" && advancedFilters.commissionSort !== "rent_term") return;

    setAdvancedFilters((current) => ({
      ...current,
      rentTerm: "all",
      commissionSort: current.commissionSort === "rent_term" ? "default" : current.commissionSort,
      sortDirection: current.commissionSort === "rent_term" ? "desc" : current.sortDirection,
    }));
  }, [advancedFilters.commissionSort, advancedFilters.rentTerm, selectedCommercialRentCategory]);

  useEffect(() => {
    if (formRouteMode) return;

    const advancedFilters = scopedAdvancedFilters;
    const nextParams = new URLSearchParams();
    if (myMode && myListingsTab !== "published") {
      nextParams.set("tab", myListingsTab);
    }
    if (listFiltersEnabled && (marketFilter === "sale" || marketFilter === "rent")) {
      nextParams.set("market", marketFilter);
    }
    if (listFiltersEnabled) {
      if (categoryFilter !== "all") nextParams.set("category", categoryFilter);
      if (subcategoryFilter !== "all") nextParams.set("subcategory", subcategoryFilter);
      if (viewMode === "map") nextParams.set("view", "map");

      const normalizedSearch = search.trim();
      if (normalizedSearch) nextParams.set("q", normalizedSearch);

      if (showAdvancedFilters) nextParams.set("advanced", "1");
      if (advancedFilters.priceSqmMin) nextParams.set("price_sqm_min", advancedFilters.priceSqmMin);
      if (advancedFilters.priceSqmMax) nextParams.set("price_sqm_max", advancedFilters.priceSqmMax);
      if (advancedFilters.priceMonthlyMin) nextParams.set("price_monthly_min", advancedFilters.priceMonthlyMin);
      if (advancedFilters.priceMonthlyMax) nextParams.set("price_monthly_max", advancedFilters.priceMonthlyMax);
      if (advancedFilters.areaMin) nextParams.set("area_min", advancedFilters.areaMin);
      if (advancedFilters.areaMax) nextParams.set("area_max", advancedFilters.areaMax);
      if (advancedFilters.utilitiesPayer !== "all") {
        nextParams.set("utilities_payer", advancedFilters.utilitiesPayer);
      }
      if (!selectedCommercialRentCategory && advancedFilters.rentTerm !== "all") {
        nextParams.set("rent_term", advancedFilters.rentTerm);
      }
      const commissionPrefix =
        advancedFilters.agentCommissionUnit === "amount"
          ? "agent_commission_amount"
          : "agent_commission_percent";
      if (advancedFilters.agentCommissionUnit !== "percent") {
        nextParams.set("agent_commission_unit", advancedFilters.agentCommissionUnit);
      }
      if (advancedFilters.agentCommissionMin) {
        nextParams.set(`${commissionPrefix}_min`, advancedFilters.agentCommissionMin);
      }
      if (advancedFilters.agentCommissionMax) {
        nextParams.set(`${commissionPrefix}_max`, advancedFilters.agentCommissionMax);
      }
      if (!selectedCommercialRentCategory && advancedFilters.commissionSort === "rent_term") {
        nextParams.set("sort", "rent_term");
        nextParams.set("sort_direction", advancedFilters.sortDirection);
      } else if (advancedFilters.commissionSort !== "default") {
        nextParams.set("sort", commissionPrefix);
        nextParams.set("sort_direction", advancedFilters.sortDirection);
      }
      if (advancedFilters.hasImage) nextParams.set("has_image", "1");
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    advancedFilters,
    categoryFilter,
    favoriteMode,
    formRouteMode,
    listFiltersEnabled,
    marketFilter,
    myListingsTab,
    myMode,
    searchParams,
    search,
    selectedCommercialRentCategory,
    setSearchParams,
    showAdvancedFilters,
    scopedAdvancedFilters,
    subcategoryFilter,
    viewMode,
  ]);
  const isAssignmentCategory = createCategory === "assignments";
  const createSteps = useMemo(
    () => (isAssignmentCategory ? CREATE_STEPS : CREATE_STEPS.filter((step) => step.id !== 5)),
    [isAssignmentCategory],
  );
  const currentStepIndex = createSteps.findIndex((step) => step.id === createStep);
  const isLastStep = currentStepIndex === createSteps.length - 1;
  const localDraftStorageKey = useMemo(
    () => `classifieds-create-draft-v3:${Number.isFinite(ownerId) ? ownerId : "guest"}`,
    [ownerId],
  );
  const favoriteStorageKey = useMemo(
    () => `classifieds-favorites-v1:${Number.isFinite(ownerId) ? ownerId : "guest"}`,
    [ownerId],
  );

  const uploadInProgress = imageUploads.some((file) => file.status === "queued" || file.status === "uploading");
  const uploadHasErrors = imageUploads.some((file) => file.status === "error");

  useEffect(() => {
    setCreateError(null);
  }, [
    address,
    assignment,
    baseForm,
    communications,
    createCategory,
    createMarketType,
    createStep,
    createSubcategory,
    assignmentHouseCompleted,
    features,
    finance,
    uploadHasErrors,
    uploadInProgress,
  ]);

  const hasDraftContent = useMemo(() => {
    const hasBase =
      String(baseForm.title || "").trim().length > 0 || String(baseForm.description || "").trim().length > 0;
    const hasAddress = Object.values(address).some((value) => String(value || "").trim().length > 0);
    const hasFinance =
      String(finance.price_monthly || "").trim().length > 0 ||
      String(finance.price_total || "").trim().length > 0 ||
      finance.utility_included ||
      finance.utilities_payer !== "tenant" ||
      finance.rent_term !== "short_term" ||
      String(finance.agent_commission_percent || "").trim().length > 0 ||
      String(finance.agent_commission_amount || "").trim().length > 0;
    const hasChangedStructure =
      createMarketType !== "sale" || createCategory !== "houses" || Boolean(createSubcategory);
    return (
      hasChangedStructure ||
      hasBase ||
      hasAddress ||
      hasFinance ||
      imageUploads.length > 0 ||
      isAssignmentCategory ||
      Object.values(communications).some(Boolean) ||
      Object.values(features).some((value) => typeof value === "boolean" && value)
    );
  }, [
    address,
    baseForm.description,
    baseForm.title,
    communications,
    createCategory,
    createMarketType,
    createSubcategory,
    features,
    finance,
    imageUploads.length,
    isAssignmentCategory,
  ]);

  const loadTaxonomy = useCallback(async () => {
    setTaxonomyPending(true);
    setTaxonomyError(null);
    try {
      const response = await apiClient.get(API_ENDPOINTS.classifiedsTaxonomy);
      const nextTaxonomy = normalizeTaxonomy(response?.data?.data);
      setTaxonomy(nextTaxonomy);
      if (!nextTaxonomy.length) {
        setTaxonomyError("Бэк вернул пустой справочник категорий");
      }
    } catch (e: any) {
      setTaxonomyError(e?.response?.data?.error || "Не удалось загрузить справочник категорий");
    } finally {
      setTaxonomyPending(false);
    }
  }, []);

  useEffect(() => {
    loadTaxonomy();
  }, [loadTaxonomy]);

  useEffect(() => {
    setShowCreateForm(formRouteMode);
  }, [formRouteMode]);

  useEffect(() => {
    const surface = editMode
      ? "edit"
      : createMode
        ? "create"
        : favoriteMode
          ? "favorites"
          : myMode
            ? "my_listings"
            : "catalog";
    const trackingKey = `${surface}:${editTypeParam || ""}:${editId || ""}`;
    if (trackedMarketSurfaceRef.current === trackingKey) {
      return;
    }

    trackedMarketSurfaceRef.current = trackingKey;
    if (formRouteMode) {
      if (createMode) {
        trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_LISTING_CREATE_STARTED, {
          market_form_mode: "create",
        });
      }
    } else {
      trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_OPENED, {
        market_surface: surface,
        market_filter: initialMarket,
      });
    }
  }, [
    createMode,
    editId,
    editMode,
    editTypeParam,
    favoriteMode,
    formRouteMode,
    initialMarket,
    myMode,
    normalizedEditType,
  ]);

  useEffect(() => {
    if (showCreateForm) {
      setCreateStep(1);
    }
  }, [showCreateForm]);

  const applyDraftPayload = useCallback((payload: Partial<DraftPayload>) => {
    if (payload.createStep && payload.createStep >= 1 && payload.createStep <= 5) {
      setCreateStep(payload.createStep);
    }
    if (payload.createMarketType) setCreateMarketType(payload.createMarketType);
    if (payload.createCategory) setCreateCategory(payload.createCategory);
    if (payload.createSubcategory !== undefined) setCreateSubcategory(payload.createSubcategory);
    if (typeof payload.assignmentHouseCompleted === "boolean") {
      setAssignmentHouseCompleted(payload.assignmentHouseCompleted);
    } else if (payload.createCategory === "assignments") {
      setAssignmentHouseCompleted(Boolean(payload.baseForm?.construction_year));
    }
    if (payload.baseForm) setBaseForm((prev) => ({ ...prev, ...payload.baseForm }));
    if (payload.features) setFeatures(payload.features);
    if (payload.communications) setCommunications(payload.communications);
    if (payload.address) setAddress(payload.address);
    if (payload.finance) {
      const financePayload = payload.finance as
        | {
            price_total?: string;
            price_monthly?: string;
            utility_included?: boolean;
            utilities_payer?: UtilitiesPayer;
            rent_term?: RentTerm;
            agent_commission_percent?: string;
            agent_commission_amount?: string;
            price_per_sqm?: string;
          }
        | undefined;
      const areaFromDraft = Number(
        normalizeAreaInput(payload.baseForm?.area_sqm || payload.baseForm?.area_site),
      );
      const sqmFromDraft = Number(financePayload?.price_per_sqm);
      const resolvedDraftTotal = resolveListingTotalPrice({
        area: areaFromDraft,
        pricePerSqm: sqmFromDraft,
      });
      const fallbackTotal = resolvedDraftTotal === null ? "" : String(resolvedDraftTotal);

      setFinance({
        price_total: financePayload?.price_total || fallbackTotal,
        price_monthly: financePayload?.price_monthly || "",
        utility_included: Boolean(financePayload?.utility_included),
        utilities_payer: toUtilitiesPayer(financePayload?.utilities_payer, financePayload?.utility_included),
        rent_term: toRentTerm(financePayload?.rent_term),
        agent_commission_percent: financePayload?.agent_commission_percent || "",
        agent_commission_amount: financePayload?.agent_commission_amount || "",
      });
    }
    if (payload.assignment) setAssignment(payload.assignment);
    if (payload.assignmentPaymentType) setAssignmentPaymentType(payload.assignmentPaymentType);
    if (payload.imageUploads?.length) {
      const restoredUploads: UploadItem[] = payload.imageUploads.map((item) => ({
        id: item.id,
        file: undefined,
        name: item.name,
        size: item.size,
        originalSize: item.originalSize || item.size,
        previewUrl: item.uploadedUrl || "",
        progress: item.progress ?? 100,
        status: item.status || "done",
        uploadedUrl: item.uploadedUrl,
        uploadedPath: item.uploadedPath,
        uploadedFileId: item.uploadedFileId,
      }));
      setImageUploads(restoredUploads.filter((item) => Boolean(item.previewUrl)));
    }
  }, []);

  const applyListingPayload = useCallback((listing: ListingDetail) => {
    const addons = listing.addons || {};
    const images = Array.isArray(listing.images)
      ? [...listing.images].sort((a, b) => Number(a.sort_order) - Number(b.sort_order))
      : [];
    const restoredImages = images.length
      ? images
      : listing.image_url
        ? [{ id: "primary", url: listing.image_url, is_primary: true, sort_order: 0 }]
        : [];
    const sqmPrice = listing.payment_terms?.find((term) => term.period === "sqm")?.price ?? listing.price_sqm;
    const monthlyPrice =
      listing.payment_terms?.find((term) => term.period === "monthly")?.price ?? listing.price_monthly;
    const resolvedTotalPrice =
      listing.market_type !== "rent"
        ? resolveListingTotalPrice({
            area: listing.type === "land_plot" ? listing.area_site : listing.area_sqm,
            pricePerSqm: sqmPrice,
            explicitTotalPrice: listing.price_total,
          })
        : null;
    const totalPrice = resolvedTotalPrice === null ? "" : String(resolvedTotalPrice);

    draftApplyingRef.current = true;
    setCreateStep(1);
    setCreateMarketType(listing.market_type);
    setCreateCategory(listing.board_category);
    setCreateSubcategory(listing.board_subcategory || "");
    setAssignmentHouseCompleted(
      listing.board_category === "assignments" && Boolean(toFormString(listing.construction_year)),
    );
    const nextBaseForm = {
      ...BASE_BY_TYPE[listing.type],
      title: toFormString(listing.title),
      longitude: toFormString(listing.longitude),
      latitude: toFormString(listing.latitude),
      status_id: toFormString(listing.status_id || 1),
      image_url: toFormString(listing.image_url),
      description: toFormString(listing.description),
    };
    const listingValues: Record<string, unknown> = {
      room_count: listing.room_count,
      floor_count: listing.floor_count,
      area_sqm: listing.area_sqm,
      area_site: listing.area_site,
      floor: listing.floor,
      type_id: listing.type_id,
      purpose_id: listing.purpose_id,
      window_view: listing.window_view,
      construction_year: listing.construction_year,
      completion_year: listing.completion_year,
    };
    Object.keys(nextBaseForm).forEach((key) => {
      if (key in listingValues) {
        nextBaseForm[key] = toFormString(listingValues[key]);
      }
    });
    if (isApartmentFloorCountContext(listing.type, listing.market_type, listing.board_category, listing.board_subcategory)) {
      nextBaseForm.floor_count = toFormString(listing.floor_count);
    }
    setBaseForm(nextBaseForm);
    setFeatures({
      is_renovated: toFormBoolean(addons.is_renovated),
      is_penthouse: toFormBoolean(addons.is_penthouse),
      is_apartment_unit: toFormBoolean(addons.is_apartment_unit),
      is_view: toFormBoolean(addons.is_view),
      house_kind: toFormString(addons.house_kind) || "house",
      is_completed: toFormBoolean(addons.is_completed),
      has_pool: toFormBoolean(addons.has_pool),
      has_bathhouse: toFormBoolean(addons.has_bathhouse),
    });
    setCommunications({
      has_sewerage: toFormBoolean(addons.has_sewerage),
      has_water: toFormBoolean(addons.has_water),
      has_electricity: toFormBoolean(addons.has_electricity),
      has_gas: toFormBoolean(addons.has_gas),
    });
    setAddress({
      address_region: toFormString(addons.address_region),
      address_locality: toFormString(addons.address_locality),
      address_street: toFormString(addons.address_street),
      address_house: toFormString(addons.address_house),
    });
    setFinance({
      price_total: totalPrice,
      price_monthly: toFormString(monthlyPrice),
      utility_included: toFormBoolean(addons.utility_included),
      utilities_payer: toUtilitiesPayer(addons.utilities_payer, addons.utility_included),
      rent_term: toRentTerm(addons.rent_term),
      agent_commission_percent: toFormString(addons.agent_commission_percent),
      agent_commission_amount: toFormString(addons.agent_commission_amount),
    });
    setAssignment({
      currency_id: toFormString(listing.assignment?.currency_id) || "1",
      initial_payment: toFormString(listing.assignment?.initial_payment),
      payment: toFormString(listing.assignment?.payment),
      full_payment_purchase: toFormString(listing.assignment?.full_payment_purchase),
      full_payment_price: toFormString(listing.assignment?.full_payment_price),
      agent_markup: toFormString(listing.assignment?.agent_markup),
      recalculation_percentage: toFormString(listing.assignment?.recalculation_percentage),
      recalculation_period: toFormString(listing.assignment?.recalculation_period),
      initial_payment_at: toFormString(listing.assignment?.initial_payment_at),
      assignment_months_left: toFormString(addons.assignment_months_left),
      assignment_paid_amount: toFormString(addons.assignment_paid_amount),
      assignment_paid_months: toFormString(addons.assignment_paid_months),
    });
    const installmentFields = [
      listing.assignment?.initial_payment,
      listing.assignment?.payment,
      listing.assignment?.agent_markup,
      listing.assignment?.recalculation_percentage,
      listing.assignment?.recalculation_period,
    ];
    setAssignmentPaymentType(
      Number(listing.assignment?.full_payment_price || 0) > 0 && installmentFields.every((value) => Number(value || 0) === 0)
        ? "full_payment"
        : "installment",
    );
    setImageUploads(
      restoredImages.map((image, index) => ({
        id: String(image.id || `image-${index}`),
        file: undefined,
        name: `Фото ${index + 1}`,
        size: 0,
        originalSize: 0,
        previewUrl: image.url,
        progress: 100,
        status: "done",
        uploadedUrl: image.url,
      })),
    );
    setUploadError(null);
    setCreateError(null);
    window.setTimeout(() => {
      draftApplyingRef.current = false;
      draftHydratedRef.current = true;
    }, 0);
  }, []);

  const buildDraftPayload = useCallback(
    (): DraftPayload => ({
      createStep,
      createMarketType,
      createCategory,
      createSubcategory,
      assignmentHouseCompleted,
      baseForm,
      features,
      communications,
      address,
      finance,
      assignmentEnabled: isAssignmentCategory,
      assignmentPaymentType,
      assignment,
      imageUploads: imageUploads.map((item) => ({
        id: item.id,
        name: item.name,
        size: item.size,
        originalSize: item.originalSize,
        progress: item.progress,
        status: item.status,
        uploadedUrl: item.uploadedUrl,
        uploadedPath: item.uploadedPath,
        uploadedFileId: item.uploadedFileId,
      })),
    }),
    [
      address,
      assignment,
      assignmentPaymentType,
      assignmentHouseCompleted,
      baseForm,
      communications,
      createStep,
      createCategory,
      createMarketType,
      createSubcategory,
      features,
      finance,
      imageUploads,
      isAssignmentCategory,
    ],
  );

  useEffect(() => {
    if (!showCreateForm || editMode) return;
    skipLocalDraftSyncRef.current = false;
    draftHydratedRef.current = false;
    draftApplyingRef.current = true;
    let mounted = true;
    let hasLocalDraft = false;

    const hydrate = async () => {
      try {
        const raw = localStorage.getItem(localDraftStorageKey);
        if (raw) {
          const parsed = JSON.parse(raw) as LocalDraftEnvelope;
          const now = Date.now();
          if (parsed?.expiresAt && parsed.expiresAt > now && parsed?.payload) {
            lastLocalDraftSnapshotRef.current = JSON.stringify(parsed.payload);
            applyDraftPayload(parsed.payload);
            hasLocalDraft = true;
          } else {
            localStorage.removeItem(localDraftStorageKey);
            if (parsed?.payload?.imageUploads?.length) {
              const staleUploads: UploadItem[] = parsed.payload.imageUploads.map((item) => ({
                id: item.id,
                file: undefined,
                name: item.name,
                size: item.size,
                originalSize: item.originalSize,
                previewUrl: item.uploadedUrl || "",
                progress: item.progress,
                status: item.status,
                uploadedUrl: item.uploadedUrl,
                uploadedPath: item.uploadedPath,
                uploadedFileId: item.uploadedFileId,
              }));
              const staleFileIds = staleUploads
                .map((item) => item.uploadedFileId)
                .filter((id): id is number => Number.isFinite(id));
              const stalePaths = staleUploads
                .map((item) => item.uploadedPath)
                .filter((path): path is string => Boolean(path));
              if (staleFileIds.length || stalePaths.length) {
                await apiClient.delete(API_ENDPOINTS.deleteUploadedFiles, {
                  data: {
                    ids: staleFileIds,
                    paths: stalePaths,
                  },
                });
              }
            }
          }
        }
      } catch {
        localStorage.removeItem(localDraftStorageKey);
      }

      if (!isAuthorized || !draftApiAvailable || hasLocalDraft) {
        draftApplyingRef.current = false;
        draftHydratedRef.current = true;
        return;
      }

      try {
        const response = await apiClient.get(API_ENDPOINTS.classifiedsDraft(ownerId));
        if (!mounted) return;
        const payload = (response?.data?.data?.payload || {}) as Partial<DraftPayload>;
        applyDraftPayload(payload);
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404 && mounted) {
          setDraftApiAvailable(false);
        }
      } finally {
        draftApplyingRef.current = false;
        draftHydratedRef.current = true;
      }
    };

    hydrate();
    return () => {
      mounted = false;
    };
  }, [applyDraftPayload, draftApiAvailable, editMode, isAuthorized, localDraftStorageKey, ownerId, showCreateForm]);

  useEffect(() => {
    if (editMode || !showCreateForm || !draftHydratedRef.current || skipLocalDraftSyncRef.current) return;
    if (draftSyncTimerRef.current) {
      clearTimeout(draftSyncTimerRef.current);
      draftSyncTimerRef.current = null;
    }

    if (!hasDraftContent) {
      return;
    }

    draftSyncTimerRef.current = setTimeout(() => {
      const now = Date.now();
      const payload = buildDraftPayload();
      const snapshot = JSON.stringify(payload);
      if (snapshot === lastLocalDraftSnapshotRef.current) {
        return;
      }
      const envelope: LocalDraftEnvelope = {
        version: 1,
        updatedAt: now,
        expiresAt: now + DRAFT_LOCAL_TTL_MS,
        payload,
      };
      lastLocalDraftSnapshotRef.current = snapshot;
      localStorage.setItem(localDraftStorageKey, JSON.stringify(envelope));
    }, 300);
  }, [buildDraftPayload, editMode, hasDraftContent, localDraftStorageKey, showCreateForm]);

  useEffect(() => {
    if (!editMode) return;
    let mounted = true;

    const loadEditableListing = async () => {
      if (!normalizedEditType || !editId) {
        setEditError("Некорректная ссылка на объявление");
        return;
      }
      if (!canManageListings && !isAuthorized) {
        setEditError("Редактирование объявления доступно только владельцу или администратору");
        return;
      }
      setEditPending(true);
      setEditError(null);
      try {
        const response = await apiClient.get(API_ENDPOINTS.classifiedsListing(normalizedEditType, editId));
        if (!mounted) return;
        const listing = response?.data?.data as ListingDetail | null;
        if (!listing) {
          setEditError("Бэк вернул пустой ответ");
          return;
        }
        const isListingOwner = String(listing.owner_id) === String(ownerId);
        if (!canManageListings && !isListingOwner) {
          setEditError("Редактирование объявления доступно только владельцу или администратору");
          return;
        }
        applyListingPayload(listing);
      } catch (requestError: any) {
        if (!mounted) return;
        setEditError(requestError?.response?.data?.error || "Не удалось загрузить объявление для редактирования");
      } finally {
        if (mounted) setEditPending(false);
      }
    };

    loadEditableListing();
    return () => {
      mounted = false;
    };
  }, [applyListingPayload, canManageListings, editId, editMode, isAuthorized, normalizedEditType, ownerId]);

  useEffect(() => {
    const recalcStickyOffsets = () => {
      const headerHeight = getHeaderScrollBlockHeight();
      const nextStickyTopOffset = Math.max(0, headerHeight);
      stickyTopOffsetRef.current = nextStickyTopOffset;
      setStickyTopOffset((current) => (current === nextStickyTopOffset ? current : nextStickyTopOffset));

      if (stepperDockRef.current) {
        setMobileDockHeight(Math.ceil(stepperDockRef.current.getBoundingClientRect().height));
      }
    };

    recalcStickyOffsets();
    window.addEventListener("scroll", recalcStickyOffsets, { passive: true });
    window.addEventListener("resize", recalcStickyOffsets);
    return () => {
      window.removeEventListener("scroll", recalcStickyOffsets);
      window.removeEventListener("resize", recalcStickyOffsets);
    };
  }, [formRouteMode, showMobilePreview, createStep]);

  useEffect(() => {
    previewUrlsRef.current = imageUploads.map((item) => item.previewUrl);
  }, [imageUploads]);

  useEffect(
    () => () => {
      if (draftSyncTimerRef.current) {
        clearTimeout(draftSyncTimerRef.current);
        draftSyncTimerRef.current = null;
      }
      previewUrlsRef.current.forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      if (guestFavoriteHintTimerRef.current) {
        clearTimeout(guestFavoriteHintTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    setFavoriteKeysHydrated(false);
    setFavoriteViewKeys(null);

    let nextFavoriteKeys = new Set<string>();
    if (isAuthorized) {
      try {
        const raw = localStorage.getItem(favoriteStorageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            nextFavoriteKeys = new Set(parsed.filter((item): item is string => typeof item === "string"));
          }
        }
      } catch {
        nextFavoriteKeys = new Set();
      }
    }

    setFavoriteKeys(nextFavoriteKeys);
    setFavoriteKeysHydrated(true);
  }, [favoriteStorageKey, isAuthorized]);

  useEffect(() => {
    if (!isAuthorized || !favoriteKeysHydrated) return;
    localStorage.setItem(favoriteStorageKey, JSON.stringify(Array.from(favoriteKeys)));
  }, [favoriteKeys, favoriteKeysHydrated, favoriteStorageKey, isAuthorized]);

  useEffect(() => {
    if (!favoriteMode) {
      setFavoriteViewKeys(null);
      return;
    }
    if (!favoriteKeysHydrated) return;

    setFavoriteViewKeys((current) => current || new Set(favoriteKeys));
  }, [favoriteKeys, favoriteKeysHydrated, favoriteMode]);

  useEffect(() => {
    if (myMode) {
      setMarketFilter("my");
      return;
    }

    setMarketFilter(initialMarket === "my" ? "sale" : initialMarket);
    if (previousInitialMarketRef.current !== initialMarket) {
      setCategoryFilter("all");
      setSubcategoryFilter("all");
      previousInitialMarketRef.current = initialMarket;
    }
  }, [initialMarket, myMode]);

  const taxonomyMarketOptions = useMemo(
    () => taxonomy.map((item) => ({ value: item.market_type, label: item.label })),
    [taxonomy],
  );

  const findTaxonomyMarket = useCallback(
    (market?: string) => taxonomy.find((item) => item.market_type === market),
    [taxonomy],
  );

  const findTaxonomyCategory = useCallback(
    (market?: string, category?: string) =>
      findTaxonomyMarket(market)?.categories.find((item) => item.value === category),
    [findTaxonomyMarket],
  );

  const toCategoryLabel = useCallback(
    (market?: string, category?: string) =>
      !market || !category ? category || "—" : findTaxonomyCategory(market, category)?.label || category,
    [findTaxonomyCategory],
  );

  const toSubcategoryLabel = useCallback(
    (market?: string, category?: string, subcategory?: string | null) => {
      if (!market || !category || !subcategory) return "—";
      return (
        findTaxonomyCategory(market, category)?.subcategories.find((item) => item.value === subcategory)?.label ||
        subcategory
      );
    },
    [findTaxonomyCategory],
  );

  const selectedCreateMarket = useMemo(
    () => findTaxonomyMarket(createMarketType),
    [createMarketType, findTaxonomyMarket],
  );

  const selectedCreateCategory = useMemo(
    () => selectedCreateMarket?.categories.find((item) => item.value === createCategory) || null,
    [createCategory, selectedCreateMarket],
  );

  const createCategoryOptions = useMemo(
    () =>
      (selectedCreateMarket?.categories || []).map((item) => ({
        value: item.value,
        label: item.label,
      })),
    [selectedCreateMarket],
  );

  const filterCategoryOptions = useMemo<FilterCategoryOption[]>(() => {
    if (marketFilter === "my") return [];
    return (findTaxonomyMarket(marketFilter)?.categories || []).map((item) => ({
      value: item.value,
      label: item.label,
    }));
  }, [findTaxonomyMarket, marketFilter]);

  const filterSubcategoryOptions = useMemo(() => {
    if (marketFilter === "my" || categoryFilter === "all") return [];
    const node = findTaxonomyCategory(marketFilter, categoryFilter);
    return (node?.subcategories || []).map((sub) => ({
      value: sub.value,
      label: sub.label,
      listing_type: sub.listing_type,
    }));
  }, [categoryFilter, findTaxonomyCategory, marketFilter]);

  const selectedFilterSubcategory = useMemo(() => {
    if (marketFilter === "my" || categoryFilter === "all" || subcategoryFilter === "all") return null;
    return (
      findTaxonomyCategory(marketFilter, categoryFilter)?.subcategories.find((item) => item.value === subcategoryFilter) ||
      null
    );
  }, [categoryFilter, findTaxonomyCategory, marketFilter, subcategoryFilter]);

  const appliedFilterBadges = useMemo(() => {
    const advancedFilters = scopedAdvancedFilters;
    const badges: Array<{ key: string; label: string }> = [];
    const normalizedSearch = search.trim();
    const rangeErrors = getFilterRangeErrors(advancedFilters);
    const priceSqmRange = rangeErrors.priceSqm
      ? null
      : formatRangeLabel(advancedFilters.priceSqmMin, advancedFilters.priceSqmMax, " ₽/м²");
    const priceMonthlyRange = rangeErrors.priceMonthly
      ? null
      : formatRangeLabel(
          advancedFilters.priceMonthlyMin,
          advancedFilters.priceMonthlyMax,
          " ₽/мес",
        );
    const areaRange = rangeErrors.area
      ? null
      : formatRangeLabel(advancedFilters.areaMin, advancedFilters.areaMax, " м²");
    const commissionUnit = advancedFilters.agentCommissionUnit === "amount" ? " ₽" : "%";
    const commissionRange = rangeErrors.agentCommission
      ? null
      : formatRangeLabel(
          advancedFilters.agentCommissionMin,
          advancedFilters.agentCommissionMax,
          commissionUnit,
        );

    if (normalizedSearch) badges.push({ key: "search", label: `Поиск: ${normalizedSearch}` });
    if (priceSqmRange) badges.push({ key: "priceSqm", label: `Цена за м²: ${priceSqmRange}` });
    if (priceMonthlyRange) badges.push({ key: "priceMonthly", label: `Цена/мес: ${priceMonthlyRange}` });
    if (areaRange) badges.push({ key: "area", label: `Площадь: ${areaRange}` });
    if (advancedFilters.utilitiesPayer !== "all") {
      badges.push({
        key: "utilitiesPayer",
        label: `Коммунальные: ${UTILITIES_PAYER_LABELS[advancedFilters.utilitiesPayer]}`,
      });
    }
    if (!selectedCommercialRentCategory && advancedFilters.rentTerm !== "all") {
      badges.push({ key: "rentTerm", label: `Срок: ${RENT_TERM_LABELS[advancedFilters.rentTerm]}` });
    }
    if (commissionRange) badges.push({ key: "commission", label: `Комиссия: ${commissionRange}` });
    if (!selectedCommercialRentCategory && advancedFilters.commissionSort === "rent_term") {
      badges.push({
        key: "sortRentTerm",
        label: `Сортировка: срок аренды ${advancedFilters.sortDirection === "asc" ? "по возрастанию" : "по убыванию"}`,
      });
    } else if (advancedFilters.commissionSort === "commission") {
      badges.push({
        key: "sortCommission",
        label: `Сортировка: комиссия ${advancedFilters.sortDirection === "asc" ? "по возрастанию" : "по убыванию"}`,
      });
    }
    if (advancedFilters.hasImage) badges.push({ key: "hasImage", label: "Только с фото" });

    return badges;
  }, [
    search,
    selectedCommercialRentCategory,
    scopedAdvancedFilters,
  ]);

  useEffect(() => {
    if (!taxonomy.length) return;
    if (marketFilter === "my") return;
    if (!findTaxonomyMarket(marketFilter)) {
      setMarketFilter(taxonomy[0].market_type);
      setCategoryFilter("all");
      setSubcategoryFilter("all");
    }
  }, [findTaxonomyMarket, marketFilter, taxonomy]);

  useEffect(() => {
    if (!taxonomy.length) return;
    if (marketFilter === "my") return;
    if (categoryFilter === "all") return;
    if (!filterCategoryOptions.some((item) => item.value === categoryFilter)) {
      setCategoryFilter("all");
      setSubcategoryFilter("all");
    }
  }, [categoryFilter, filterCategoryOptions, marketFilter, taxonomy.length]);

  useEffect(() => {
    if (!taxonomy.length) return;
    if (marketFilter === "my") return;
    if (categoryFilter === "all") return;
    if (subcategoryFilter === "all") return;
    const categoryNode = findTaxonomyCategory(marketFilter, categoryFilter);
    if (!categoryNode) return;
    if (!filterSubcategoryOptions.some((item) => item.value === subcategoryFilter)) {
      setSubcategoryFilter("all");
    }
  }, [categoryFilter, filterSubcategoryOptions, findTaxonomyCategory, marketFilter, subcategoryFilter, taxonomy.length]);

  useEffect(() => {
    if (!taxonomy.length) return;
    if (!selectedCreateMarket) {
      setCreateMarketType(taxonomy[0].market_type);
      setCreateCategory(taxonomy[0].categories[0]?.value || "");
      setCreateSubcategory("");
      return;
    }
    if (!selectedCreateCategory && selectedCreateMarket.categories[0]) {
      setCreateCategory(selectedCreateMarket.categories[0].value);
      setCreateSubcategory("");
    }
  }, [selectedCreateCategory, selectedCreateMarket, taxonomy]);

  const createSubcategoryOptions = useMemo(() => {
    return (selectedCreateCategory?.subcategories || []).map((sub) => ({
      value: sub.value,
      label: sub.label,
    }));
  }, [selectedCreateCategory]);

  const resolvedListingType = useMemo<ListingType | null>(() => {
    if (!selectedCreateCategory) return null;
    if (selectedCreateCategory.listing_type) return selectedCreateCategory.listing_type;
    const selectedSubcategory = selectedCreateCategory.subcategories.find((item) => item.value === createSubcategory);
    return selectedSubcategory?.listing_type || selectedCreateCategory.subcategories[0]?.listing_type || null;
  }, [createSubcategory, selectedCreateCategory]);

  const listingType = resolvedListingType || "house";
  const supportsCreateRentTerm = createMarketType === "rent" && listingType !== "commercial";
  const supportsApartmentFloorCount = isApartmentFloorCountContext(
    listingType,
    createMarketType,
    createCategory,
    createSubcategory,
  );
  const areaSqmConstraint =
    listingConstraints.areas.area_sqm.by_listing_type[listingType] ||
    FALLBACK_LISTING_CONSTRAINTS.areas.area_sqm.by_listing_type.house!;
  const areaSiteConstraint =
    listingConstraints.areas.area_site.by_listing_type[listingType] ||
    FALLBACK_LISTING_CONSTRAINTS.areas.area_site.by_listing_type.land_plot!;
  const areaSqmValidationError =
    "area_sqm" in baseForm ? getAreaInputError(baseForm.area_sqm, true, areaSqmConstraint) : null;
  const areaSiteValidationError =
    "area_site" in baseForm ? getAreaInputError(baseForm.area_site, true, areaSiteConstraint) : null;
  const areaSqmError = formValidationAttempted ? areaSqmValidationError : null;
  const areaSiteError = formValidationAttempted ? areaSiteValidationError : null;
  const monthlyPriceConstraint = listingConstraints.money.payment_terms.periods.monthly;
  const pricePerSqmFieldConstraint = listingConstraints.money.fields.price_per_sqm;
  const pricePerSqmPeriodConstraint = listingConstraints.money.payment_terms.periods.sqm;
  const combinedPricePerSqmMin = Math.max(pricePerSqmFieldConstraint.min, pricePerSqmPeriodConstraint.min);
  const combinedPricePerSqmMax = Math.min(pricePerSqmFieldConstraint.max, pricePerSqmPeriodConstraint.max);
  const pricePerSqmConstraint =
    combinedPricePerSqmMin <= combinedPricePerSqmMax
      ? {
          min: combinedPricePerSqmMin,
          max: combinedPricePerSqmMax,
          decimal_places: Math.min(
            pricePerSqmFieldConstraint.decimal_places,
            pricePerSqmPeriodConstraint.decimal_places,
          ),
        }
      : FALLBACK_LISTING_CONSTRAINTS.money.fields.price_per_sqm;
  const agentCommissionPercentConstraint = listingConstraints.money.fields.agent_commission_percent;
  const agentCommissionAmountConstraint = listingConstraints.money.fields.agent_commission_amount;
  const titleConstraint = listingConstraints.fields.title;
  const descriptionConstraint = listingConstraints.fields.description;
  const roomCountConstraint = listingConstraints.fields.room_count;
  const floorConstraint = listingConstraints.fields.floor;
  const floorCountConstraint = listingConstraints.fields.floor_count;
  const titleValidationError = getStringInputError(baseForm.title, titleConstraint, {
    required: true,
    valueName: "Заголовок",
  });
  const titleError = formValidationAttempted ? titleValidationError : null;
  const descriptionValidationError = getStringInputError(baseForm.description, descriptionConstraint, {
    valueName: "Описание",
  });
  const descriptionError = formValidationAttempted ? descriptionValidationError : null;
  const roomCountRequired = roomCountConstraint.required_for?.includes(listingType) || false;
  const roomCountValidationError =
    "room_count" in baseForm
      ? getNumericInputError(baseForm.room_count, roomCountConstraint, {
          required: roomCountRequired,
          requiredMessage: "Укажите количество комнат.",
          valueName: "Количество комнат",
        })
      : null;
  const roomCountError = formValidationAttempted ? roomCountValidationError : null;
  const floorRequired = floorConstraint.required_for?.includes(listingType) || false;
  const floorValidationError =
    "floor" in baseForm
      ? getNumericInputError(baseForm.floor, floorConstraint, {
          required: floorRequired,
          requiredMessage: "Укажите этаж.",
          valueName: "Этаж",
        })
      : null;
  const floorError = formValidationAttempted ? floorValidationError : null;
  const floorCountRequired = floorCountConstraint.required_for?.includes(listingType) || false;
  const hasFloorCountField = "floor_count" in baseForm || supportsApartmentFloorCount;
  const floorCountValue = String(baseForm.floor_count || "");
  const floorCountValidationError = hasFloorCountField
    ? getNumericInputError(floorCountValue, floorCountConstraint, {
        required: floorCountRequired,
        requiredMessage: "Укажите этажность.",
        valueName: "Этажность",
      })
    : null;
  const parsedFloor = Number(baseForm.floor);
  const parsedFloorCount = Number(floorCountValue);
  const floorRelationError =
    listingConstraints.cross_field_rules.includes("floor_must_not_exceed_floor_count_when_both_are_positive") &&
    baseForm.floor !== "" &&
    floorCountValue !== "" &&
    parsedFloor > 0 &&
    parsedFloorCount > 0 &&
    parsedFloor > parsedFloorCount
      ? "Этаж не может быть выше этажности дома."
      : null;
  const floorCountError = formValidationAttempted ? floorCountValidationError || floorRelationError : null;
  const yearKey = isAssignmentCategory && !assignmentHouseCompleted ? "completion_year" : "construction_year";
  const yearLabel = yearKey === "completion_year" ? "Год сдачи" : "Год постройки";
  const configuredYearConstraint = listingConstraints.fields[yearKey];
  const currentYear = new Date().getFullYear();
  const yearConstraint = isAssignmentCategory
    ? {
        ...configuredYearConstraint,
        min: yearKey === "construction_year" ? 1800 : currentYear,
        max: yearKey === "construction_year" ? currentYear : currentYear + 10,
      }
    : configuredYearConstraint;
  const yearRequired = isAssignmentCategory;
  const yearValidationError =
    listingType !== "land_plot"
      ? getNumericInputError(baseForm[yearKey], yearConstraint, {
          required: yearRequired,
          requiredMessage: `Укажите ${yearLabel.toLowerCase()}.`,
          valueName: yearLabel,
          useGrouping: false,
        })
      : null;
  const yearError = formValidationAttempted ? yearValidationError : null;
  const typeIdValidationError =
    listingType === "house"
      ? getNumericInputError(baseForm.type_id, listingConstraints.fields.type_id, {
          required: true,
          valueName: "Тип дома",
        })
      : null;
  const purposeIdValidationError =
    listingType === "land_plot"
      ? getNumericInputError(baseForm.purpose_id, listingConstraints.fields.purpose_id, {
          required: true,
          valueName: "Назначение участка",
        })
      : null;
  const windowViewValidationError =
    listingType === "apartment" || listingType === "commercial"
      ? getStringInputError(baseForm.window_view, listingConstraints.fields.window_view, {
          required: true,
          valueName: "Вид из окна",
        })
      : null;
  const longitudeValidationError = getNumericInputError(
    baseForm.longitude,
    { ...listingConstraints.fields.longitude, decimal_places: 20 },
    {
      required: true,
      requiredMessage: "Укажите точку на карте.",
      valueName: "Долгота",
    },
  );
  const latitudeValidationError = getNumericInputError(
    baseForm.latitude,
    { ...listingConstraints.fields.latitude, decimal_places: 20 },
    {
      required: true,
      requiredMessage: "Укажите точку на карте.",
      valueName: "Широта",
    },
  );
  const addressLengthError = Object.values(address).some(
    (value) => String(value || "").length > listingConstraints.addons.address_fields.max_length,
  )
    ? `Каждая часть адреса должна быть не длиннее ${listingConstraints.addons.address_fields.max_length} символов.`
    : null;
  const imageCountError =
    imageUploads.length > listingConstraints.images.max_items
      ? `Можно добавить не более ${listingConstraints.images.max_items} фотографий.`
      : null;
  const imageUrlError =
    imageUploads
      .filter((item) => item.status === "done" && item.uploadedUrl)
      .map((item) => getUrlInputError(item.uploadedUrl, listingConstraints.images.url))
      .find(Boolean) || null;
  const visibleAddressError = formValidationAttempted
    ? longitudeValidationError || latitudeValidationError || addressLengthError
    : null;
  const visibleImageError = formValidationAttempted ? imageCountError || imageUrlError : null;
  const priceAreaValue = Number(
    normalizeAreaInput(listingType === "land_plot" ? baseForm.area_site : baseForm.area_sqm),
  );
  const totalPriceValue = Number(finance.price_total);
  const derivedPricePerSqm =
    finance.price_total && Number.isFinite(totalPriceValue) && Number.isFinite(priceAreaValue) && priceAreaValue > 0
      ? roundToDecimalPlaces(totalPriceValue / priceAreaValue, pricePerSqmConstraint.decimal_places)
      : null;
  const priceTotalError =
    createMarketType !== "rent" && derivedPricePerSqm !== null
      ? getNumericInputError(derivedPricePerSqm, pricePerSqmConstraint, {
          unit: "₽/м²",
          valueName: "Цена",
        })
      : null;
  const priceMonthlyError =
    createMarketType === "rent"
      ? getNumericInputError(finance.price_monthly, monthlyPriceConstraint, {
          unit: "₽",
          valueName: "Цена в месяц",
        })
      : null;
  const agentCommissionPercentError =
    createMarketType === "rent"
      ? getNumericInputError(finance.agent_commission_percent, agentCommissionPercentConstraint, {
          unit: "%",
          valueName: "Комиссия",
        })
      : null;
  const agentCommissionAmountError =
    createMarketType === "rent"
      ? getNumericInputError(finance.agent_commission_amount, agentCommissionAmountConstraint, {
          unit: "₽",
          valueName: "Комиссия",
        })
      : null;
  const visiblePriceTotalError = formValidationAttempted ? priceTotalError : null;
  const visiblePriceMonthlyError = formValidationAttempted ? priceMonthlyError : null;
  const visibleAgentCommissionPercentError = formValidationAttempted ? agentCommissionPercentError : null;
  const visibleAgentCommissionAmountError = formValidationAttempted ? agentCommissionAmountError : null;
  const isFullPaymentAssignment = isAssignmentCategory && assignmentPaymentType === "full_payment";
  const assignmentConstraintErrors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(assignment)
          .filter(([field]) =>
            !isFullPaymentAssignment || ["currency_id", "full_payment_purchase", "full_payment_price"].includes(field),
          )
          .map(([field, value]) => {
          const constraint =
            listingConstraints.money.fields[field as ListingMoneyField] ||
            (field === "assignment_months_left"
              ? listingConstraints.addons.assignment_months_left
              : field === "assignment_paid_months"
                ? listingConstraints.addons.assignment_paid_months
                : field === "recalculation_period"
                  ? listingConstraints.assignment.recalculation_period
                  : field === "currency_id"
                    ? listingConstraints.assignment.currency_id
                    : null);
          return [
            field,
            constraint
              ? getNumericInputError(value, constraint, {
                  required: isFullPaymentAssignment && field === "full_payment_price",
                  requiredMessage: "Укажите полную стоимость продажи.",
                  unit:
                    field === "recalculation_percentage"
                      ? "%"
                      : ["assignment_months_left", "assignment_paid_months", "recalculation_period", "currency_id"].includes(field)
                        ? ""
                        : "₽",
                  valueName: ASSIGNMENT_LABELS[field] || "Значение",
                })
              : null,
          ];
          }),
      ) as Record<string, string | null>,
    [assignment, isFullPaymentAssignment, listingConstraints.addons, listingConstraints.assignment, listingConstraints.money.fields],
  );
  const fullPaymentPrice = Number(assignment.full_payment_price || 0);
  const assignmentCrossFieldError =
    isFullPaymentAssignment
      ? null
      : listingConstraints.cross_field_rules.includes("full_payment_price_must_cover_initial_payment") &&
          fullPaymentPrice < Number(assignment.initial_payment || 0)
        ? "Стоимость при полной оплате не может быть меньше первоначального взноса."
        : listingConstraints.cross_field_rules.includes("full_payment_price_must_cover_full_payment_purchase") &&
            fullPaymentPrice < Number(assignment.full_payment_purchase || 0)
          ? "Стоимость продажи при полной оплате не может быть меньше стоимости покупки."
          : null;
  const showValidationError = useCallback((step: CreateStep, fieldId: string) => {
    pendingValidationFocusIdRef.current = fieldId;
    setValidationFocusRevision((revision) => revision + 1);
    setCreateStep(step);
  }, []);
  const validateListingConstraintFields = useCallback(() => {
    setFormValidationAttempted(true);
    if (
      areaSqmValidationError ||
      areaSiteValidationError ||
      titleValidationError ||
      descriptionValidationError ||
      roomCountValidationError ||
      floorValidationError ||
      floorCountValidationError ||
      floorRelationError ||
      yearValidationError ||
      typeIdValidationError ||
      purposeIdValidationError ||
      windowViewValidationError ||
      priceTotalError ||
      priceMonthlyError ||
      agentCommissionPercentError ||
      agentCommissionAmountError
    ) {
      const firstFieldId = titleValidationError
        ? "listing-title"
        : priceTotalError
          ? "listing-price-total"
          : priceMonthlyError
            ? "listing-price-monthly"
            : roomCountValidationError
              ? "listing-room-count"
              : areaSqmValidationError
                ? "listing-area-sqm"
                : floorValidationError
                  ? "listing-floor"
                  : floorCountValidationError || floorRelationError
                    ? "listing-floor-count"
                    : yearValidationError
                      ? "listing-year"
                      : areaSiteValidationError
                        ? "listing-area-site"
                        : descriptionValidationError
                          ? "listing-description"
                          : agentCommissionPercentError
                            ? "listing-agent-commission-percent"
                            : agentCommissionAmountError
                              ? "listing-agent-commission-amount"
                              : "listing-create-category";
      showValidationError(1, firstFieldId);
      return false;
    }
    if (imageCountError || imageUrlError) {
      showValidationError(2, "listing-images");
      return false;
    }
    if (longitudeValidationError || latitudeValidationError || addressLengthError) {
      showValidationError(4, "listing-address");
      return false;
    }
    if (
      isAssignmentCategory &&
      (Object.values(assignmentConstraintErrors).some(Boolean) || assignmentCrossFieldError)
    ) {
      const firstAssignmentField = Object.entries(assignmentConstraintErrors).find(([, error]) => Boolean(error))?.[0];
      showValidationError(
        5,
        firstAssignmentField ? `listing-assignment-${firstAssignmentField}` : "listing-assignment-full_payment_price",
      );
      return false;
    }

    return true;
  }, [
    agentCommissionAmountError,
    agentCommissionPercentError,
    areaSiteValidationError,
    areaSqmValidationError,
    addressLengthError,
    assignmentConstraintErrors,
    assignmentCrossFieldError,
    descriptionValidationError,
    floorCountValidationError,
    floorRelationError,
    floorValidationError,
    imageCountError,
    imageUrlError,
    isAssignmentCategory,
    latitudeValidationError,
    longitudeValidationError,
    priceMonthlyError,
    priceTotalError,
    purposeIdValidationError,
    roomCountValidationError,
    showValidationError,
    titleValidationError,
    typeIdValidationError,
    windowViewValidationError,
    yearValidationError,
  ]);

  useEffect(() => {
    const fieldId = pendingValidationFocusIdRef.current;
    if (!fieldId) return;

    const animationFrame = window.requestAnimationFrame(() => {
      const field = document.getElementById(fieldId);
      if (!field) return;

      pendingValidationFocusIdRef.current = null;
      field.scrollIntoView({ behavior: "smooth", block: "center" });
      if (field instanceof HTMLElement) {
        field.focus({ preventScroll: true });
      }
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [createStep, formValidationAttempted, validationFocusRevision]);

  useEffect(() => {
    if (editMode) return;
    if (draftApplyingRef.current) return;
    setFormValidationAttempted(false);
    setBaseForm((prev) => {
      const defaults = BASE_BY_TYPE[listingType];
      const preserved = Object.fromEntries(
        Object.keys(defaults)
          .filter((key) => prev[key] !== undefined)
          .map((key) => [key, prev[key]]),
      ) as Record<string, string>;
      return { ...defaults, ...preserved };
    });
  }, [editMode, listingType]);

  useEffect(() => {
    if (!createSubcategory && createSubcategoryOptions.length) {
      setCreateSubcategory(createSubcategoryOptions[0].value);
    }
    if (createSubcategory && !createSubcategoryOptions.some((item) => item.value === createSubcategory)) {
      setCreateSubcategory("");
    }
  }, [createSubcategory, createSubcategoryOptions]);

  useEffect(() => {
    if (!isAssignmentCategory && createStep === 5) {
      setCreateStep(4);
    }
  }, [createStep, isAssignmentCategory]);

  useEffect(() => {
    if (createMarketType === "rent" && finance.price_total) {
      setFinance((prev) => ({ ...prev, price_total: "" }));
    }
  }, [createMarketType, finance.price_total]);

  useEffect(() => {
    if (!isFullPaymentAssignment || assignment.full_payment_price === finance.price_total) return;
    setAssignment((prev) => ({ ...prev, full_payment_price: finance.price_total }));
  }, [assignment.full_payment_price, finance.price_total, isFullPaymentAssignment]);

  const scrollToListingsTop = useCallback(() => {
    if (formRouteMode) return;

    requestAnimationFrame(() => {
      const anchorTop = listingsTopRef.current
        ? window.scrollY + listingsTopRef.current.getBoundingClientRect().top
        : 0;
      const nextTop = Math.max(0, anchorTop - stickyTopOffsetRef.current - 12);
      window.scrollTo({ top: nextTop, behavior: "smooth" });
    });
  }, [formRouteMode]);

  const buildListingsQuery = useCallback(
    (options: {
      cursor?: string | null;
      bounds?: MapBoundsState | null;
      zoom?: number | null;
      limit?: number | null;
      includeSort?: boolean;
    } = {}) => {
      const query = new URLSearchParams();
      if (listFiltersEnabled && isMarketType(marketFilter)) {
        const advancedFilters = scopedAdvancedFilters;
        const rangeErrors = getFilterRangeErrors(advancedFilters);
        query.set("market_type", marketFilter);
        if (categoryFilter !== "all") query.set("board_category", categoryFilter);
        if (subcategoryFilter !== "all") query.set("board_subcategory", subcategoryFilter);
        const normalizedSearch = search.trim();
        if (normalizedSearch) {
          query.set("q", normalizedSearch);
        }
        if (advancedFilters.utilitiesPayer !== "all") {
          query.set("utilities_payer", advancedFilters.utilitiesPayer);
        }
        if (!selectedCommercialRentCategory && advancedFilters.rentTerm !== "all") {
          query.set("rent_term", advancedFilters.rentTerm);
        }
        if (!rangeErrors.priceSqm && advancedFilters.priceSqmMin) {
          query.set("price_sqm_min", advancedFilters.priceSqmMin);
        }
        if (!rangeErrors.priceSqm && advancedFilters.priceSqmMax) {
          query.set("price_sqm_max", advancedFilters.priceSqmMax);
        }
        if (!rangeErrors.priceMonthly && advancedFilters.priceMonthlyMin) {
          query.set("price_monthly_min", advancedFilters.priceMonthlyMin);
        }
        if (!rangeErrors.priceMonthly && advancedFilters.priceMonthlyMax) {
          query.set("price_monthly_max", advancedFilters.priceMonthlyMax);
        }
        if (!rangeErrors.area && advancedFilters.areaMin) query.set("area_min", advancedFilters.areaMin);
        if (!rangeErrors.area && advancedFilters.areaMax) query.set("area_max", advancedFilters.areaMax);
        if (advancedFilters.hasImage) query.set("has_image", "1");
        const commissionPrefix =
          advancedFilters.agentCommissionUnit === "amount"
            ? "agent_commission_amount"
            : "agent_commission_percent";
        if (!rangeErrors.agentCommission && advancedFilters.agentCommissionMin) {
          query.set(`${commissionPrefix}_min`, advancedFilters.agentCommissionMin);
        }
        if (!rangeErrors.agentCommission && advancedFilters.agentCommissionMax) {
          query.set(`${commissionPrefix}_max`, advancedFilters.agentCommissionMax);
        }
        const includeSort = options.includeSort !== false;
        if (includeSort && !selectedCommercialRentCategory && advancedFilters.commissionSort === "rent_term") {
          query.set("sort", "rent_term");
          query.set("sort_direction", advancedFilters.sortDirection);
        } else if (includeSort && advancedFilters.commissionSort !== "default") {
          query.set("sort", commissionPrefix);
          query.set("sort_direction", advancedFilters.sortDirection);
        }
        if (options.bounds) {
          query.set("lat_min", formatCoordinate(options.bounds.latMin));
          query.set("lat_max", formatCoordinate(options.bounds.latMax));
          query.set("lng_min", formatCoordinate(options.bounds.lngMin));
          query.set("lng_max", formatCoordinate(options.bounds.lngMax));
        }
        if (typeof options.zoom === "number" && Number.isFinite(options.zoom)) {
          query.set("zoom", String(Math.round(options.zoom)));
        }
      }
      if (options.limit !== null) {
        query.set("limit", String(options.limit || LISTINGS_PAGE_LIMIT));
      }
      if (options.cursor) query.set("cursor", options.cursor);
      return query;
    },
    [
      categoryFilter,
      listFiltersEnabled,
      marketFilter,
      search,
      selectedCommercialRentCategory,
      scopedAdvancedFilters,
      subcategoryFilter,
    ],
  );

  const loadListings = useCallback(
    async (cursor: string | null = null) => {
      if (isMyListingsRequest && !authToken) return;

      const append = Boolean(cursor);
      const requestId = ++listingsRequestIdRef.current;
      if (append) {
        setLoadMorePending(true);
        setLoadMoreError(null);
      } else {
    setPending(true);
        setLoadMorePending(false);
    setError(null);
        setLoadMoreError(null);
        scrollToListingsTop();
      }
    try {
        const query = buildListingsQuery({ cursor });

        const endpoint = isMyListingsRequest
          ? API_ENDPOINTS.classifiedsMyListings
          : API_ENDPOINTS.classifiedsListings;
        const response = await apiClient.get(`${endpoint}?${query.toString()}`, {
          ...(isMyListingsRequest ? { headers: { Authorization: `Bearer ${authToken}` } } : {}),
        });
        if (requestId !== listingsRequestIdRef.current) return;

        const nextItems: Listing[] = Array.isArray(response?.data?.data) ? response.data.data : [];
        const nextPagination = response?.data?.meta?.pagination;
        const rawTotal = response?.data?.meta?.total;
        const nextMyListingsCounts = parseMyListingsCounts(response?.data?.meta?.counts);
        setItems((current) => {
          if (!append) return nextItems;
          const knownKeys = new Set(current.map((item) => `${item.type}:${item.id}`));
          return [...current, ...nextItems.filter((item) => !knownKeys.has(`${item.type}:${item.id}`))];
        });
        if (isMyListingsRequest && !append) {
          setMyListingsCounts(nextMyListingsCounts);
        }
        setPagination({
          limit: Number(nextPagination?.limit) || LISTINGS_PAGE_LIMIT,
          has_more: Boolean(nextPagination?.has_more),
          next_cursor: typeof nextPagination?.next_cursor === "string" ? nextPagination.next_cursor : null,
          total: rawTotal !== null && rawTotal !== undefined && Number.isFinite(Number(rawTotal))
            ? Number(rawTotal)
            : null,
        });
        trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_LISTINGS_LOADED, {
          market_surface: isMyListingsRequest ? "my_listings" : favoriteMode ? "favorites" : "catalog",
          load_mode: append ? "pagination" : "initial",
          result_count: nextItems.length,
          has_more: Boolean(nextPagination?.has_more),
          search_used: Boolean(query.get("q")),
          search_length: query.get("q")?.length || 0,
          market_filter: query.get("market_type") || (isMyListingsRequest ? "my" : undefined),
          category_filter: query.get("board_category") || undefined,
          subcategory_filter: query.get("board_subcategory") || undefined,
          query_parameter_count: query.toString()
            ? query.toString().split("&").length
            : 0,
        });
    } catch (e: any) {
        if (requestId !== listingsRequestIdRef.current) return;
        const message = e?.response?.data?.error || "Не удалось загрузить объявления";
        if (append) setLoadMoreError(message);
        else setError(message);
        trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_LISTINGS_LOAD_FAILED, {
          market_surface: isMyListingsRequest ? "my_listings" : favoriteMode ? "favorites" : "catalog",
          load_mode: append ? "pagination" : "initial",
          http_status: e?.response?.status,
        });
    } finally {
        if (requestId === listingsRequestIdRef.current) {
          if (append) setLoadMorePending(false);
          else {
            initialListingsLoadedRef.current = true;
            setPending(false);
          }
    }
      }
    },
    [
      authToken,
      buildListingsQuery,
      favoriteMode,
      isMyListingsRequest,
      scrollToListingsTop,
    ],
  );

  const loadMapListings = useCallback(
    async (viewport: MapViewportState | null = mapViewport) => {
      if (!viewport || !listFiltersEnabled) return;

      setMapPending(true);
      setMapError(null);
      try {
        const query = buildListingsQuery({ bounds: viewport, zoom: viewport.zoom, limit: null, includeSort: false });
        const response = await apiClient.get(`${API_ENDPOINTS.classifiedsListingsMap}?${query.toString()}`);
        const nextItems: ClassifiedsMapItem[] = Array.isArray(response?.data?.data) ? response.data.data : [];
        setMapItems(nextItems);
      } catch (e: any) {
        setMapError(e?.response?.data?.error || "Не удалось загрузить объявления на карте");
      } finally {
        setMapPending(false);
      }
    },
    [buildListingsQuery, listFiltersEnabled, mapViewport],
  );

  useEffect(() => {
    if (formRouteMode) return;
    loadListings();
  }, [formRouteMode, loadListings]);

  useEffect(() => {
    if (
      formRouteMode ||
      pending ||
      !initialListingsLoadedRef.current ||
      scrollRestorationCompletedRef.current
    ) {
      return;
    }

    const targetCount = scrollRestorationTargetCountRef.current;
    const nextCursor = pagination.next_cursor;
    const needsMoreItems = items.length < targetCount && pagination.has_more && Boolean(nextCursor);

    if (needsMoreItems && !loadMoreError) {
      if (!loadMorePending && nextCursor && scrollRestorationCursorRef.current !== nextCursor) {
        scrollRestorationCursorRef.current = nextCursor;
        void loadListings(nextCursor);
      }
      return;
    }

    if (loadMorePending) return;

    scrollRestorationCompletedRef.current = true;
    scrollRestorationCursorRef.current = null;
    if (!getClassifiedsScrollPosition(scrollRestorationReturnToRef.current)) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return;
    }
    return restoreClassifiedsScrollPosition(scrollRestorationReturnToRef.current);
  }, [
    formRouteMode,
    items.length,
    loadListings,
    loadMoreError,
    loadMorePending,
    pagination.has_more,
    pagination.next_cursor,
    pending,
  ]);

  useEffect(() => {
    if (!listFiltersEnabled && viewMode === "map") {
      setViewMode("list");
    }
  }, [listFiltersEnabled, viewMode]);

  useEffect(() => {
    if (formRouteMode || viewMode !== "map" || !mapViewport) return;
    loadMapListings(mapViewport);
  }, [formRouteMode, loadMapListings, mapViewport, viewMode]);

  const filteredItems = useMemo(() => {
    if (!listFiltersEnabled) return items;
    const advancedFilters = scopedAdvancedFilters;
    const rangeErrors = getFilterRangeErrors(advancedFilters);

    return items.filter((item) => {
      if (
        selectedFilterSubcategory?.listing_type &&
        item.type !== selectedFilterSubcategory.listing_type
      ) {
        return false;
      }

      const priceSqm = parseNumber(item.price_sqm);
      const priceMonthly = parseNumber(item.price_monthly);
      const area = parseNumber(item.type === "land_plot" ? item.area_site : item.area_sqm);
      const imageUrl = String(item.image_url || "").trim();
      const utilitiesPayer = unwrapListingValue(getListingUtilitiesPayer(item));
      const rentTerm = unwrapListingValue(getListingRentTerm(item));
      const agentCommissionValue =
        advancedFilters.agentCommissionUnit === "amount"
          ? parseNumber(getListingAgentCommissionAmount(item))
          : parseNumber(getListingAgentCommissionPercent(item));

      const minPriceSqm = parseNumber(advancedFilters.priceSqmMin);
      const maxPriceSqm = parseNumber(advancedFilters.priceSqmMax);
      if (!rangeErrors.priceSqm && minPriceSqm !== null && (priceSqm === null || priceSqm < minPriceSqm)) {
        return false;
      }
      if (!rangeErrors.priceSqm && maxPriceSqm !== null && (priceSqm === null || priceSqm > maxPriceSqm)) {
        return false;
      }

      const minPriceMonthly = parseNumber(advancedFilters.priceMonthlyMin);
      const maxPriceMonthly = parseNumber(advancedFilters.priceMonthlyMax);
      if (
        !rangeErrors.priceMonthly &&
        minPriceMonthly !== null &&
        (priceMonthly === null || priceMonthly < minPriceMonthly)
      ) {
        return false;
      }
      if (
        !rangeErrors.priceMonthly &&
        maxPriceMonthly !== null &&
        (priceMonthly === null || priceMonthly > maxPriceMonthly)
      ) {
        return false;
      }

      const minArea = parseNumber(advancedFilters.areaMin);
      const maxArea = parseNumber(advancedFilters.areaMax);
      if (!rangeErrors.area && minArea !== null && (area === null || area < minArea)) return false;
      if (!rangeErrors.area && maxArea !== null && (area === null || area > maxArea)) return false;

      if (
        advancedFilters.utilitiesPayer !== "all" &&
        utilitiesPayer !== advancedFilters.utilitiesPayer
      ) {
        return false;
      }
      if (
        item.type !== "commercial" &&
        advancedFilters.rentTerm !== "all" &&
        rentTerm !== advancedFilters.rentTerm
      ) {
        return false;
      }

      const minAgentCommission = parseNumber(advancedFilters.agentCommissionMin);
      const maxAgentCommission = parseNumber(advancedFilters.agentCommissionMax);
      if (
        !rangeErrors.agentCommission &&
        minAgentCommission !== null &&
        (agentCommissionValue === null || agentCommissionValue < minAgentCommission)
      ) {
        return false;
      }
      if (
        !rangeErrors.agentCommission &&
        maxAgentCommission !== null &&
        (agentCommissionValue === null || agentCommissionValue > maxAgentCommission)
      ) {
        return false;
      }

      if (advancedFilters.hasImage && !imageUrl) return false;

      return true;
    });
  }, [items, listFiltersEnabled, scopedAdvancedFilters, selectedFilterSubcategory]);

  const getFavoriteKey = useCallback((item: Listing) => `${item.type}:${item.id}`, []);

  const selectedMapItem = useMemo(
    () => mapItems.find((item) => !isMapCluster(item) && getFavoriteKey(item) === selectedMapItemKey) || null,
    [getFavoriteKey, mapItems, selectedMapItemKey],
  );

  useEffect(() => {
    if (!selectedMapItemKey) return;
    if (mapItems.some((item) => !isMapCluster(item) && getFavoriteKey(item) === selectedMapItemKey)) return;
    setSelectedMapItemKey(null);
  }, [getFavoriteKey, mapItems, selectedMapItemKey]);

  const isFavorite = useCallback(
    (item: Listing) => favoriteKeys.has(getFavoriteKey(item)),
    [favoriteKeys, getFavoriteKey],
  );

  const toggleFavorite = useCallback(
    (item: Listing) => {
      const key = getFavoriteKey(item);
      if (!isAuthorized) {
        trackAnalyticsEvent(ANALYTICS_EVENTS.FEATURE_ACCESS_DENIED, {
          ...getMarketListingAnalyticsProperties(item),
          feature_key: "market_favorites",
          denial_reason: "authentication_required",
        });
        setGuestFavoriteHintKey(key);
        if (guestFavoriteHintTimerRef.current) {
          clearTimeout(guestFavoriteHintTimerRef.current);
        }
        guestFavoriteHintTimerRef.current = setTimeout(() => {
          setGuestFavoriteHintKey(null);
        }, 3500);
        return;
      }
      const wasFavorite = isFavorite(item);
      trackAnalyticsEvent(
        wasFavorite
          ? ANALYTICS_EVENTS.MARKET_FAVORITE_REMOVED
          : ANALYTICS_EVENTS.MARKET_FAVORITE_ADDED,
        getMarketListingAnalyticsProperties(item),
      );
      setFavoriteKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    [getFavoriteKey, isAuthorized, isFavorite],
  );

  const adjustMyListingsCounts = useCallback((changes: Partial<MyListingsCounts>) => {
    setMyListingsCounts((current) => {
      if (!current) return current;
      return (Object.keys(changes) as MyListingsTab[]).reduce(
        (next, tab) => ({ ...next, [tab]: Math.max(0, next[tab] + (changes[tab] || 0)) }),
        current,
      );
    });
  }, []);

  const toggleListingPublication = useCallback(
    async (item: Listing) => {
      if (!canManageListings && !(myMode && isAuthorized)) return;

      const key = getFavoriteKey(item);
      if (listingStatusPendingKeys.has(key)) return;

      const isPublished = isListingPublished(item);
      const nextStatusId = isPublished ? UNPUBLISHED_STATUS_ID : PUBLISHED_STATUS_ID;

      setListingStatusPendingKeys((current) => new Set(current).add(key));
      setListingStatusErrors((current) => {
        if (!current[key]) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });

      try {
        const response = await apiClient.patch(API_ENDPOINTS.classifiedsListing(item.type, item.id), {
          status_id: nextStatusId,
        });
        const responseStatusId = Number(response?.data?.data?.status_id);
        const resolvedStatusId = Number.isFinite(responseStatusId) ? responseStatusId : nextStatusId;
        const previousTab = getMyListingsTab(item);
        const nextTab = getMyListingsTab({ ...item, status_id: resolvedStatusId });

        setItems((current) =>
          current.map((currentItem) =>
            getFavoriteKey(currentItem) === key ? { ...currentItem, status_id: resolvedStatusId } : currentItem,
          ),
        );
        if (previousTab !== nextTab) {
          adjustMyListingsCounts({ [previousTab]: -1, [nextTab]: 1 });
        }
        trackAnalyticsEvent(
          ANALYTICS_EVENTS.MARKET_LISTING_PUBLISH_TOGGLED,
          {
            ...getMarketListingAnalyticsProperties(item),
            publication_enabled: !isPublished,
            operation_succeeded: true,
            resulting_status_id: resolvedStatusId,
          },
        );
      } catch (e: any) {
        trackAnalyticsEvent(
          ANALYTICS_EVENTS.MARKET_LISTING_PUBLISH_TOGGLED,
          {
            ...getMarketListingAnalyticsProperties(item),
            publication_enabled: !isPublished,
            operation_succeeded: false,
            http_status: e?.response?.status,
          },
        );
        setListingStatusErrors((current) => ({
          ...current,
          [key]: e?.response?.data?.error || "Не удалось изменить статус публикации",
        }));
      } finally {
        setListingStatusPendingKeys((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    },
    [adjustMyListingsCounts, canManageListings, getFavoriteKey, isAuthorized, listingStatusPendingKeys, myMode],
  );

  const deleteListing = useCallback(
    async (item: Listing) => {
      if (!canManageListings) return;

      const key = getFavoriteKey(item);
      if (listingDeletePendingKeys.has(key)) return;

      const shouldDelete = window.confirm(`Удалить объявление «${item.title}»?`);
      if (!shouldDelete) return;

      setListingDeletePendingKeys((current) => new Set(current).add(key));
      setListingDeleteErrors((current) => {
        if (!current[key]) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });

      try {
        await apiClient.delete(API_ENDPOINTS.classifiedsListing(item.type, item.id));
        adjustMyListingsCounts({ [getMyListingsTab(item)]: -1 });
        setItems((current) => current.filter((currentItem) => getFavoriteKey(currentItem) !== key));
        setMapItems((current) =>
          current.filter((currentItem) => isMapCluster(currentItem) || getFavoriteKey(currentItem) !== key),
        );
        setFavoriteKeys((current) => {
          if (!current.has(key)) return current;
          const next = new Set(current);
          next.delete(key);
          return next;
        });
        setSelectedMapItemKey((current) => (current === key ? null : current));
      } catch (e: any) {
        setListingDeleteErrors((current) => ({
          ...current,
          [key]: e?.response?.data?.error || "Не удалось удалить объявление",
        }));
      } finally {
        setListingDeletePendingKeys((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    },
    [adjustMyListingsCounts, canManageListings, getFavoriteKey, listingDeletePendingKeys],
  );

  const myListingsGroups = useMemo(() => {
    const moderation = filteredItems.filter(isListingInModeration);
    const published = filteredItems.filter((item) => !isListingInModeration(item) && isListingPublished(item));
    const unpublished = filteredItems.filter((item) => !isListingInModeration(item) && !isListingPublished(item));
    return { published, moderation, unpublished };
  }, [filteredItems]);

  useEffect(() => {
    if (!myMode || !myListingsAutoLoadTab || !myListingsCounts) return;

    const loadedCount = myListingsGroups[myListingsAutoLoadTab].length;
    const targetCount = myListingsCounts[myListingsAutoLoadTab];
    const nextCursor = pagination.next_cursor;

    if (loadedCount >= targetCount || !pagination.has_more || !nextCursor || loadMoreError) {
      setMyListingsAutoLoadTab(null);
      myListingsAutoLoadCursorRef.current = null;
      return;
    }
    if (loadMorePending || myListingsAutoLoadCursorRef.current === nextCursor) return;

    myListingsAutoLoadCursorRef.current = nextCursor;
    void loadListings(nextCursor);
  }, [
    loadListings,
    loadMoreError,
    loadMorePending,
    myListingsAutoLoadTab,
    myListingsCounts,
    myListingsGroups,
    myMode,
    pagination.has_more,
    pagination.next_cursor,
  ]);

  const myListingsTabs = useMemo<TabsMap>(
    () => ({
      published: {
        id: "published",
        name: `Опубликовано: ${myListingsCounts?.published ?? myListingsGroups.published.length}`,
      },
      moderation: {
        id: "moderation",
        name: `На модерации: ${myListingsCounts?.moderation ?? myListingsGroups.moderation.length}`,
      },
      unpublished: {
        id: "unpublished",
        name: `Не опубликовано: ${myListingsCounts?.unpublished ?? myListingsGroups.unpublished.length}`,
      },
    }),
    [
      myListingsCounts?.moderation,
      myListingsCounts?.published,
      myListingsCounts?.unpublished,
      myListingsGroups.moderation.length,
      myListingsGroups.published.length,
      myListingsGroups.unpublished.length,
    ],
  );

  const visibleFavoriteKeys = favoriteMode && favoriteViewKeys ? favoriteViewKeys : favoriteKeys;
  const visibleItems = useMemo(() => {
    if (favoriteMode) {
      return filteredItems.filter((item) => visibleFavoriteKeys.has(getFavoriteKey(item)));
    }
    if (myMode) {
      return myListingsGroups[myListingsTab];
    }
    return filteredItems;
  }, [
    favoriteMode,
    filteredItems,
    getFavoriteKey,
    myListingsGroups,
    myListingsTab,
    myMode,
    visibleFavoriteKeys,
  ]);
  const shownListingsCount = viewMode === "map" && listFiltersEnabled ? mapItems.length : visibleItems.length;
  const shownListingsTotal = viewMode === "map" ? null : pagination.total;

  const myListingsTabLoading =
    myMode &&
    myListingsAutoLoadTab === myListingsTab &&
    Boolean(myListingsCounts) &&
    myListingsGroups[myListingsTab].length < (myListingsCounts?.[myListingsTab] || 0) &&
    (pagination.has_more || loadMorePending);
  const hasMoreFavoriteListings = !favoriteMode || visibleFavoriteKeys.size > visibleItems.length;

  const selectedListings = useMemo(
    () => visibleItems.filter((item) => selectedListingKeys.has(getFavoriteKey(item))),
    [getFavoriteKey, selectedListingKeys, visibleItems],
  );
  const selectableListings = useMemo(() => visibleItems.slice(0, 100), [visibleItems]);
  const allSelectableListingsSelected =
    selectableListings.length > 0 &&
    selectableListings.every((item) => selectedListingKeys.has(getFavoriteKey(item)));

  useEffect(() => {
    if (!canManageListings) {
      setSelectedListingKeys(new Set());
      return;
    }

    const visibleKeys = new Set(visibleItems.map(getFavoriteKey));
    setSelectedListingKeys((current) => {
      const next = new Set(Array.from(current).filter((key) => visibleKeys.has(key)).slice(0, 100));
      if (next.size === current.size && Array.from(next).every((key) => current.has(key))) return current;
      return next;
    });
  }, [canManageListings, getFavoriteKey, visibleItems]);

  const toggleListingSelection = useCallback(
    (item: Listing) => {
      if (!canManageListings || bulkDeletePending) return;
      const key = getFavoriteKey(item);
      if (!selectedListingKeys.has(key) && selectedListingKeys.size >= 100) {
        setBulkDeleteError("За один раз можно удалить не более 100 объявлений.");
        return;
      }
      setBulkDeleteError(null);
      setSelectedListingKeys((current) => {
        const next = new Set(current);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    },
    [bulkDeletePending, canManageListings, getFavoriteKey, selectedListingKeys],
  );

  const toggleAllListingSelection = useCallback(() => {
    if (!canManageListings || bulkDeletePending) return;
    setBulkDeleteError(null);
    setSelectedListingKeys((current) => {
      const next = new Set(current);
      if (allSelectableListingsSelected) {
        selectableListings.forEach((item) => next.delete(getFavoriteKey(item)));
      } else {
        selectableListings.forEach((item) => {
          if (next.size < 100) next.add(getFavoriteKey(item));
        });
      }
      return next;
    });
  }, [allSelectableListingsSelected, bulkDeletePending, canManageListings, getFavoriteKey, selectableListings]);

  const deleteSelectedListings = useCallback(async () => {
    if (!canManageListings || !selectedListings.length || bulkDeletePending) return;

    const shouldDelete = window.confirm(
      `Удалить выбранные объявления (${selectedListings.length})? Это действие нельзя отменить.`,
    );
    if (!shouldDelete) return;

    const deletedKeys = new Set(selectedListings.map(getFavoriteKey));
    setBulkDeletePending(true);
    setBulkDeleteError(null);
    setListingDeletePendingKeys((current) => new Set([...Array.from(current), ...Array.from(deletedKeys)]));

    try {
      await apiClient.delete(API_ENDPOINTS.classifiedsListings, {
        data: {
          listings: selectedListings.map((item) => {
            const numericId = Number(item.id);
            return {
              type: item.type,
              id: Number.isSafeInteger(numericId) && numericId > 0 ? numericId : item.id,
            };
          }),
        },
      });

      const deletedCounts = selectedListings.reduce<MyListingsCounts>(
        (counts, item) => {
          const tab = getMyListingsTab(item);
          return { ...counts, [tab]: counts[tab] + 1 };
        },
        { published: 0, moderation: 0, unpublished: 0 },
      );
      adjustMyListingsCounts({
        published: -deletedCounts.published,
        moderation: -deletedCounts.moderation,
        unpublished: -deletedCounts.unpublished,
      });
      setItems((current) => current.filter((item) => !deletedKeys.has(getFavoriteKey(item))));
      setMapItems((current) =>
        current.filter((item) => isMapCluster(item) || !deletedKeys.has(getFavoriteKey(item))),
      );
      setFavoriteKeys((current) => {
        const next = new Set(current);
        deletedKeys.forEach((key) => next.delete(key));
        return next;
      });
      setSelectedMapItemKey((current) => (current && deletedKeys.has(current) ? null : current));
      setSelectedListingKeys(new Set());
    } catch (e: any) {
      setBulkDeleteError(e?.response?.data?.error || "Не удалось удалить выбранные объявления");
    } finally {
      setBulkDeletePending(false);
      setListingDeletePendingKeys((current) => {
        const next = new Set(current);
        deletedKeys.forEach((key) => next.delete(key));
        return next;
      });
    }
  }, [adjustMyListingsCounts, bulkDeletePending, canManageListings, getFavoriteKey, selectedListings]);

  const myListingsEmptyText =
    myListingsTab === "moderation"
      ? "Объявлений на модерации нет"
      : myListingsTab === "unpublished"
        ? "Неопубликованных объявлений нет"
        : "Опубликованных объявлений нет";

  const handleMyListingsTabChange = useCallback((id: string) => {
    if (id === "published" || id === "moderation" || id === "unpublished") {
      setMyListingsTab(id);
      setMyListingsAutoLoadTab(id);
      setLoadMoreError(null);
      myListingsAutoLoadCursorRef.current = null;
    }
  }, []);

  const applyFilterInputs = useCallback(
    ({
      search,
      advancedFilters,
    }: {
      search: string;
      advancedFilters: AdvancedFiltersState;
    }) => {
      if (search.trim()) {
        trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_SEARCH_APPLIED, {
          search_length: search.trim().length,
          market_filter: marketFilter,
          category_filter: categoryFilter,
          subcategory_filter: subcategoryFilter,
        });
      }
      if (hasActiveAdvancedFilters(advancedFilters)) {
        trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_FILTER_APPLIED, {
          market_filter: marketFilter,
          category_filter: categoryFilter,
          subcategory_filter: subcategoryFilter,
          has_price_filter: Boolean(
            advancedFilters.priceSqmMin ||
              advancedFilters.priceSqmMax ||
              advancedFilters.priceMonthlyMin ||
              advancedFilters.priceMonthlyMax,
          ),
          has_area_filter: Boolean(
            advancedFilters.areaMin || advancedFilters.areaMax,
          ),
          has_commission_filter: Boolean(
            advancedFilters.agentCommissionMin ||
              advancedFilters.agentCommissionMax,
          ),
          image_required: advancedFilters.hasImage,
          rent_term_filter: advancedFilters.rentTerm || undefined,
        });
      }
      setSearch(search);
      setAdvancedFilters(advancedFilters);
    },
    [categoryFilter, marketFilter, subcategoryFilter],
  );

  const clearSearchFilter = useCallback(() => {
    setSearch((current) => (current ? "" : current));
  }, []);

  const handleMarketFilterChange = useCallback(
    (value: MarketType) => {
      if (favoriteMode || myMode) {
        navigate(`/classifieds?market=${value}`);
        return;
      }

      setMarketFilter(value);
      setCategoryFilter("all");
      setSubcategoryFilter("all");
    },
    [favoriteMode, myMode, navigate],
  );

  const handleFilterCategoryChange = useCallback((value: string) => {
    setCategoryFilter(value);
    setSubcategoryFilter("all");
  }, []);

  const handleFilterSubcategoryChange = useCallback((value: string) => {
    setSubcategoryFilter(value);
  }, []);

  const toggleAdvancedFilters = useCallback(() => {
    setShowAdvancedFilters((prev) => !prev);
  }, []);

  const resetFilters = useCallback(() => {
    setSearch("");
    setMarketFilter(myMode ? "my" : "sale");
    setCategoryFilter("all");
    setSubcategoryFilter("all");
    setShowAdvancedFilters(false);
    setAdvancedFilters(DEFAULT_ADVANCED_FILTERS);
    setFilterControlsSyncRevision((current) => current + 1);
  }, [myMode]);

  const classifiedsReturnTo = useMemo(() => {
    const returnSearchParams = new URLSearchParams(location.search);

    if (myMode) {
      if (myListingsTab === "published") returnSearchParams.delete("tab");
      else returnSearchParams.set("tab", myListingsTab);
    }

    const returnSearch = returnSearchParams.toString();
    return `${location.pathname}${returnSearch ? `?${returnSearch}` : ""}`;
  }, [location.pathname, location.search, myListingsTab, myMode]);

  const getPrimaryUploadedUrl = (uploads: UploadItem[]) =>
    uploads.find((f) => f.status === "done" && f.uploadedUrl)?.uploadedUrl || "";

  const compressImage = async (file: File): Promise<File> => {
    let imageFile = file;
    if (await isHeifFile(file)) {
      try {
        const { heicTo } = await import("heic-to");
        const jpegBlob = await heicTo({
          blob: file,
          type: "image/jpeg",
          quality: 0.9,
        });
        if (!jpegBlob.size) {
          throw new Error("HEIF conversion returned an empty file");
        }
        imageFile = new File([jpegBlob], getConvertedJpegName(file.name), {
          type: "image/jpeg",
          lastModified: file.lastModified || Date.now(),
        });
      } catch (error) {
        console.error("Не удалось конвертировать HEIF в JPEG", error);
        throw new Error("Не удалось обработать HEIF-файл");
      }
    }

    const options = {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      initialQuality: 0.85,
    };
    try {
      const compressed = await imageCompression(imageFile, options);
      return new File([compressed], imageFile.name, {
        type: compressed.type || imageFile.type,
        lastModified: Date.now(),
      });
    } catch {
      return imageFile;
    }
  };

  const resolveUploadedMeta = (response: unknown): UploadResolvedMeta => {
    const typedResponse = response as {
      data?: {
        path?: { file?: string; id?: number } | Array<{ file?: string; id?: number }>;
      };
    };
    const rawNode = Array.isArray(typedResponse?.data?.path)
      ? typedResponse?.data?.path?.[0]
      : typedResponse?.data?.path;
    const rawPath = rawNode?.file;
    const fileId = typeof rawNode?.id === "number" ? rawNode.id : null;

    if (!rawPath || typeof rawPath !== "string") {
      return { url: null, path: null, fileId };
    }
    const cleanPath = rawPath.replace(/^\/+/, "");
    const url =
      cleanPath.startsWith("http://") || cleanPath.startsWith("https://")
        ? cleanPath
        : `${baseHost}/storage/${cleanPath}`;
    return { url, path: cleanPath, fileId };
  };

  const cleanupUploadedFiles = useCallback(async (uploads: UploadItem[]) => {
    const doneItems = uploads.filter((item) => item.status === "done");
    if (!doneItems.length) return;

    const fileIds = doneItems.map((item) => item.uploadedFileId).filter((id): id is number => Number.isFinite(id));
    const paths = doneItems.map((item) => item.uploadedPath).filter((path): path is string => Boolean(path));

    if (!fileIds.length && !paths.length) return;

    try {
      await apiClient.delete(API_ENDPOINTS.deleteUploadedFiles, {
        data: {
          ids: fileIds,
          paths,
        },
      });
    } catch (error) {
      // no-op: cleanup is best-effort and should not block user flow
    }
  }, []);

  const clearLocalDraftStorage = useCallback(() => {
    lastLocalDraftSnapshotRef.current = "";
    try {
      const prefix = `classifieds-create-draft-`;
      for (let index = localStorage.length - 1; index >= 0; index -= 1) {
        const key = localStorage.key(index);
        if (!key) continue;
        if (key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      localStorage.removeItem(localDraftStorageKey);
    }
  }, [localDraftStorageKey]);

  const uploadSingleImage = async (item: UploadItem) => {
    if (!item.file) {
      setImageUploads((prev) =>
        prev.map((f) => (f.id === item.id ? { ...f, status: "error", error: "Файл недоступен для загрузки" } : f)),
      );
      return;
    }

    const itemId = item.id;
    setImageUploads((prev) =>
      prev.map((f) => (f.id === itemId ? { ...f, status: "uploading", progress: 1, error: undefined } : f)),
    );

    const formData = new FormData();
    formData.append("files[]", item.file);

    try {
      const response = await apiClient.post(API_ENDPOINTS.uploadFiles, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (evt) => {
          const total = evt.total || item.size || 1;
          const progress = Math.max(1, Math.min(100, Math.round((evt.loaded / total) * 100)));
          setImageUploads((prev) => prev.map((f) => (f.id === itemId ? { ...f, progress } : f)));
        },
      });

      const uploadedMeta = resolveUploadedMeta(response);
      if (!uploadedMeta.url) {
        throw new Error("Не удалось получить URL загруженного файла");
      }

      setImageUploads((prev) => {
        const next = prev.map((f) =>
          f.id === itemId
            ? {
                ...f,
                status: "done",
                progress: 100,
                uploadedUrl: uploadedMeta.url || undefined,
                uploadedPath: uploadedMeta.path || undefined,
                uploadedFileId: uploadedMeta.fileId || undefined,
              }
            : f,
        );
        const primaryUrl = getPrimaryUploadedUrl(next);
        if (primaryUrl) {
          setBaseForm((prevForm) => ({ ...prevForm, image_url: primaryUrl }));
        }
        return next;
      });
      setUploadError(null);
    } catch (e: any) {
      setImageUploads((prev) =>
        prev.map((f) =>
          f.id === itemId
            ? {
                ...f,
                status: "error",
                error: e?.response?.data?.message || "Ошибка загрузки файла",
              }
            : f,
        ),
      );
      setUploadError("Некоторые фото не загрузились. Удалите проблемные файлы или повторите.");
    }
  };

  const handlePhotoFiles = async (files: File[]) => {
    if (!files.length) return;

    setUploadError(null);
    const availableSlots = Math.max(0, listingConstraints.images.max_items - imageUploads.length);
    if (!availableSlots) {
      setUploadError(`Можно добавить не более ${listingConstraints.images.max_items} фотографий.`);
      return;
    }
    const acceptedFiles = files.slice(0, availableSlots);
    const filesWereLimited = acceptedFiles.length < files.length;

    const processingResults = await Promise.allSettled(acceptedFiles.map((file) => compressImage(file)));
    const processedFiles = processingResults.flatMap((result, index) =>
      result.status === "fulfilled" ? [{ file: result.value, originalFile: acceptedFiles[index] }] : [],
    );
    const failedFileNames = processingResults.flatMap((result, index) =>
      result.status === "rejected" ? [acceptedFiles[index].name] : [],
    );

    const newItems: UploadItem[] = processedFiles.map(({ file, originalFile }) => ({
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      file,
      name: file.name,
      size: file.size,
      originalSize: originalFile.size,
      previewUrl: URL.createObjectURL(file),
      progress: 0,
      status: "queued",
    }));

    setImageUploads((prev) => [...prev, ...newItems]);

    for (const item of newItems) {
      // eslint-disable-next-line no-await-in-loop
      await uploadSingleImage(item);
    }

    if (failedFileNames.length) {
      setUploadError(
        `Не удалось обработать HEIF: ${failedFileNames.join(", ")}. Попробуйте выбрать другое фото или сохранить его как JPG.`,
      );
    } else if (filesWereLimited) {
      setUploadError(`Можно добавить не более ${listingConstraints.images.max_items} фотографий.`);
    }
  };

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    void handlePhotoFiles(files);
  };

  const removeUploadedImage = (itemId: string) => {
    if (!imageUploads.some((item) => item.id !== itemId && item.status === "error")) {
      setUploadError(null);
    }
    setImageUploads((prev) => {
      const item = prev.find((f) => f.id === itemId);
      if (item) {
        URL.revokeObjectURL(item.previewUrl);
      }
      const next = prev.filter((f) => f.id !== itemId);
      const doneUrls = next.filter((f) => f.status === "done" && f.uploadedUrl).map((f) => f.uploadedUrl);
      setBaseForm((formPrev) => ({
        ...formPrev,
        image_url: doneUrls.includes(formPrev.image_url) ? formPrev.image_url : doneUrls[0] || "",
      }));
      return next;
    });
  };

  const reorderUploadedImages = (fromId: string, toId: string, position: "before" | "after") => {
    if (fromId === toId) return;
    setImageUploads((prev) => {
      const fromIndex = prev.findIndex((item) => item.id === fromId);
      const toIndex = prev.findIndex((item) => item.id === toId);
      if (fromIndex < 0 || toIndex < 0) return prev;

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      const adjustedToIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
      const insertIndex = position === "after" ? adjustedToIndex + 1 : adjustedToIndex;
      next.splice(Math.max(0, Math.min(insertIndex, next.length)), 0, moved);

      const primaryUrl = getPrimaryUploadedUrl(next);
      if (primaryUrl) {
        setBaseForm((formPrev) => ({ ...formPrev, image_url: primaryUrl }));
      }

      return next;
    });
  };

  const handleSaveDraft = useCallback(async () => {
    if (!hasDraftContent) return;
    if (!isAuthorized || !draftApiAvailable) {
      setCreateError("Серверный черновик сейчас недоступен.");
      return;
    }
    if (uploadInProgress) {
      setCreateError("Дождитесь завершения загрузки всех выбранных фото.");
      return;
    }
    if (uploadHasErrors) {
      setCreateError("Исправьте ошибки загрузки фото перед сохранением черновика.");
      return;
    }

    setCreateError(null);
    setSaveDraftPending(true);
    try {
      await apiClient.put(API_ENDPOINTS.classifiedsDraft(ownerId), buildDraftPayload());
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setDraftApiAvailable(false);
      }
      setCreateError("Не удалось сохранить черновик в облако.");
    } finally {
      setSaveDraftPending(false);
    }
  }, [buildDraftPayload, draftApiAvailable, hasDraftContent, isAuthorized, ownerId, uploadHasErrors, uploadInProgress]);

  const resetCreateDraft = useCallback(
    async (cleanupRemoteFiles: boolean) => {
      skipLocalDraftSyncRef.current = true;
      if (cleanupRemoteFiles) {
        await cleanupUploadedFiles(imageUploads);
      }
      imageUploads.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      setImageUploads([]);
      setUploadError(null);
      setCreateError(null);
      setFormValidationAttempted(false);
      setCreatePending(false);
      setCreateStep(1);

      setCreateMarketType("sale");
      setCreateCategory("houses");
      setCreateSubcategory("");
      setAssignmentHouseCompleted(false);
      setBaseForm(BASE_BY_TYPE.house);
      setFeatures({
        is_renovated: false,
        is_penthouse: false,
        is_apartment_unit: false,
        is_view: false,
        house_kind: "house",
        is_completed: false,
        has_pool: false,
        has_bathhouse: false,
      });
      setCommunications({
        has_sewerage: false,
        has_water: false,
        has_electricity: false,
        has_gas: false,
      });
      setAddress({
        address_region: "",
        address_locality: "",
        address_street: "",
        address_house: "",
      });
      setFinance({
        price_total: "",
        price_monthly: "",
        utility_included: false,
        utilities_payer: "tenant",
        rent_term: "short_term",
        agent_commission_percent: "",
        agent_commission_amount: "",
      });
      setAssignment({
        currency_id: "1",
        initial_payment: "",
        payment: "",
        full_payment_purchase: "",
        full_payment_price: "",
        agent_markup: "",
        recalculation_percentage: "",
        recalculation_period: "",
        initial_payment_at: "",
        assignment_months_left: "",
        assignment_paid_amount: "",
        assignment_paid_months: "",
      });
      setAssignmentPaymentType("installment");
      clearLocalDraftStorage();

      if (isAuthorized) {
        await apiClient.delete(API_ENDPOINTS.classifiedsDraft(ownerId)).catch((error) => {
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status === 404) {
            setDraftApiAvailable(false);
          }
          return undefined;
        });
      }
    },
    [cleanupUploadedFiles, clearLocalDraftStorage, imageUploads, isAuthorized, ownerId],
  );

  const buildListingPayload = useCallback(
    (includeGallery: boolean) => {
      if (!resolvedListingType) return null;

      const payload: Record<string, unknown> = {
        market_type: createMarketType,
        board_category: createCategory,
        board_subcategory: createSubcategory || null,
      };
      if (!editMode && Number.isFinite(ownerId) && ownerId > 0) {
        payload.owner_id = ownerId;
      }

      Object.entries(baseForm).forEach(([k, v]) => {
        if (k === "floor_count" && resolvedListingType === "apartment") return;
        if (k === "construction_year" || k === "completion_year") return;
        if (k === "image_url") return;
        const numericValue = k === "area_sqm" || k === "area_site" ? normalizeAreaInput(v) : v;
        if (k === "latitude" || k === "longitude") {
          const coordinateConstraint = listingConstraints.fields[k];
          payload[k] = roundToDecimalPlaces(Number(numericValue), coordinateConstraint.decimal_places);
          return;
        }
        if (k === "title" || k === "description") {
          const stringConstraint = listingConstraints.fields[k];
          payload[k] = stringConstraint.trim ? v.trim() : v;
          return;
        }
        payload[k] = numericField(k) ? Number(numericValue) : v;
      });
      if (!editMode) {
        payload.status_id = listingConstraints.creation.forced_status_id;
      }

      if (resolvedListingType !== "land_plot") {
        const yearValue = String(baseForm[yearKey] || "").trim();
        if (yearValue) {
          payload[yearKey] = Number(yearValue);
        } else if (editMode && yearKey === "construction_year") {
          payload[yearKey] = null;
        }
      }

      const listingImages: ListingImagePayload[] = imageUploads
        .filter(
          (item): item is UploadItem & { uploadedUrl: string } => item.status === "done" && Boolean(item.uploadedUrl),
        )
        .map((item, index) => ({
          url: item.uploadedUrl,
          is_primary: index === 0,
          sort_order: index,
        }));
      if (listingImages.length) {
        payload.image_url = listingImages[0].url;
        if (includeGallery) {
          payload.images = listingImages;
        }
      } else if (includeGallery) {
        payload.images = [];
      }
      if (supportsApartmentFloorCount) {
        const rawFloorCount = String(baseForm.floor_count || "").trim();
        payload.floor_count = rawFloorCount ? Number(rawFloorCount) : null;
      }

      const isFullPaymentAssignment = isAssignmentCategory && assignmentPaymentType === "full_payment";
      const areaForCalc = Number(
        normalizeAreaInput(resolvedListingType === "land_plot" ? baseForm.area_site : baseForm.area_sqm),
      );
      const totalPriceInput = isFullPaymentAssignment ? assignment.full_payment_price : finance.price_total;
      const totalPriceForCalc = Number(totalPriceInput);
      const hasTotalPrice =
        createMarketType !== "rent" &&
        String(totalPriceInput).trim().length > 0 &&
        Number.isFinite(totalPriceForCalc) &&
        totalPriceForCalc > 0;
      if (hasTotalPrice) {
        payload.price_total = totalPriceForCalc;
      }
      const derivedPricePerSqm =
        hasTotalPrice && Number.isFinite(areaForCalc) && areaForCalc > 0
          ? roundToDecimalPlaces(totalPriceForCalc / areaForCalc, pricePerSqmConstraint.decimal_places)
          : null;

      const { house_kind: houseKind, ...sharedFeatures } = features;
      const allowedBooleanAddonFields = new Set(listingConstraints.addons.boolean_fields);
      const booleanAddons = Object.fromEntries(
        Object.entries({ ...sharedFeatures, ...communications }).filter(
          ([field]) => allowedBooleanAddonFields.has(field),
        ),
      );
      const addressAddons = buildAddressAddons(
        address,
        listingConstraints.addons.address_fields.names,
      );
      payload.addons = {
        ...booleanAddons,
        ...(resolvedListingType === "house" ? { house_kind: houseKind } : {}),
        ...addressAddons,
        price_per_sqm: derivedPricePerSqm ?? undefined,
        utility_included: createMarketType === "rent" ? finance.utilities_payer === "landlord" : undefined,
        utilities_payer: createMarketType === "rent" ? finance.utilities_payer : undefined,
        rent_term: createMarketType === "rent" && resolvedListingType !== "commercial" ? finance.rent_term : undefined,
        ...(createMarketType === "rent"
          ? buildAgentCommissionAddons(
              finance.agent_commission_percent,
              finance.agent_commission_amount,
              editMode,
            )
          : {}),
        assignment_months_left: !isFullPaymentAssignment && assignment.assignment_months_left
          ? Number(assignment.assignment_months_left)
          : undefined,
        assignment_paid_amount: !isFullPaymentAssignment && assignment.assignment_paid_amount
          ? Number(assignment.assignment_paid_amount)
          : undefined,
        assignment_paid_months: !isFullPaymentAssignment && assignment.assignment_paid_months
          ? Number(assignment.assignment_paid_months)
          : undefined,
      };

      const paymentTerms: Array<{
        period: string;
        price: number;
        currency_id: number;
      }> = [];
      if (createMarketType !== "rent" && derivedPricePerSqm !== null) {
        paymentTerms.push({
          period: "sqm",
          price: derivedPricePerSqm,
          currency_id: 1,
        });
      }
      if (createMarketType === "rent" && finance.price_monthly) {
        paymentTerms.push({
          period: "monthly",
          price: Number(finance.price_monthly),
          currency_id: 1,
        });
      }
      if (!isFullPaymentAssignment) {
        payload.payment_terms = paymentTerms;
      }

      payload.assignment = isAssignmentCategory
        ? isFullPaymentAssignment
          ? {
              enabled: true,
              currency_id: Number(assignment.currency_id || 1),
              full_payment_purchase: totalPriceForCalc,
              full_payment_price: totalPriceForCalc,
            }
          : {
            enabled: true,
            currency_id: Number(assignment.currency_id || 1),
            initial_payment: Number(assignment.initial_payment || 0),
            payment: Number(assignment.payment || 0),
            full_payment_purchase: Number(assignment.full_payment_purchase || 0),
            full_payment_price: Number(assignment.full_payment_price || 0),
            agent_markup: Number(assignment.agent_markup || 0),
            recalculation_percentage: Number(assignment.recalculation_percentage || 0),
            recalculation_period: Number(assignment.recalculation_period || 0),
            initial_payment_at: assignment.initial_payment_at || new Date().toISOString(),
          }
        : { enabled: false };

      return payload;
    },
    [
      address,
      assignment,
      assignmentPaymentType,
      baseForm,
      communications,
      createCategory,
      createMarketType,
      createSubcategory,
      features,
      finance.price_monthly,
      finance.price_total,
      finance.utilities_payer,
      finance.rent_term,
      finance.agent_commission_percent,
      finance.agent_commission_amount,
      imageUploads,
      isAssignmentCategory,
      editMode,
      listingConstraints.addons,
      listingConstraints.creation.forced_status_id,
      listingConstraints.fields,
      ownerId,
      pricePerSqmConstraint.decimal_places,
      resolvedListingType,
      supportsApartmentFloorCount,
      yearKey,
    ],
  );

  const onCreate = async () => {
    if (!validateListingConstraintFields()) {
      return;
    }
    if (!editMode && !authToken) {
      setCreateError("Для публикации объявления войдите в аккаунт.");
      return;
    }
    if (!editMode && (!Number.isFinite(ownerId) || ownerId <= 0)) {
      setCreateError("Не найден user id");
      return;
    }
    if (!editMode && !canCreateListings) {
      setCreateError("Ваш email не добавлен в список пользователей, которым разрешено создавать объявления.");
      return;
    }
    if (uploadInProgress) {
      setCreateError("Дождитесь завершения загрузки всех выбранных фото.");
      return;
    }
    if (uploadHasErrors) {
      setCreateError("Исправьте ошибки загрузки фото перед публикацией.");
      return;
    }
    if (taxonomyPending || !resolvedListingType) {
      setCreateError("Дождитесь загрузки справочника категорий.");
      return;
    }
    setCreatePending(true);
    setCreateError(null);
    try {
      const payload = buildListingPayload(true);
      if (!payload) {
        setCreateError("Не удалось определить тип объявления.");
        return;
      }
      const requestBodyBytes = new Blob([JSON.stringify(payload)]).size;
      if (requestBodyBytes > listingConstraints.request.max_body_bytes) {
        setCreateError(
          `Объём данных объявления превышает ${formatConstraintNumber(
            listingConstraints.request.max_body_bytes / 1024,
            0,
          )} КБ. Сократите текстовые поля.`,
        );
        return;
      }

      if (editMode) {
        if (!normalizedEditType || !editId) {
          setCreateError("Некорректная ссылка на объявление.");
          return;
        }
        const response = await apiClient.patch(
          API_ENDPOINTS.classifiedsListing(normalizedEditType, editId),
          payload,
        );
        const updatedListing = response?.data?.data as Partial<ListingDetail> | undefined;
        const updatedListingType = normalizeListingType(updatedListing?.type) || normalizedEditType;
        const updatedListingId = updatedListing?.id ?? editId;
        trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_LISTING_UPDATED, {
          ...getMarketListingAnalyticsProperties({
            ...payload,
            ...updatedListing,
            id: updatedListingId,
            type: updatedListingType,
          }),
          image_count: imageUploads.length,
        });
        navigate(getListingDetailPath(updatedListingType, updatedListingId), { replace: true });
      } else {
        const response = await apiClient.post(
          `${API_ENDPOINTS.classifiedsListings}/${resolvedListingType}`,
          payload,
        );
        const createdListing = response?.data?.data as Partial<ListingDetail> | undefined;
        const createdListingType = normalizeListingType(createdListing?.type) || resolvedListingType;
        const createdListingId = createdListing?.id;
        trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_LISTING_CREATED, {
          ...getMarketListingAnalyticsProperties({
            ...payload,
            ...createdListing,
            type: createdListingType,
          }),
          image_count: imageUploads.length,
        });
        await resetCreateDraft(false);
        setShowCreateForm(false);
        navigate(
          createdListingId === undefined || createdListingId === null
            ? "/classifieds/my"
            : getListingDetailPath(createdListingType, createdListingId),
          { replace: true },
        );
      }
    } catch (e: any) {
      trackAnalyticsEvent(
        editMode
          ? ANALYTICS_EVENTS.MARKET_LISTING_SAVE_FAILED
          : ANALYTICS_EVENTS.MARKET_LISTING_CREATE_FAILED,
        {
        market_form_mode: editMode ? "edit" : "create",
        listing_id: editId,
        listing_type: normalizedEditType || resolvedListingType || undefined,
        http_status: e?.response?.status,
        },
      );
      setCreateError(e?.response?.data?.error || (editMode ? "Ошибка сохранения" : "Ошибка создания"));
    } finally {
      setCreatePending(false);
    }
  };

  const renderListingCard = (item: Listing, showFavorite: boolean) => {
    const listingKey = getFavoriteKey(item);
    const isPublicationPending = listingStatusPendingKeys.has(listingKey);
    const publicationError = listingStatusErrors[listingKey];
    const isDeletePending = listingDeletePendingKeys.has(listingKey);
    const deleteError = listingDeleteErrors[listingKey];
    const showBulkSelection = canManageListings && showFavorite && viewMode === "list";
    const isSelectedForBulkDelete = showBulkSelection && selectedListingKeys.has(listingKey);
    const isPublished = isListingPublished(item);
    const moderationStatus = item.moderation_status;
    const showListingManagement = (canManageListings || (myMode && isAuthorized)) && showFavorite;
    const priceMonthlyText = formatMoney(item.price_monthly);
    const priceSqmText = formatMoney(item.price_sqm);
    const saleArea = Number(item.type === "land_plot" ? item.area_site : item.area_sqm);
    const priceSqm = Number(item.price_sqm);
    const unitPriceText = item.type === "land_plot" ? formatMoney(priceSqm * 100) : priceSqmText;
    const unitPriceLabel = item.type === "land_plot" ? "сотку" : "м²";
    const buildingAreaText = formatAreaValue(item.area_sqm, "sqm");
    const siteAreaText = formatAreaValue(item.area_site, "sotka");
    const areaDetails = [
      buildingAreaText ? `Площадь: ${buildingAreaText}` : null,
      siteAreaText ? `Участок: ${siteAreaText}` : null,
    ].filter(Boolean);
    const saleTotalPrice = resolveListingTotalPrice({
      area: saleArea,
      pricePerSqm: priceSqm,
      explicitTotalPrice: item.price_total,
    });
    const primaryPriceText =
      item.market_type === "rent"
        ? priceMonthlyText
          ? `${priceMonthlyText}/мес`
          : "Договорная"
        : formatMoney(saleTotalPrice) || unitPriceText || "Договорная";
    const priceDescriptor =
      item.market_type === "rent"
        ? null
        : saleTotalPrice && unitPriceText
          ? `Цена за ${unitPriceLabel}: ${unitPriceText}`
          : unitPriceText
            ? `за ${unitPriceLabel}`
          : null;
    const rentDetails: string[] = [];
    if (item.market_type === "rent") {
      const utilitiesPayer = unwrapListingValue(getListingUtilitiesPayer(item));
      const rentTerm = unwrapListingValue(getListingRentTerm(item));
      const legacyUtilityIncluded = toFormBoolean(item.addons?.utility_included);
      if (item.type !== "commercial" && (rentTerm === "short_term" || rentTerm === "long_term")) {
        rentDetails.push(`Срок аренды: ${RENT_TERM_LABELS[rentTerm]}`);
      }
      if (utilitiesPayer === "tenant" || utilitiesPayer === "landlord") {
        rentDetails.push(`Коммунальные платит: ${UTILITIES_PAYER_LABELS[utilitiesPayer]}`);
      } else if (legacyUtilityIncluded) {
        rentDetails.push(`Коммунальные платит: ${UTILITIES_PAYER_LABELS.landlord}`);
      }

      rentDetails.push(
        `Комиссия агента: ${formatAgentCommission(
          getListingAgentCommissionPercent(item),
          getListingAgentCommissionAmount(item),
        )}`,
      );
    }
    const subcategoryLabel = toSubcategoryLabel(
      item.market_type,
      item.board_category,
      item.board_subcategory as string,
    );
    const hasSubcategory = subcategoryLabel !== "—";
    const openListing = () => {
      if (showFavorite) {
        trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_LISTING_OPENED, {
          ...getMarketListingAnalyticsProperties(item),
          market_surface: myMode
            ? "my_listings"
            : favoriteMode
              ? "favorites"
              : viewMode,
        });
        saveClassifiedsScrollPosition(classifiedsReturnTo, items.length);
        navigate(`/classifieds/${item.type}/${item.id}`, {
          state: {
            classifiedsReturnTo,
          },
        });
      }
    };
    return (
      <div
        className={`${ui.card} ${isSelectedForBulkDelete ? "ring-2 ring-[#c8508a]" : ""}`}
        role={showFavorite ? "link" : undefined}
        tabIndex={showFavorite ? 0 : undefined}
        onClick={openListing}
        onKeyDown={(event) => {
          if (showFavorite && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            openListing();
          }
        }}
      >
        <div className={ui.cardImage}>
          {showBulkSelection ? (
            <label
              className="absolute left-3 top-3 z-10 grid h-8 w-8 cursor-pointer place-items-center rounded-lg border border-[#e8c5d8] bg-white shadow-[0_3px_10px_rgba(0,0,0,0.14)]"
              onClick={(event) => event.stopPropagation()}
            >
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer accent-[#c8508a]"
                checked={isSelectedForBulkDelete}
                disabled={bulkDeletePending}
                aria-label={`Выбрать объявление «${item.title}»`}
                onChange={() => toggleListingSelection(item)}
              />
            </label>
          ) : null}
          {item.image_url ? (
            <img className={ui.cardImageTag} src={item.image_url} alt={item.title} />
          ) : (
            <div className={ui.cardPlaceholder}>Нет фото</div>
          )}
        </div>

        <div className={ui.cardBody}>
          <div className={ui.cardTop}>
            <div className={ui.cardTypeWrap}>
              <span className={ui.cardType}>{item.market_type === "rent" ? "Аренда" : "Купля/Продажа"}</span>
              <span className={ui.cardType}>{toCategoryLabel(item.market_type, item.board_category)}</span>
              {hasSubcategory ? <span className={ui.cardType}>{subcategoryLabel}</span> : null}
              {myMode && moderationStatus && moderationStatus !== "approved" ? (
                <span
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    moderationStatus === "rejected"
                      ? "bg-[#fef2f2] text-[#b42318]"
                      : "bg-[#fff7ed] text-[#9a3412]"
                  }`}
                >
                  {MODERATION_LABELS[moderationStatus]}
                </span>
              ) : null}
            </div>
            {showFavorite ? (
              <div className="relative">
                <button
                  type="button"
                  aria-label={isFavorite(item) ? "Убрать из избранного" : "Добавить в избранное"}
                  title={isFavorite(item) ? "Убрать из избранного" : "Добавить в избранное"}
                  className={`grid h-8 w-8 place-items-center rounded-full border transition ${
                    isFavorite(item)
                      ? "border-[#c8508a] bg-[#fbeaf0] text-[#c8508a]"
                      : "border-[#e8e8e8] bg-white text-[#a0a0a0] hover:border-[#d8bfd0] hover:text-[#c8508a]"
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleFavorite(item);
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill={isFavorite(item) ? "currentColor" : "none"}
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                      d="M12 20.4l-1.45-1.32C5.4 14.36 2 11.28 2 7.5 2 4.42 4.42 2 7.5 2 9.24 2 10.91 2.81 12 4.09 13.09 2.81 14.76 2 16.5 2 19.58 2 22 4.42 22 7.5c0 3.78-3.4 6.86-8.55 11.58L12 20.4z"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {guestFavoriteHintKey === getFavoriteKey(item) ? (
                  <div className="absolute right-0 top-[36px] z-10 w-[240px] rounded-[10px] border border-[#efcddd] bg-white p-2.5 text-[12px] leading-[1.45] text-[#6b6b6b] shadow-[0_6px_20px_rgba(0,0,0,0.08)]">
                    Функция доступна только авторизованным пользователям.{" "}
                    <button
                      type="button"
                      className="cursor-pointer border-none bg-transparent p-0 font-semibold text-[#8b2a5e] underline"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate("/signin");
                      }}
                    >
                      Войти
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <h4 className={ui.cardTitle}>{item.title}</h4>
          <div className={ui.cardMainPrice}>{primaryPriceText}</div>
          <div className={ui.cardPriceDescriptor}>{priceDescriptor || "\u00A0"}</div>
          {areaDetails.length ? <div className={ui.cardMeta}>{areaDetails.join(" · ")}</div> : null}
          {rentDetails.length ? (
            <div className="mt-1 grid gap-0.5 text-xs leading-[1.45] text-[#6b6b6b]">
              {rentDetails.map((detail) => (
                <div key={detail}>{detail}</div>
              ))}
            </div>
          ) : null}
          {myMode && moderationStatus === "rejected" && item.moderation_reason ? (
            <div className="rounded-lg border border-[#f5c2c7] bg-[#fff5f5] px-3 py-2 text-[12px] leading-5 text-[#9f1239]">
              Причина отказа: {item.moderation_reason}
            </div>
          ) : null}
          {showListingManagement ? (
            <div className={ui.cardPublicationRow}>
              <span className={`${ui.cardPublicationStatus} ${isPublished ? "text-[#8b2a5e]" : "text-[#777]"}`}>
                <span
                  aria-hidden="true"
                  className={`${ui.cardPublicationDot} ${
                    isPublished ? "bg-[#c8508a]" : moderationStatus === "rejected" ? "bg-[#e11d48]" : "bg-[#b8b8b8]"
                  }`}
                />
                {moderationStatus && moderationStatus !== "approved"
                  ? MODERATION_LABELS[moderationStatus]
                  : isPublished
                    ? "Опубликовано"
                    : "Снято с публикации"}
              </span>
              <button
                type="button"
                className={`${ui.cardPublicationButton} ${
                  isPublished
                    ? "border-[#dedede] bg-white text-[#555] hover:border-[#c8508a] hover:text-[#8b2a5e]"
                    : "border-[#c8508a] bg-[#c8508a] text-white hover:border-[#b3407a] hover:bg-[#b3407a]"
                }`}
                disabled={isPublicationPending || Boolean(moderationStatus && moderationStatus !== "approved")}
                aria-busy={isPublicationPending}
                onClick={(event) => {
                  event.stopPropagation();
                  void toggleListingPublication(item);
                }}
              >
                {moderationStatus && moderationStatus !== "approved"
                  ? "Ждёт решения"
                  : isPublicationPending
                  ? isPublished
                    ? "Снимаем..."
                    : "Публикуем..."
                  : isPublished
                    ? "Снять с публикации"
                    : "Опубликовать"}
              </button>
              <div className={ui.cardAdminActions}>
                <button
                  type="button"
                  className={`${ui.cardAdminButton} border-[#c8508a] bg-white text-[#8b2a5e] hover:bg-[#fff2f8]`}
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/classifieds/${item.type}/${item.id}/edit`);
                  }}
                >
                  Редактировать
                </button>
                {canManageListings ? (
                  <button
                    type="button"
                    className={`${ui.cardAdminButton} border-rose-300 bg-white text-rose-600 hover:bg-rose-50`}
                    disabled={isDeletePending}
                    aria-busy={isDeletePending}
                    onClick={(event) => {
                      event.stopPropagation();
                      void deleteListing(item);
                    }}
                  >
                    {isDeletePending ? "Удаляем..." : "Удалить"}
                  </button>
                ) : null}
              </div>
              {publicationError ? <span className={ui.cardPublicationError}>{publicationError}</span> : null}
              {deleteError ? <span className={ui.cardPublicationError}>{deleteError}</span> : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const previewListing: Listing = {
    id: "preview",
    title: baseForm.title || "Без заголовка",
    type: listingType,
    market_type: createMarketType,
    board_category: createCategory,
    board_subcategory: createSubcategory || null,
    image_url: baseForm.image_url || null,
    area_sqm: baseForm.area_sqm || null,
    area_site: baseForm.area_site || null,
    price_sqm:
      createMarketType !== "rent" &&
      Number(normalizeAreaInput(listingType === "land_plot" ? baseForm.area_site : baseForm.area_sqm)) > 0 &&
      Number(finance.price_total) > 0
        ? String(
            Number(finance.price_total) /
              Number(normalizeAreaInput(listingType === "land_plot" ? baseForm.area_site : baseForm.area_sqm)),
          )
        : null,
    price_monthly: finance.price_monthly || null,
    utilities_payer: createMarketType === "rent" ? finance.utilities_payer : null,
    rent_term: supportsCreateRentTerm ? finance.rent_term : null,
    agent_commission_percent:
      createMarketType === "rent" && finance.agent_commission_percent ? finance.agent_commission_percent : null,
    agent_commission_amount:
      createMarketType === "rent" && finance.agent_commission_amount ? finance.agent_commission_amount : null,
    addons:
      createMarketType === "rent"
        ? {
            utilities_payer: finance.utilities_payer,
            rent_term: supportsCreateRentTerm ? finance.rent_term : undefined,
            utility_included: finance.utilities_payer === "landlord",
            agent_commission_percent: finance.agent_commission_percent || undefined,
            agent_commission_amount: finance.agent_commission_amount || undefined,
          }
        : undefined,
  };

  const previewCard = (
    <div className={ui.previewCard}>
      <FormSectionTitle text="Предпросмотр объявления" />
      {renderListingCard(previewListing, false)}
    </div>
  );

  const listingsSkeleton = (
    <ul className={ui.list}>
      {Array.from({ length: 6 }).map((_, idx) => (
        <li key={idx} className={`${ui.card} ${ui.skeletonPulse}`}>
          <div className={ui.cardImage}>
            <div className="h-full w-full bg-[#ececec]" />
          </div>
          <div className={ui.cardBody}>
            <div className={ui.cardTop}>
              <div className="h-6 w-28 rounded-full bg-[#f1e7ec]" />
              <div className="h-4 w-16 rounded bg-[#efefef]" />
            </div>
            <div className="h-6 w-4/5 rounded bg-[#ededed]" />
            <div className="h-4 w-2/3 rounded bg-[#f1f1f1]" />
            <div className={ui.cardPrices}>
              <div className="h-6 w-28 rounded-full bg-[#f0f0f0]" />
              <div className="h-6 w-24 rounded-full bg-[#f0f0f0]" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );

  const pageNavigation = (
    <PageNavigation
      backLabel={editMode ? "К объявлению" : createMode ? "К списку объявлений" : undefined}
      onBack={
        formRouteMode
          ? () =>
              editMode && normalizedEditType && editId
                ? navigate(`/classifieds/${normalizedEditType}/${editId}`, { replace: true })
                : navigate("/classifieds")
          : undefined
      }
    />
  );

  if (editPending || (taxonomyPending && !taxonomy.length)) {
    return (
      <div className={ui.container}>
        {pageNavigation}
        <div className={`${ui.header} ${ui.skeletonPulse}`}>
          <div className="space-y-2">
            <div className="h-9 w-64 rounded bg-[#ececec]" />
            <div className="h-4 w-80 rounded bg-[#f1f1f1]" />
          </div>
          <div className="flex w-full gap-2 sm:w-auto">
            <div className="h-[38px] w-36 rounded-md bg-[#efefef]" />
            <div className="h-[38px] w-44 rounded-md bg-[#e6d4df]" />
          </div>
        </div>

        <div className={`${ui.marketTabs} ${ui.skeletonPulse}`}>
          <div className="h-[38px] w-28 rounded-[10px] bg-[#f0f0f0]" />
          <div className="h-[38px] w-40 rounded-[10px] bg-[#e6d4df]" />
          <div className="h-[38px] w-28 rounded-[10px] bg-[#f0f0f0]" />
          {isAuthorized ? <div className="h-[38px] w-32 rounded-[10px] bg-[#f0f0f0]" /> : null}
        </div>

        <div className={`${ui.filters} ${ui.filtersMobileEdge} ${ui.skeletonPulse}`}>
          <div className="h-[38px] rounded-md bg-[#f0f0f0] md:col-span-2 lg:col-span-1" />
          <div className="h-[38px] rounded-md bg-[#f0f0f0]" />
          <div className="h-[38px] rounded-md bg-[#f0f0f0]" />
          <div className="h-[38px] rounded-md bg-[#f0f0f0]" />
          <div className="h-[22px] w-28 rounded bg-[#f3f3f3]" />
          <div className="h-[38px] rounded-md bg-[#f6e8ef]" />
        </div>

        <div className={`${ui.toolbar} ${ui.skeletonPulse}`}>
          <div className="h-5 w-32 rounded bg-[#f0f0f0]" />
          <div className="flex gap-1.5">
            <div className="h-7 w-14 rounded-full bg-[#f0f0f0]" />
            <div className="h-7 w-20 rounded-full bg-[#f0f0f0]" />
            <div className="h-7 w-16 rounded-full bg-[#f0f0f0]" />
          </div>
        </div>

        {listingsSkeleton}
      </div>
    );
  }
  if (error) {
    return (
      <div className={ui.container}>
        {pageNavigation}
        <Error title="Ошибка загрузки" description={error} retryCallback={() => loadListings()} />
      </div>
    );
  }
  if (editError) {
    return (
      <div className={ui.container}>
        {pageNavigation}
        <Error
          title="Редактирование недоступно"
          description={editError}
          buttonText="к объявлению"
          retryCallback={() => {
            if (normalizedEditType && editId) {
              navigate(`/classifieds/${normalizedEditType}/${editId}`, { replace: true });
            } else {
              navigate("/classifieds", { replace: true });
            }
          }}
        />
      </div>
    );
  }
  if (taxonomyError && !taxonomy.length) {
    return (
      <div className={ui.container}>
        {pageNavigation}
        <Error title="Ошибка загрузки справочника" description={taxonomyError} retryCallback={loadTaxonomy} />
      </div>
    );
  }

  return (
    <div className={ui.container}>
      {pageNavigation}

      <div className={ui.header}>
        <div>
          <h1 className={ui.title}>
            {editMode
              ? "Редактирование объявления"
              : createMode
                ? "Добавление объявления"
                : "Маркет"}
          </h1>
          <p className={ui.subtitle}>
            {editMode
              ? "Измените параметры объявления и сохраните обновлённую версию."
              : createMode
                ? "Заполните параметры объявления и опубликуйте его в Маркете."
              : myMode
                ? "Управляйте публикацией своих объявлений в Маркете."
                : favoriteMode
                  ? "Сохранённые объявления из Маркета."
                  : "Покупка, аренда и переуступки недвижимости в одном месте."}
          </p>
        </div>
        <div className={ui.headerActions}>
          {createMode && canCreateListings ? (
            <button
              type="button"
              className={`${ui.btnBase} ${ui.btnGhost}`}
              onClick={handleSaveDraft}
              disabled={!hasDraftContent || saveDraftPending || createPending || uploadInProgress || !draftApiAvailable}
            >
              {saveDraftPending ? "Сохраняем..." : "Сохранить черновик"}
            </button>
          ) : null}
          {createMode && canCreateListings && hasDraftContent ? (
            <button
              type="button"
              className={`${ui.btnBase} ${ui.btnGhost}`}
              onClick={() => resetCreateDraft(true)}
              disabled={createPending || uploadInProgress || saveDraftPending}
            >
              Сбросить черновик
            </button>
          ) : null}
          {!showCreateForm && canCreateListings ? (
            <button
              type="button"
              className={`${ui.btnBase} ${ui.btnPrimary}`}
              onClick={() => navigate("/classifieds/create")}
            >
              Добавить объявление
            </button>
          ) : null}
        </div>
      </div>

      {!formRouteMode && isAuthorized ? (
        <MarketNavigation canModerateListings={canManageListings} />
      ) : null}

      {!formRouteMode && listFiltersEnabled ? (
        <FilterControls
          marketFilter={marketFilter}
          search={search}
          categoryFilter={categoryFilter}
          subcategoryFilter={subcategoryFilter}
          stickyTopOffset={stickyTopOffset}
          showAdvancedFilters={showAdvancedFilters}
          advancedFilters={scopedAdvancedFilters}
          filterCapabilities={filterCapabilities}
          filterCategoryOptions={filterCategoryOptions}
          filterSubcategoryOptions={filterSubcategoryOptions}
          syncRevision={filterControlsSyncRevision}
          onApply={applyFilterInputs}
          onSearchClear={clearSearchFilter}
          onReset={resetFilters}
          onMarketChange={handleMarketFilterChange}
          onCategoryChange={handleFilterCategoryChange}
          onSubcategoryChange={handleFilterSubcategoryChange}
          onToggleAdvanced={toggleAdvancedFilters}
          onAdvancedFiltersChange={setAdvancedFilters}
        />
      ) : null}

      {!formRouteMode ? <div ref={listingsTopRef} /> : null}

      {!formRouteMode ? (
      <div className={ui.toolbar}>
        <div className={ui.foundCount}>
            Показано: <strong>{shownListingsCount}{shownListingsTotal !== null ? `/${shownListingsTotal}` : ""}</strong>
        </div>
        {listFiltersEnabled ? (
          <div className={ui.toolbarControls}>
            {filterSubcategoryOptions.length ? (
              <div className={ui.chips}>
                <button
                  type="button"
                  className={`${ui.chip} ${subcategoryFilter === "all" ? ui.chipActive : ""}`}
                  onClick={() => setSubcategoryFilter("all")}
                >
                  Все
                </button>
                {filterSubcategoryOptions.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className={`${ui.chip} ${subcategoryFilter === item.value ? ui.chipActive : ""}`}
                    onClick={() => setSubcategoryFilter(item.value)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ) : null}
            <HHTabs
              activeTab={viewMode}
              setActiveTab={handleViewModeChange}
              tabs={CLASSIFIEDS_VIEW_TABS}
              className={ui.viewTabs}
              listClassName="!grid !w-full !grid-cols-2 !items-stretch !gap-0.5 !rounded-[12px] !border-[#eadde5] !p-0.5 !shadow-none sm:!inline-flex sm:!w-fit"
              triggerClassName="!min-h-[38px] !min-w-0 !justify-center !gap-1 !whitespace-nowrap !rounded-[9px] !px-1 !py-1.5 !text-center !text-[11px] !font-semibold !leading-none !text-[#667085] hover:!bg-[#fff7fb] hover:!text-[#8b2a5e] data-[state=active]:!border-[#efc5d8] data-[state=active]:!bg-[#fbeaf0] data-[state=active]:!text-[#8b2a5e] sm:!min-h-[38px] sm:!flex-none sm:!gap-1.5 sm:!px-3 sm:!text-[13px]"
            />
          </div>
        ) : null}
      </div>
      ) : null}

      {!formRouteMode && myMode && isAuthorized ? (
        <HHTabs
          activeTab={myListingsTab}
          setActiveTab={handleMyListingsTabChange}
          tabs={myListingsTabs}
          listClassName="!rounded-[12px] !border-[#e8e8e8] !bg-white !p-1 !shadow-none"
          triggerClassName="!min-h-[38px] !rounded-[10px] !px-3.5 !py-2 !text-[13px] data-[state=active]:!bg-[#fbeaf0] data-[state=active]:!text-[#8b2a5e] data-[state=active]:!shadow-none"
        />
      ) : null}

      {!formRouteMode && listFiltersEnabled && appliedFilterBadges.length ? (
        <div className={ui.appliedFilters}>
          {appliedFilterBadges.map((item) => (
            <span key={item.key} className={ui.appliedFilterBadge}>
              {item.label}
            </span>
          ))}
        </div>
      ) : null}

      {!formRouteMode && canManageListings && viewMode === "list" && visibleItems.length ? (
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[#e8c5d8] bg-[#fff7fb] px-3.5 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] font-semibold text-[#5f5f5f]">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer accent-[#c8508a]"
                checked={allSelectableListingsSelected}
                disabled={bulkDeletePending}
                onChange={toggleAllListingSelection}
              />
              {allSelectableListingsSelected ? "Снять выбор" : "Выбрать все показанные"}
            </label>
            <span className="text-[13px] text-[#6b6b6b]">
              Выбрано: <strong>{selectedListings.length}</strong>
              {visibleItems.length > 100 ? " (максимум 100 за один запрос)" : ""}
            </span>
          </div>
          <button
            type="button"
            className="h-[38px] cursor-pointer rounded-md border border-rose-600 bg-rose-600 px-3.5 text-[13px] font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!selectedListings.length || bulkDeletePending}
            aria-busy={bulkDeletePending}
            onClick={() => void deleteSelectedListings()}
          >
            {bulkDeletePending ? "Удаляем..." : `Удалить выбранные${selectedListings.length ? ` (${selectedListings.length})` : ""}`}
          </button>
          {bulkDeleteError ? <p className="m-0 w-full text-[12px] text-[#b42318]">{bulkDeleteError}</p> : null}
        </section>
      ) : null}

      {createMode && creationPermissionPending ? (
        <div className={ui.empty}>Проверяем доступ к созданию объявлений...</div>
      ) : null}

      {createAccessDenied ? (
        <div className={ui.empty}>
          {creationPermissionError ||
            "Ваш email не добавлен в список пользователей, которым разрешено создавать объявления."}
        </div>
      ) : null}

      {showCreateForm && (editMode || canCreateListings) && (
        <div className={ui.createLayout}>
          <div className={ui.createMain}>
            <div ref={stepperDockRef} className={ui.stepperDock} style={{ top: `${stickyTopOffset}px` }}>
              <div className={`${ui.stepper} ${isAssignmentCategory ? "md:grid-cols-5" : "md:grid-cols-4"}`}>
                {createSteps.map((step) => {
                  const done = step.id < createStep;
                  const active = step.id === createStep;
                  return (
                    <button
                      key={step.id}
                      type="button"
                      className={`${ui.stepItem} ${active ? ui.stepItemActive : done ? ui.stepItemDone : ""}`}
                      onClick={() => setCreateStep(step.id)}
                    >
                      <span className={`${ui.stepDot} ${active ? ui.stepDotActive : done ? ui.stepDotDone : ""}`}>
                        {done ? "✓" : step.id}
                      </span>
                      <span className={`${ui.stepText} ${active ? ui.stepTextActive : ""}`}>{step.label}</span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className={ui.previewToggleBtn}
                onClick={() => setShowMobilePreview((prev) => !prev)}
              >
                {showMobilePreview ? "Скрыть предпросмотр" : "Показать предпросмотр"}
              </button>
            </div>
            {showMobilePreview ? (
              <div
                className={`${ui.previewWrapMobile} sticky z-10`}
                style={{ top: `${stickyTopOffset + mobileDockHeight + 8}px` }}
              >
                {previewCard}
              </div>
            ) : null}

            {createStep === 1 && (
              <>
                <div className={ui.block}>
              <FormSectionTitle text="Структура объявления" />
              <div className={ui.formMarket}>
                <label className={ui.formField}>
                  <span className={ui.formLabel}>
                    Тип сделки<span className={ui.requiredMark}>*</span>
                  </span>
                  <select
                    className={ui.select}
                    value={createMarketType}
                    disabled={!taxonomyMarketOptions.length}
                    onChange={(e) => {
                      const nextMarketType = e.target.value as MarketType;
                      const nextMarket = findTaxonomyMarket(nextMarketType);
                      setCreateMarketType(nextMarketType);
                      setCreateCategory(nextMarket?.categories[0]?.value || "");
                      setCreateSubcategory("");
                    }}
                  >
                        {taxonomyMarketOptions.length ? null : <option value={createMarketType}>Загрузка...</option>}
                    {taxonomyMarketOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={ui.formField}>
                  <span className={ui.formLabel}>
                    Тип объекта<span className={ui.requiredMark}>*</span>
                  </span>
                  <select
                    id="listing-create-category"
                    className={ui.select}
                    value={createCategory}
                    disabled={!createCategoryOptions.length}
                    onChange={(e) => {
                      setCreateCategory(e.target.value);
                      setCreateSubcategory("");
                    }}
                  >
                        {createCategoryOptions.length ? null : <option value={createCategory}>Загрузка...</option>}
                    {createCategoryOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                {!!createSubcategoryOptions.length && (
                  <label className={ui.formField}>
                    <span className={ui.formLabel}>Подтип</span>
                    <select
                      className={ui.select}
                      value={createSubcategory}
                      onChange={(e) => setCreateSubcategory(e.target.value)}
                    >
                      {createSubcategoryOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            </div>

            <div className={ui.block}>
              <FormSectionTitle text="Основная информация" />
              <div className={ui.formGrid}>
                <label className={ui.formField}>
                  <span className={ui.formLabel}>
                    Заголовок<span className={ui.requiredMark}>*</span>
                  </span>
                  <div className="relative">
                    <input
                      id="listing-title"
                      className={`${ui.input} pr-10 ${titleError ? "!border-[#b42318] !bg-[#fff7f6]" : ""}`}
                      minLength={titleConstraint.min_length}
                      maxLength={titleConstraint.max_length}
                      aria-invalid={Boolean(titleError)}
                      aria-describedby={titleError ? "listing-title-error" : undefined}
                      placeholder="Например: Просторный дом с участком"
                      value={baseForm.title || ""}
                        onChange={(e) =>
                          setBaseForm((prev) => ({
                            ...prev,
                            title: e.target.value,
                          }))
                        }
                    />
                    <FieldHint text={getStringConstraintHint(titleConstraint)} />
                  </div>
                  {titleError ? (
                    <span id="listing-title-error" className={ui.errorText} role="alert">
                      {titleError}
                    </span>
                  ) : null}
                </label>

                {createMarketType !== "rent" ? (
                  <label className={ui.formField}>
                    <span className={ui.formLabel}>Итоговая цена</span>
                    <div className="relative">
                      <GroupedPriceInput
                        id="listing-price-total"
                        className={`${ui.input} appearance-none pr-16 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                          visiblePriceTotalError ? "!border-[#b42318] !bg-[#fff7f6]" : ""
                        }`}
                        aria-invalid={Boolean(visiblePriceTotalError)}
                        aria-describedby={visiblePriceTotalError ? "listing-price-total-error" : undefined}
                        placeholder="Например: 24 500 000"
                        value={finance.price_total}
                        onValueChange={(value) =>
                          setFinance((p) => ({
                            ...p,
                            price_total: value,
                          }))
                        }
                      />
                      <span
                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#6b6b6b]"
                        aria-hidden="true"
                      >
                        ₽
                      </span>
                      <FieldHint
                        text={`${
                          derivedPricePerSqm === null
                            ? "После расчёта цена за м²"
                            : `Расчётная цена — ${formatConstraintNumber(
                                derivedPricePerSqm,
                                pricePerSqmConstraint.decimal_places,
                              )} ₽/м²`
                        } · ${getNumericConstraintHint(pricePerSqmConstraint, "₽/м²")}`}
                        positionClassName="right-8 top-1/2 -translate-y-1/2"
                      />
                    </div>
                    {visiblePriceTotalError ? (
                      <span id="listing-price-total-error" className={ui.errorText} role="alert">
                        {visiblePriceTotalError}
                      </span>
                    ) : null}
                  </label>
                ) : (
                  <label className={ui.formField}>
                    <span className={ui.formLabel}>Цена / мес</span>
                    <div className="relative">
                      <GroupedPriceInput
                        id="listing-price-monthly"
                        className={`${ui.input} appearance-none pr-16 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                          visiblePriceMonthlyError ? "!border-[#b42318] !bg-[#fff7f6]" : ""
                        }`}
                        aria-invalid={Boolean(visiblePriceMonthlyError)}
                        aria-describedby={visiblePriceMonthlyError ? "listing-price-monthly-error" : undefined}
                        placeholder="Например: 95 000"
                        value={finance.price_monthly}
                        onValueChange={(value) =>
                          setFinance((p) => ({
                            ...p,
                            price_monthly: value,
                          }))
                        }
                      />
                      <span
                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-[#6b6b6b]"
                        aria-hidden="true"
                      >
                        ₽
                      </span>
                      <FieldHint
                        text={getNumericConstraintHint(monthlyPriceConstraint, "₽")}
                        positionClassName="right-8 top-1/2 -translate-y-1/2"
                      />
                    </div>
                    {visiblePriceMonthlyError ? (
                      <span id="listing-price-monthly-error" className={ui.errorText} role="alert">
                        {visiblePriceMonthlyError}
                      </span>
                    ) : null}
                  </label>
                )}

                {"room_count" in baseForm && (
                  <label className={ui.formField}>
                    <span className={ui.formLabel}>
                      Количество комнат
                      {roomCountRequired ? <span className={ui.requiredMark}>*</span> : null}
                    </span>
                    <div className="relative">
                      <input
                        id="listing-room-count"
                        className={`${ui.input} pr-10 ${roomCountError ? "!border-[#b42318] !bg-[#fff7f6]" : ""}`}
                        type="number"
                        min={roomCountConstraint.min}
                        max={roomCountConstraint.max}
                        step="1"
                        aria-invalid={Boolean(roomCountError)}
                        aria-describedby={roomCountError ? "listing-room-count-error" : undefined}
                        value={baseForm.room_count || ""}
                        onChange={(e) =>
                          setBaseForm((prev) => ({
                            ...prev,
                            room_count: e.target.value,
                          }))
                        }
                      />
                      <FieldHint text={getNumericConstraintHint(roomCountConstraint)} />
                    </div>
                    {roomCountError ? (
                      <span id="listing-room-count-error" className={ui.errorText} role="alert">
                        {roomCountError}
                      </span>
                    ) : null}
                  </label>
                )}

                {"area_sqm" in baseForm && (
                  <label className={ui.formField}>
                    <span className={ui.formLabel}>
                      Площадь, м²<span className={ui.requiredMark}>*</span>
                    </span>
                    <AreaInput
                      id="listing-area-sqm"
                      constraint={areaSqmConstraint}
                      error={areaSqmError}
                      hint={getNumericConstraintHint(areaSqmConstraint, "м²")}
                      value={baseForm.area_sqm || ""}
                      onValueChange={(value) =>
                        setBaseForm((prev) => ({
                          ...prev,
                          area_sqm: value,
                        }))
                      }
                    />
                  </label>
                )}

                {"floor" in baseForm && (
                  <label className={ui.formField}>
                    <span className={ui.formLabel}>
                      Этаж{floorRequired ? <span className={ui.requiredMark}>*</span> : null}
                    </span>
                    <div className="relative">
                      <input
                        id="listing-floor"
                        className={`${ui.input} pr-10 ${floorError ? "!border-[#b42318] !bg-[#fff7f6]" : ""}`}
                        type="number"
                        min={floorConstraint.min}
                        max={floorConstraint.max}
                        step="1"
                        aria-invalid={Boolean(floorError)}
                        aria-describedby={floorError ? "listing-floor-error" : undefined}
                        value={baseForm.floor || ""}
                        onChange={(e) =>
                          setBaseForm((prev) => ({
                            ...prev,
                            floor: e.target.value,
                          }))
                        }
                      />
                      <FieldHint text={getNumericConstraintHint(floorConstraint)} />
                    </div>
                    {floorError ? (
                      <span id="listing-floor-error" className={ui.errorText} role="alert">
                        {floorError}
                      </span>
                    ) : null}
                  </label>
                )}

                    {((listingType !== "apartment" && "floor_count" in baseForm) || supportsApartmentFloorCount) && (
                  <label className={ui.formField}>
                        <span className={ui.formLabel}>
                          {listingType === "apartment" ? "Этажность дома" : "Этажность"}
                          {floorCountRequired ? <span className={ui.requiredMark}>*</span> : null}
                        </span>
                    <div className="relative">
                      <input
                        id="listing-floor-count"
                        className={`${ui.input} pr-10 ${floorCountError ? "!border-[#b42318] !bg-[#fff7f6]" : ""}`}
                        type="number"
                        min={floorCountConstraint.min}
                        max={floorCountConstraint.max}
                        step="1"
                        aria-invalid={Boolean(floorCountError)}
                        aria-describedby={floorCountError ? "listing-floor-count-error" : undefined}
                        placeholder={listingType === "apartment" ? "Например: 16" : undefined}
                        value={baseForm.floor_count || ""}
                        onChange={(e) =>
                          setBaseForm((prev) => ({
                            ...prev,
                            floor_count: e.target.value,
                          }))
                        }
                      />
                      <FieldHint text={getNumericConstraintHint(floorCountConstraint)} />
                    </div>
                    {floorCountError ? (
                      <span id="listing-floor-count-error" className={ui.errorText} role="alert">
                        {floorCountError}
                      </span>
                    ) : null}
                  </label>
                )}

                {listingType !== "land_plot" && (
                  <>
                    {isAssignmentCategory && (
                      <div className={ui.formField}>
                        <span className={ui.formLabel}>Статус дома</span>
                        <label className={`${ui.checkbox} h-[38px]`}>
                          <input
                            className={ui.checkboxInput}
                            type="checkbox"
                            checked={assignmentHouseCompleted}
                            onChange={(e) => {
                              const completed = e.target.checked;
                              setAssignmentHouseCompleted(completed);
                              setBaseForm((prev) => ({
                                ...prev,
                                [completed ? "completion_year" : "construction_year"]: "",
                              }));
                            }}
                          />
                          Дом сдан
                        </label>
                      </div>
                    )}
                    <label className={ui.formField}>
                      <span className={ui.formLabel}>
                        {yearLabel}
                        {yearRequired && <span className={ui.requiredMark}>*</span>}
                      </span>
                      <div className="relative">
                        <input
                          id="listing-year"
                          className={`${ui.input} pr-10 ${yearError ? "!border-[#b42318] !bg-[#fff7f6]" : ""}`}
                          type="number"
                          min={yearConstraint.min}
                          max={yearConstraint.max}
                          step="1"
                          aria-invalid={Boolean(yearError)}
                          aria-describedby={yearError ? "listing-year-error" : undefined}
                          value={baseForm[yearKey] || ""}
                          onChange={(e) => {
                            setBaseForm((prev) => ({
                              ...prev,
                              [yearKey]: e.target.value,
                            }));
                          }}
                        />
                        <FieldHint text={getNumericConstraintHint(yearConstraint, "", { useGrouping: false })} />
                      </div>
                      {yearError ? (
                        <span id="listing-year-error" className={ui.errorText} role="alert">
                          {yearError}
                        </span>
                      ) : null}
                    </label>
                  </>
                )}

                {"area_site" in baseForm && (
                  <label className={ui.formField}>
                    <span className={ui.formLabel}>
                      Площадь участка, м²<span className={ui.requiredMark}>*</span>
                    </span>
                    <AreaInput
                      id="listing-area-site"
                      constraint={areaSiteConstraint}
                      error={areaSiteError}
                      hint={`${getNumericConstraintHint(areaSiteConstraint, "м²")} · ${formatConstraintNumber(
                        areaSiteConstraint.min /
                          listingConstraints.areas.area_site.square_meters_per_sotka,
                        areaSiteConstraint.decimal_places,
                      )}–${formatConstraintNumber(
                        areaSiteConstraint.max /
                          listingConstraints.areas.area_site.square_meters_per_sotka,
                        areaSiteConstraint.decimal_places,
                      )} соток`}
                      value={baseForm.area_site || ""}
                      onValueChange={(value) =>
                        setBaseForm((prev) => ({
                          ...prev,
                          area_site: value,
                        }))
                      }
                    />
                  </label>
                )}

                {"description" in baseForm && (
                  <label className="grid gap-1.5 md:col-span-2">
                    <span className={ui.formLabel}>Описание</span>
                    <div className="relative">
                      <AutoGrowingTextarea
                        id="listing-description"
                        className={`${ui.textarea} pr-10 ${descriptionError ? "!border-[#b42318] !bg-[#fff7f6]" : ""}`}
                        maxLength={descriptionConstraint.max_length}
                        aria-invalid={Boolean(descriptionError)}
                        aria-describedby={descriptionError ? "listing-description-error" : undefined}
                        value={baseForm.description || ""}
                        onChange={(e) =>
                          setBaseForm((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                      />
                      <FieldHint
                        text={getStringConstraintHint(descriptionConstraint)}
                        positionClassName="right-3 top-3"
                      />
                    </div>
                    {descriptionError ? (
                      <span id="listing-description-error" className={ui.errorText} role="alert">
                        {descriptionError}
                      </span>
                    ) : null}
                  </label>
                )}
              </div>
              {createMarketType === "rent" ? (
                <div className={ui.formGrid}>
                  <label className={ui.formField}>
                    <span className={ui.formLabel}>Коммунальные платит</span>
                    <select
                      className={ui.select}
                      value={finance.utilities_payer}
                      onChange={(e) => {
                        const nextPayer = e.target.value as UtilitiesPayer;
                        setFinance((p) => ({
                          ...p,
                          utilities_payer: nextPayer,
                          utility_included: nextPayer === "landlord",
                        }));
                      }}
                    >
                      <option value="tenant">{UTILITIES_PAYER_LABELS.tenant}</option>
                      <option value="landlord">{UTILITIES_PAYER_LABELS.landlord}</option>
                    </select>
                  </label>
                  {supportsCreateRentTerm ? (
                    <label className={ui.formField}>
                      <span className={ui.formLabel}>Срок аренды</span>
                      <select
                        className={ui.select}
                        value={finance.rent_term}
                        onChange={(e) =>
                          setFinance((p) => ({
                            ...p,
                            rent_term: e.target.value as RentTerm,
                          }))
                        }
                      >
                        <option value="short_term">{RENT_TERM_LABELS.short_term}</option>
                        <option value="long_term">{RENT_TERM_LABELS.long_term}</option>
                      </select>
                    </label>
                  ) : null}
                  <label className={ui.formField}>
                    <span className={ui.formLabel}>Комиссия агента, %</span>
                    <div className="relative">
                      <DecimalInput
                        id="listing-agent-commission-percent"
                        className={`${ui.input} pr-10 ${
                          visibleAgentCommissionPercentError ? "!border-[#b42318] !bg-[#fff7f6]" : ""
                        }`}
                        min={agentCommissionPercentConstraint.min}
                        max={agentCommissionPercentConstraint.max}
                        step={10 ** -agentCommissionPercentConstraint.decimal_places}
                        aria-invalid={Boolean(visibleAgentCommissionPercentError)}
                        aria-describedby={
                          visibleAgentCommissionPercentError ? "listing-agent-commission-percent-error" : undefined
                        }
                        placeholder="Например: 50"
                        value={finance.agent_commission_percent}
                        onValueChange={(value) =>
                          setFinance((p) => ({
                            ...p,
                            agent_commission_percent: value,
                          }))
                        }
                      />
                      <FieldHint text={getNumericConstraintHint(agentCommissionPercentConstraint, "%")} />
                    </div>
                    {visibleAgentCommissionPercentError ? (
                      <span id="listing-agent-commission-percent-error" className={ui.errorText} role="alert">
                        {visibleAgentCommissionPercentError}
                      </span>
                    ) : null}
                  </label>
                  <label className={ui.formField}>
                    <span className={ui.formLabel}>Комиссия агента, ₽</span>
                    <div className="relative">
                      <GroupedPriceInput
                        id="listing-agent-commission-amount"
                        className={`${ui.input} pr-10 ${
                          visibleAgentCommissionAmountError ? "!border-[#b42318] !bg-[#fff7f6]" : ""
                        }`}
                        min={agentCommissionAmountConstraint.min}
                        max={agentCommissionAmountConstraint.max}
                        step={10 ** -agentCommissionAmountConstraint.decimal_places}
                        aria-invalid={Boolean(visibleAgentCommissionAmountError)}
                        aria-describedby={
                          visibleAgentCommissionAmountError ? "listing-agent-commission-amount-error" : undefined
                        }
                        placeholder="Например: 90 000"
                        value={finance.agent_commission_amount}
                        onValueChange={(value) =>
                          setFinance((p) => ({
                            ...p,
                            agent_commission_amount: value,
                          }))
                        }
                      />
                      <FieldHint text={getNumericConstraintHint(agentCommissionAmountConstraint, "₽")} />
                    </div>
                    {visibleAgentCommissionAmountError ? (
                      <span id="listing-agent-commission-amount-error" className={ui.errorText} role="alert">
                        {visibleAgentCommissionAmountError}
                      </span>
                    ) : null}
                  </label>
                </div>
              ) : null}
            </div>
              </>
            )}

            {createStep === 2 && (
              <div className={ui.block}>
              <FormSectionTitle text="Фотографии" />
              <div id="listing-images" className={ui.uploadPanel} tabIndex={-1}>
                  <label
                    className={ui.uploadDropzone}
                    onDragOver={(event) => {
                      if (Array.from(event.dataTransfer.types).includes("Files")) {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "copy";
                      }
                    }}
                    onDrop={(event) => {
                      const files = Array.from(event.dataTransfer.files).filter(isMarketImageFile);
                      if (!files.length) return;
                      event.preventDefault();
                      void handlePhotoFiles(files);
                    }}
                  >
                  <input
                    className="hidden"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,image/webp,image/heic,image/heif,.heic,.heif"
                    multiple
                    onChange={handlePhotoSelect}
                  />
                  <div className="grid gap-1">
                    <div className="text-[15px] font-semibold text-[#8b2a5e]">
                      Перетащите фото сюда или выберите файлы
                    </div>
                    <div className={ui.uploadHint}>
                      JPG, PNG, WEBP, HEIC/HEIF. До {listingConstraints.images.max_items} изображений.
                    </div>
                  </div>
                </label>

                {imageUploads.length ? (
                  <div className={ui.uploadGrid}>
                    {imageUploads.map((file, index) => (
                      <React.Fragment key={file.id}>
                        {dragOverUploadId === file.id && dragOverPosition === "before" ? (
                          <div className={ui.uploadDropPlaceholder} />
                        ) : null}
                        <div
                          className={`${ui.uploadItemCard} ${draggedUploadId === file.id ? ui.uploadItemDragging : ""}`}
                          onDragOver={(e) => {
                            e.preventDefault();
                              e.dataTransfer.dropEffect = "move";
                            const rect = e.currentTarget.getBoundingClientRect();
                            const relativeY = e.clientY - rect.top;
                              const nextPosition = relativeY > rect.height / 2 ? "after" : "before";
                            setDragOverUploadId(file.id);
                            setDragOverPosition(nextPosition);
                          }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const fromId = draggedUploadId || e.dataTransfer.getData("text/plain");
                              const rect = e.currentTarget.getBoundingClientRect();
                              const position = e.clientY - rect.top > rect.height / 2 ? "after" : "before";
                              if (fromId) {
                                reorderUploadedImages(fromId, file.id, position);
                            }
                            setDraggedUploadId(null);
                            setDragOverUploadId(null);
                          }}
                          onDragEnd={() => {
                            setDraggedUploadId(null);
                            setDragOverUploadId(null);
                          }}
                        >
                          <div className={ui.uploadRow}>
                          <div className={ui.uploadInfo}>
                            <button
                              type="button"
                              className={ui.uploadDrag}
                              title="Перетащите карточку, чтобы изменить порядок"
                                  aria-label={`Перетащить фото ${file.name}`}
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.effectAllowed = "move";
                                    e.dataTransfer.setData("text/plain", file.id);
                                    setDraggedUploadId(file.id);
                                  }}
                            >
                              <span className={ui.uploadGrip} aria-hidden="true">
                                {Array.from({ length: 6 }).map((_, dotIdx) => (
                                  <span key={dotIdx} className={ui.uploadGripDot} />
                                ))}
                              </span>
                            </button>
                            <img className={ui.uploadPreview} src={file.previewUrl} alt={file.name} />
                            <div className={ui.uploadText}>
                              <div className={ui.uploadName}>{file.name}</div>
                            </div>
                          </div>
                          <div className={ui.uploadActions}>
                            {index === 0 ? <span className={ui.uploadPrimaryBadge}>Главное</span> : null}
                            <button
                              type="button"
                              className={ui.uploadRemove}
                              onClick={() => removeUploadedImage(file.id)}
                            >
                              Удалить
                            </button>
                          </div>
                          </div>
                          <div className={ui.uploadTrack}>
                            <div className={ui.uploadFill} style={{ width: `${file.progress}%` }} />
                          </div>
                          {file.error ? <div className={ui.errorText}>{file.error}</div> : null}
                        </div>
                        {dragOverUploadId === file.id && dragOverPosition === "after" ? (
                          <div className={ui.uploadDropPlaceholder} />
                        ) : null}
                      </React.Fragment>
                    ))}
                  </div>
                ) : null}

                <span className={visibleImageError ? ui.errorText : ui.helperText} role={visibleImageError ? "alert" : undefined}>
                  {visibleImageError ||
                    `Главное фото берётся первым в списке. Загружено ${imageUploads.length} из ${listingConstraints.images.max_items}.`}
                </span>
              </div>
            </div>
            )}

            {createStep === 3 && (
              <>
                <div className={ui.block}>
              <FormSectionTitle text="Характеристики" />
              {listingType === "house" && (
                <label className={ui.formField}>
                  <span className={ui.formLabel}>Тип дома</span>
                  <select
                    className={ui.select}
                    value={features.house_kind}
                    onChange={(e) =>
                          setFeatures((p) => ({
                            ...p,
                            house_kind: e.target.value,
                          }))
                    }
                  >
                    <option value="house">Дом</option>
                    <option value="dacha">Дача</option>
                    <option value="cottage">Коттедж</option>
                    <option value="townhouse">Таунхаус</option>
                  </select>
                </label>
              )}
              <div className={ui.checksGrid}>
                {listingType === "house" &&
                  [
                    ["is_renovated", "Ремонт"],
                    ["is_completed", "Достроен"],
                    ["has_bathhouse", "Баня/сауна"],
                    ["has_pool", "Бассейн"],
                  ].map(([k, label]) => (
                    <label className={ui.checkbox} key={k}>
                      <input
                        className={ui.checkboxInput}
                        type="checkbox"
                        checked={Boolean((features as Record<string, unknown>)[k])}
                        onChange={(e) =>
                              setFeatures((p) => ({
                                ...p,
                                [k]: e.target.checked,
                              }))
                        }
                      />
                      {label}
                    </label>
                  ))}

                {listingType === "apartment" &&
                  [
                    ["is_penthouse", "Пентхаус"],
                    ["is_apartment_unit", "Апартаменты"],
                    ["is_view", "Видовая"],
                    ["is_renovated", "Ремонт"],
                  ].map(([k, label]) => (
                    <label className={ui.checkbox} key={k}>
                      <input
                        className={ui.checkboxInput}
                        type="checkbox"
                        checked={Boolean((features as Record<string, unknown>)[k])}
                        onChange={(e) =>
                              setFeatures((p) => ({
                                ...p,
                                [k]: e.target.checked,
                              }))
                        }
                      />
                      {label}
                    </label>
                  ))}

                {listingType === "commercial" && (
                  <label className={ui.checkbox}>
                    <input
                      className={ui.checkboxInput}
                      type="checkbox"
                      checked={features.is_renovated}
                      onChange={(e) =>
                            setFeatures((p) => ({
                              ...p,
                              is_renovated: e.target.checked,
                            }))
                      }
                    />
                    Ремонт
                  </label>
                )}
              </div>
            </div>

            {(listingType === "house" || listingType === "land_plot") && (
              <div className={ui.block}>
                <FormSectionTitle text="Коммуникации" />
                <div className={ui.checksGrid}>
                  {[
                    ["has_sewerage", "Канализация"],
                    ["has_electricity", "Электричество"],
                    ["has_water", "Водопровод"],
                    ["has_gas", "Газ"],
                  ].map(([k, label]) => (
                    <label className={ui.checkbox} key={k}>
                      <input
                        className={ui.checkboxInput}
                        type="checkbox"
                        checked={Boolean((communications as Record<string, unknown>)[k])}
                        onChange={(e) =>
                              setCommunications((p) => ({
                                ...p,
                                [k]: e.target.checked,
                              }))
                        }
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            )}
              </>
            )}

            {createStep === 4 && (
              <>
                <div className={ui.block}>
              <FormSectionTitle text="Адрес" />
              <ListingLocationPicker
                latitudeValue={baseForm.latitude || ""}
                longitudeValue={baseForm.longitude || ""}
                address={address}
                coordinateDecimalPlaces={Math.min(
                  listingConstraints.fields.latitude.decimal_places,
                  listingConstraints.fields.longitude.decimal_places,
                )}
                error={visibleAddressError}
                onCoordinatesChange={(coordinates) =>
                  setBaseForm((prev) => ({
                    ...prev,
                    latitude: coordinates.latitude,
                    longitude: coordinates.longitude,
                  }))
                }
                onAddressChange={setAddress}
              />
            </div>
              </>
            )}

            {createStep === 5 && isAssignmentCategory ? (
              <div className={ui.block}>
                <FormSectionTitle text="Переуступка" />
                <div className="mb-5 grid gap-2 sm:grid-cols-2">
                  <label className={ui.checkbox}>
                    <input
                      className={ui.checkboxInput}
                      type="radio"
                      name="assignment-payment-type"
                      checked={assignmentPaymentType === "installment"}
                      onChange={() => setAssignmentPaymentType("installment")}
                    />
                    Рассрочка
                  </label>
                  <label className={ui.checkbox}>
                    <input
                      className={ui.checkboxInput}
                      type="radio"
                      name="assignment-payment-type"
                      checked={assignmentPaymentType === "full_payment"}
                      onChange={() => {
                        setAssignmentPaymentType("full_payment");
                        setAssignment((prev) => ({
                          ...prev,
                          full_payment_price: prev.full_payment_price || finance.price_total,
                        }));
                      }}
                    />
                    Полная стоимость
                  </label>
                </div>
                {isFullPaymentAssignment ? (
                  <p className="mb-4 text-[13px] leading-5 text-[#6b6b6b]">
                    Полная стоимость совпадает с итоговой ценой объявления на первом шаге.
                  </p>
                ) : null}
                <div className={ui.formGrid}>
                  {Object.entries(assignment)
                    .filter(
                      ([k]) =>
                        !["currency_id", "recalculation_percentage", "recalculation_period"].includes(k) &&
                        (!isFullPaymentAssignment || k === "full_payment_price"),
                    )
                    .map(([k, v]) => {
                      const constraint =
                        listingConstraints.money.fields[k as ListingMoneyField] ||
                        (k === "assignment_months_left"
                          ? listingConstraints.addons.assignment_months_left
                          : k === "assignment_paid_months"
                            ? listingConstraints.addons.assignment_paid_months
                            : null);
                      const constraintError = assignmentConstraintErrors[k];
                      const visibleConstraintError = formValidationAttempted ? constraintError : null;
                      const errorId = `listing-assignment-${k}-error`;
                      const hint = constraint
                        ? getNumericConstraintHint(
                            constraint,
                            ["assignment_months_left", "assignment_paid_months"].includes(k) ? "" : "₽",
                          )
                        : null;
                      const inputClassName = `${ui.input} pr-10 ${
                        visibleConstraintError ? "!border-[#b42318] !bg-[#fff7f6]" : ""
                      }`;

                      return (
                        <label className={ui.formField} key={k}>
                          <span className={ui.formLabel}>
                            {isFullPaymentAssignment && k === "full_payment_price"
                              ? "Полная стоимость"
                              : ASSIGNMENT_LABELS[k] || k}
                          </span>
                          <div className="relative">
                            {ASSIGNMENT_PRICE_FIELDS.has(k) ? (
                              <GroupedPriceInput
                                id={`listing-assignment-${k}`}
                                className={inputClassName}
                                min={constraint?.min}
                                max={constraint?.max}
                                step={constraint ? 10 ** -constraint.decimal_places : undefined}
                                aria-invalid={Boolean(visibleConstraintError)}
                                aria-describedby={visibleConstraintError ? errorId : undefined}
                                placeholder={
                                  isFullPaymentAssignment && k === "full_payment_price"
                                    ? "Полная стоимость"
                                    : ASSIGNMENT_LABELS[k] || k
                                }
                                value={v}
                                onValueChange={(value) => {
                                  setAssignment((p) => ({
                                    ...p,
                                    [k]: value,
                                  }));
                                  if (isFullPaymentAssignment && k === "full_payment_price") {
                                    setFinance((p) => ({ ...p, price_total: value }));
                                  }
                                }}
                              />
                            ) : k.includes("_at") ? (
                              <input
                                id={`listing-assignment-${k}`}
                                className={inputClassName}
                                type="datetime-local"
                                aria-invalid={Boolean(visibleConstraintError)}
                                aria-describedby={visibleConstraintError ? errorId : undefined}
                                placeholder={ASSIGNMENT_LABELS[k] || k}
                                value={v}
                                onChange={(event) =>
                                  setAssignment((p) => ({
                                    ...p,
                                    [k]: event.target.value,
                                  }))
                                }
                              />
                            ) : (
                              <DecimalInput
                                id={`listing-assignment-${k}`}
                                className={inputClassName}
                                min={constraint?.min}
                                max={constraint?.max}
                                step={constraint ? 10 ** -constraint.decimal_places : undefined}
                                aria-invalid={Boolean(visibleConstraintError)}
                                aria-describedby={visibleConstraintError ? errorId : undefined}
                                placeholder={ASSIGNMENT_LABELS[k] || k}
                                value={v}
                                onValueChange={(value) =>
                                  setAssignment((p) => ({
                                    ...p,
                                    [k]: value,
                                  }))
                                }
                              />
                            )}
                            {hint ? (
                              <FieldHint
                                text={hint}
                                positionClassName={k.includes("_at") ? "right-9 top-1/2 -translate-y-1/2" : undefined}
                              />
                            ) : null}
                          </div>
                          {visibleConstraintError ? (
                            <span id={errorId} className={ui.errorText} role="alert">
                              {visibleConstraintError}
                            </span>
                          ) : null}
                        </label>
                      );
                    })}
                  {formValidationAttempted && assignmentCrossFieldError ? (
                    <div className={ui.errorText} role="alert">
                      {assignmentCrossFieldError}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {uploadError ? <div className={ui.errorText}>{uploadError}</div> : null}
            {createError ? <div className={ui.errorText}>{createError}</div> : null}
            <div className={ui.formActions}>
              <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:justify-end">
                <button
                  type="button"
                  className={`${ui.btnBase} ${ui.btnGhost} w-full sm:w-auto`}
                  disabled={currentStepIndex <= 0}
                  onClick={() => {
                    if (currentStepIndex <= 0) return;
                    setCreateStep(createSteps[currentStepIndex - 1].id);
                  }}
                >
                  Назад
                </button>
                {!isLastStep ? (
                  <button
                    type="button"
                    className={`${ui.btnBase} ${ui.btnPrimary} w-full sm:w-auto`}
                    disabled={createStep === 2 && uploadInProgress}
                    onClick={() => {
                      if (currentStepIndex < 0 || currentStepIndex >= createSteps.length - 1) return;
                      setCreateStep(createSteps[currentStepIndex + 1].id);
                    }}
                  >
                    {createStep === 2 && uploadInProgress ? "Дождитесь загрузки фото" : "Далее"}
                  </button>
                ) : (
                  <Button
                    text={uploadInProgress ? "Загружаем фото..." : editMode ? "Сохранить" : "Создать"}
                    pending={createPending}
                    disabled={uploadInProgress || uploadHasErrors}
                    onClick={onCreate}
                  />
                )}
              </div>
            </div>
          </div>

          <aside className={`${ui.previewWrap} ${ui.previewWrapDesktop}`} style={{ top: `${stickyTopOffset + 16}px` }}>
            {previewCard}
          </aside>
        </div>
      )}

      {!formRouteMode && viewMode === "map" && listFiltersEnabled ? (
        <div className={ui.mapLayout}>
          <ClassifiedsMapView
            items={mapItems}
            selectedItemKey={selectedMapItemKey}
            pending={mapPending}
            error={mapError}
            getItemKey={getFavoriteKey}
            onViewportChange={setMapViewport}
            onSelect={(item) => setSelectedMapItemKey(getFavoriteKey(item))}
            onRetry={() => loadMapListings()}
          />
          <aside className={ui.mapSide} style={{ top: `${stickyTopOffset + 16}px` }}>
            {selectedMapItem ? (
              renderListingCard(selectedMapItem, true)
            ) : (
              <div className={ui.empty}>
                {mapPending
                  ? "Загружаем объявления в этой области"
                  : mapItems.length
                    ? "Выберите отметку на карте"
                    : "В видимой области объявлений нет"}
              </div>
            )}
          </aside>
        </div>
      ) : !formRouteMode && pending ? (
        listingsSkeleton
      ) : !formRouteMode && !visibleItems.length ? (
        <div className={ui.empty}>
          {favoriteMode && !isAuthorized
            ? "Раздел «Избранное» доступен только авторизованным пользователям"
            : favoriteMode
            ? "Пока нет избранных объявлений"
            : myMode
            ? myListingsTabLoading
              ? "Загружаем объявления..."
              : myListingsEmptyText
            : "Объявления не найдены"}
        </div>
      ) : !formRouteMode ? (
        <ul className={ui.list}>
          {visibleItems.map((item) => (
            <li key={`${item.type}-${item.id}`}>{renderListingCard(item, true)}</li>
          ))}
        </ul>
      ) : null}

      {!formRouteMode && hasMoreFavoriteListings && (pagination.has_more || loadMoreError) ? (
        <div className="grid justify-items-center gap-2 pt-2">
          {loadMoreError ? <p className="m-0 text-[13px] text-[#b42318]">{loadMoreError}</p> : null}
          <button
            type="button"
            className={`${ui.btnBase} ${ui.btnGhost} min-w-[170px]`}
            disabled={loadMorePending || !pagination.next_cursor}
            onClick={() => loadListings(pagination.next_cursor)}
          >
            {loadMorePending ? "Загружаем..." : loadMoreError ? "Повторить" : "Показать ещё"}
          </button>
        </div>
      ) : null}
    </div>
  );
};

const mapState = (state: RootState) => ({
  user: selectUserInfo(state),
  userData: selectUserData(state),
  userToken: selectUserToken(state),
});

export const ClassifiedsBoard = connect(mapState)(_ClassifiedsBoard);
